import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useSession } from '@/context/SessionContext';
import { useProposal, type Proposal, COUNTRY_STANDARD } from '@/context/ProposalContext';
import { getAllLocalSamples, getLocalProcessTrains, getLocalSites, saveLocalSite, updateLocalSite, deleteLocalSite, type UnifiedSample, type UnifiedTrain, type CustomerSite } from '@/lib/hybridStorage';
import { postcodeToState, postcodeToUkRegion, guessStateFromText, guessUkRegion } from '@/types/locations';
import { useAuth } from '@/hooks/useAuth';
import { getRetentionPolicy, isWithinRetention, retentionToastMessage, adminOverridePrompt } from '@/lib/retention';
import { apiListSamples, apiListInfluentSamples, apiListCustomers, apiListTrains, apiListCustomerSamples, apiListProposals, apiListCustomerSites, apiSaveCustomerSite, apiDeleteCustomerSite, apiUpdateCustomerSite, apiSaveCustomer, apiUpdateCustomer, apiSaveSAWaterEstimate, apiListSAWaterEstimates, apiListSiteEquipment, hasAuthToken as hasAuth } from '@/lib/phpApi';
import { hasAuthToken } from '@/lib/phpApi';
import { toast } from 'sonner';
import {
  Building2, FlaskConical, Workflow, FileText, Search, Plus, Star, X,
  MapPin, Mail, Phone, Calendar, CheckCircle2, AlertTriangle, Droplets,
  Beaker, ChevronRight, ArrowRight, TrendingUp, Shield, Clock,
  Users, Globe, Hash, Layers, Edit3, Trash2, Eye, MoreHorizontal,
  Activity, BarChart3, Database, Zap, Info, RefreshCw, Lock,
  Factory, Fish, Milk, Cog, FlaskRound, ShieldCheck,
  ChevronDown, ChevronUp, Download, Printer, Link2,
  Navigation, Building, Pencil, DollarSign, TrendingDown, Receipt,
  Calculator, Gauge, List, ChevronLeft, Droplet, Save, RotateCcw, ExternalLink,

} from 'lucide-react';
import { getStandard, ALL_STANDARDS, type StandardCode } from '@/types/standards';
import { inferPolishRegulator, PL_RZGW_BODIES, PL_NATIONAL_BODY } from '@/lib/polandRegulator';
import { generateTrendReportPDF, generateEffluentSamplePDF, buildRegulatoryReportNumber, generateSAWaterChargesReport } from '@/lib/pdfGenerator';
import type { SAWaterChargeRow } from '@/lib/pdfGenerator';
import PageHeader from '@/components/PageHeader';

// ─── Country / Industry / Regulatory maps ─────────────────────────────────────
const COUNTRY_TO_STANDARD: Record<string, StandardCode> = {
  // Australasia — AU EPA
  AU: 'AU_EPA', NZ: 'AU_EPA', PG: 'AU_EPA', FJ: 'AU_EPA',
  // UK & Ireland — UK EA
  GB: 'UK_EA', IE: 'UK_EA',
  // EU member states — EU UWWTD (Directive 91/271/EEC)
  AT: 'EU_UWWTD', BE: 'EU_UWWTD', BG: 'EU_UWWTD', CY: 'EU_UWWTD',
  CZ: 'EU_UWWTD', DE: 'EU_UWWTD', DK: 'EU_UWWTD', EE: 'EU_UWWTD',
  ES: 'EU_UWWTD', FI: 'EU_UWWTD', FR: 'EU_UWWTD', GR: 'EU_UWWTD',
  HR: 'EU_UWWTD', HU: 'EU_UWWTD', IT: 'EU_UWWTD', LT: 'EU_UWWTD',
  LU: 'EU_UWWTD', LV: 'EU_UWWTD', MT: 'EU_UWWTD', NL: 'EU_UWWTD',
  PL: 'PL_WP', PT: 'EU_UWWTD', RO: 'EU_UWWTD', SE: 'EU_UWWTD',
  SI: 'EU_UWWTD', SK: 'EU_UWWTD',
  // EEA/EFTA (apply EU standards by treaty)
  NO: 'EU_UWWTD', IS: 'EU_UWWTD', CH: 'EU_UWWTD',
  // North America — UK EA used as reference international standard
  US: 'UK_EA', CA: 'UK_EA', MX: 'UK_EA',
  // Asia-Pacific
  SG: 'UK_EA', MY: 'AU_EPA', JP: 'UK_EA', CN: 'UK_EA', KR: 'UK_EA',
  IN: 'UK_EA', TH: 'AU_EPA', ID: 'AU_EPA', PH: 'AU_EPA', VN: 'AU_EPA',
  HK: 'UK_EA', TW: 'UK_EA',
  // Middle East
  AE: 'UK_EA', SA: 'UK_EA', QA: 'UK_EA', KW: 'UK_EA', BH: 'UK_EA', OM: 'UK_EA',
  // Africa
  ZA: 'UK_EA', NG: 'UK_EA', KE: 'UK_EA', EG: 'UK_EA', GH: 'UK_EA', TZ: 'UK_EA',
  // South America
  BR: 'UK_EA', AR: 'UK_EA', CL: 'UK_EA', CO: 'UK_EA', PE: 'UK_EA',
  OTHER: 'UK_EA',
};
const COUNTRY_FLAGS: Record<string, string> = {
  // Australasia
  AU: '🇦🇺', NZ: '🇳🇿', PG: '🇵🇬', FJ: '🇫🇯',
  // UK & Ireland
  GB: '🇬🇧', IE: '🇮🇪',
  // EU
  AT: '🇦🇹', BE: '🇧🇪', BG: '🇧🇬', CY: '🇨🇾',
  CZ: '🇨🇿', DE: '🇩🇪', DK: '🇩🇰', EE: '🇪🇪',
  ES: '🇪🇸', FI: '🇫🇮', FR: '🇫🇷', GR: '🇬🇷',
  HR: '🇭🇷', HU: '🇭🇺', IT: '🇮🇹', LT: '🇱🇹',
  LU: '🇱🇺', LV: '🇱🇻', MT: '🇲🇹', NL: '🇳🇱',
  PL: '🇵🇱', PT: '🇵🇹', RO: '🇷🇴', SE: '🇸🇪',
  SI: '🇸🇮', SK: '🇸🇰',
  // EEA/EFTA
  NO: '🇳🇴', IS: '🇮🇸', CH: '🇨🇭',
  // North America
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽',
  // Asia-Pacific
  SG: '🇸🇬', MY: '🇲🇾', JP: '🇯🇵', CN: '🇨🇳', KR: '🇰🇷',
  IN: '🇮🇳', TH: '🇹🇭', ID: '🇮🇩', PH: '🇵🇭', VN: '🇻🇳',
  HK: '🇭🇰', TW: '🇹🇼',
  // Middle East
  AE: '🇦🇪', SA: '🇸🇦', QA: '🇶🇦', KW: '🇰🇼', BH: '🇧🇭', OM: '🇴🇲',
  // Africa
  ZA: '🇿🇦', NG: '🇳🇬', KE: '🇰🇪', EG: '🇪🇬', GH: '🇬🇭', TZ: '🇹🇿',
  // South America
  BR: '🇧🇷', AR: '🇦🇷', CL: '🇨🇱', CO: '🇨🇴', PE: '🇵🇪',
  OTHER: '🌐',
};
const COUNTRY_NAMES: Record<string, string> = {
  // Australasia
  AU: 'Australia', NZ: 'New Zealand', PG: 'Papua New Guinea', FJ: 'Fiji',
  // UK & Ireland
  GB: 'United Kingdom', IE: 'Ireland',
  // EU
  AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', CY: 'Cyprus',
  CZ: 'Czech Republic', DE: 'Germany', DK: 'Denmark', EE: 'Estonia',
  ES: 'Spain', FI: 'Finland', FR: 'France', GR: 'Greece',
  HR: 'Croatia', HU: 'Hungary', IT: 'Italy', LT: 'Lithuania',
  LU: 'Luxembourg', LV: 'Latvia', MT: 'Malta', NL: 'Netherlands',
  PL: 'Poland', PT: 'Portugal', RO: 'Romania', SE: 'Sweden',
  SI: 'Slovenia', SK: 'Slovakia',
  // EEA/EFTA
  NO: 'Norway', IS: 'Iceland', CH: 'Switzerland',
  // North America
  US: 'United States', CA: 'Canada', MX: 'Mexico',
  // Asia-Pacific
  SG: 'Singapore', MY: 'Malaysia', JP: 'Japan', CN: 'China', KR: 'South Korea',
  IN: 'India', TH: 'Thailand', ID: 'Indonesia', PH: 'Philippines', VN: 'Vietnam',
  HK: 'Hong Kong', TW: 'Taiwan',
  // Middle East
  AE: 'United Arab Emirates', SA: 'Saudi Arabia', QA: 'Qatar', KW: 'Kuwait', BH: 'Bahrain', OM: 'Oman',
  // Africa
  ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya', EG: 'Egypt', GH: 'Ghana', TZ: 'Tanzania',
  // South America
  BR: 'Brazil', AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru',
  OTHER: 'International / Other',
};
// Grouped for optgroup rendering
const COUNTRY_GROUPS: { label: string; countries: string[] }[] = [
  { label: 'Australasia — AU EPA',             countries: ['AU', 'NZ', 'PG', 'FJ'] },
  { label: 'United Kingdom & Ireland — UK EA', countries: ['GB', 'IE'] },
  { label: 'European Union — EU UWWTD',        countries: ['AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK'] },
  { label: 'EEA / EFTA — EU UWWTD',            countries: ['NO','IS','CH'] },
  { label: 'North America — Intl Ref.',         countries: ['US','CA','MX'] },
  { label: 'Asia-Pacific',                      countries: ['SG','MY','JP','CN','KR','IN','TH','ID','PH','VN','HK','TW'] },
  { label: 'Middle East',                       countries: ['AE','SA','QA','KW','BH','OM'] },
  { label: 'Africa',                            countries: ['ZA','NG','KE','EG','GH','TZ'] },
  { label: 'South America',                     countries: ['BR','AR','CL','CO','PE'] },
  { label: 'Other',                             countries: ['OTHER'] },
];
const DISCHARGE_ROUTES: { value: string; label: string; tip: string }[] = [
  { value: 'controlled_waters', label: 'Controlled Waters (river/lake/sea)', tip: 'Requires Water Discharge Activity Permit (UK EPR) / EPA licence (AU) / UWWTD permit (EU)' },
  { value: 'sewer',             label: 'Sewer / Sewerage System',            tip: 'Requires Trade Effluent Consent (UK Water Industry Act 1991) / Trade Waste Agreement (AU)' },
  { value: 'land',              label: 'Land Application / Irrigation',      tip: 'Requires environmental permit; setback distances and soil suitability assessment required' },
  { value: 'reuse',             label: 'Water Recycling / Reuse Scheme',     tip: 'Governed by AU AGWR / EU Water Reuse Regulation (EU) 2020/741 / site-specific permit (UK)' },
  { value: 'groundwater',       label: 'Groundwater / Soakaway',             tip: 'Requires Groundwater Activity Permit (UK) / specific EPA licence conditions (AU/EU)' },
];
const STANDARD_COLORS: Record<string, { color: string; bg: string; border: string }> = {
  UK_EA:     { color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/20' },
  AU_EPA:    { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  EU_UWWTD:  { color: 'text-violet-400',  bg: 'bg-violet-500/10',  border: 'border-violet-500/20' },
  PL_WP:     { color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20' },
};
const INDUSTRY_SECTORS: { label: string; subsectors: string[] }[] = [
  { label: 'Food & Drink',         subsectors: ['Dairy / Milk Processing', 'Brewery / Distillery', 'Winery / Cider', 'Bakery / Flour Mill', 'Meat & Poultry', 'Seafood / Fish Processing', 'Snack Foods / Ready Meals', 'Beverages / Soft Drinks', 'Confectionery', 'General Food Processing'] },
  { label: 'Agriculture',          subsectors: ['Livestock Farming', 'Crop Production / Arable', 'Horticulture / Nursery', 'Aquaculture / Fish Farm', 'Silage / Biogas'] },
  { label: 'Manufacturing',        subsectors: ['Metal Finishing / Electroplating', 'Chemical Manufacturing', 'Pharmaceutical', 'Textile / Laundry / Dyehouse', 'Paper / Pulp / Packaging', 'Plastics / Rubber', 'Electronics / PCB', 'Automotive', 'General Manufacturing'] },
  { label: 'Mining & Extractive',  subsectors: ['Coal / Colliery', 'Metal Ore / Mineral', 'Stone / Quarry / Aggregate', 'Oil & Gas', 'Landfill / Waste Management'] },
  { label: 'Hospitality',          subsectors: ['Hotel / Accommodation', 'Restaurant / Takeaway', 'Catering / Event Venue', 'Leisure / Spa / Gym'] },
  { label: 'Municipal / Public',   subsectors: ['Municipal / Local Authority', 'Healthcare / Hospital', 'Education / University', 'Military / MOD Site'] },
  { label: 'Other',                subsectors: ['Agricultural Services', 'Transport / Fleet', 'Retail / Warehousing', 'Research / Laboratory', 'Other / Mixed Use'] },
];
// Flat list of all sector labels for dropdowns that only need the top-level
const INDUSTRY_TYPES = INDUSTRY_SECTORS.map(s => s.label);

const REGULATORY_BODIES: { label: string; region: string }[] = [
  { label: 'Environment Agency (EA)',                region: 'England' },
  { label: 'Natural Resources Wales (NRW)',          region: 'Wales' },
  { label: 'Scottish Environment Protection Agency (SEPA)', region: 'Scotland' },
  { label: 'Northern Ireland Environment Agency (NIEA)', region: 'Northern Ireland' },
  { label: 'EPA New South Wales',                    region: 'AU — NSW' },
  { label: 'EPA Victoria',                           region: 'AU — VIC' },
  { label: 'EPA Queensland',                         region: 'AU — QLD' },
  { label: 'EPA South Australia',                    region: 'AU — SA' },
  { label: 'EPA Western Australia',                  region: 'AU — WA' },
  { label: 'EPA Tasmania',                           region: 'AU — TAS' },
  { label: 'EPA Northern Territory',                 region: 'AU — NT' },
  { label: 'ACT Environment, Planning & Sustainable Development', region: 'AU — ACT' },
  { label: 'European Environment Agency (EEA)',      region: 'EU' },
  // Poland — Wody Polskie (PGW WP): national body + 11 regional RZGW offices
  { label: PL_NATIONAL_BODY,                         region: 'PL — national' },
  ...PL_RZGW_BODIES.map(label => ({ label, region: 'PL — ' + label.replace('Wody Polskie — RZGW ', '') })),
  { label: 'Other / International',                  region: 'International' },
];

// Default regulatory body per country (auto-suggested when country is selected)
// AU is left blank — state/territory EPA must be chosen by user
const COUNTRY_TO_REG_BODY: Record<string, string> = {
  // UK nations
  GB: 'Environment Agency (EA)',
  IE: 'Other / International',
  // Australia/Pacific — state-specific, leave empty
  AU: '', NZ: '', PG: '', FJ: '',
  // EU member states + EEA → EEA
  AT: 'European Environment Agency (EEA)', BE: 'European Environment Agency (EEA)',
  BG: 'European Environment Agency (EEA)', CY: 'European Environment Agency (EEA)',
  CZ: 'European Environment Agency (EEA)', DE: 'European Environment Agency (EEA)',
  DK: 'European Environment Agency (EEA)', EE: 'European Environment Agency (EEA)',
  ES: 'European Environment Agency (EEA)', FI: 'European Environment Agency (EEA)',
  FR: 'European Environment Agency (EEA)', GR: 'European Environment Agency (EEA)',
  HR: 'European Environment Agency (EEA)', HU: 'European Environment Agency (EEA)',
  IT: 'European Environment Agency (EEA)', LT: 'European Environment Agency (EEA)',
  LU: 'European Environment Agency (EEA)', LV: 'European Environment Agency (EEA)',
  MT: 'European Environment Agency (EEA)', NL: 'European Environment Agency (EEA)',
  PL: PL_NATIONAL_BODY, PT: 'European Environment Agency (EEA)',
  RO: 'European Environment Agency (EEA)', SE: 'European Environment Agency (EEA)',
  SI: 'European Environment Agency (EEA)', SK: 'European Environment Agency (EEA)',
  NO: 'European Environment Agency (EEA)', IS: 'European Environment Agency (EEA)',
  CH: 'European Environment Agency (EEA)',
  // Rest of world
  US: 'Other / International', CA: 'Other / International', MX: 'Other / International',
  SG: 'Other / International', MY: 'Other / International', JP: 'Other / International',
  CN: 'Other / International', KR: 'Other / International', IN: 'Other / International',
  TH: 'Other / International', ID: 'Other / International', PH: 'Other / International',
  VN: 'Other / International', HK: 'Other / International', TW: 'Other / International',
  AE: 'Other / International', SA: 'Other / International', QA: 'Other / International',
  KW: 'Other / International', BH: 'Other / International', OM: 'Other / International',
  ZA: 'Other / International', NG: 'Other / International', KE: 'Other / International',
  EG: 'Other / International', GH: 'Other / International', TZ: 'Other / International',
  BR: 'Other / International', AR: 'Other / International', CL: 'Other / International',
  CO: 'Other / International', PE: 'Other / International',
};

// ─── Country → valid regulatory bodies (filtered dropdown) ───────────────────
// Only bodies that genuinely operate in the selected country are shown.
// GB shows all 4 UK nations — postcode detection narrows it automatically.
// AU shows all 8 state/territory EPAs — postcode detection selects the right one.
// EU / EEA countries only get EEA. Everything else → Other / International.
const COUNTRY_TO_REG_BODIES: Record<string, string[]> = {
  GB: ['Environment Agency (EA)', 'Natural Resources Wales (NRW)',
       'Scottish Environment Protection Agency (SEPA)', 'Northern Ireland Environment Agency (NIEA)'],
  IE: ['Other / International'],
  AU: ['EPA New South Wales', 'EPA Victoria', 'EPA Queensland', 'EPA South Australia',
       'EPA Western Australia', 'EPA Tasmania', 'EPA Northern Territory',
       'ACT Environment, Planning & Sustainable Development'],
  NZ: ['Other / International'], PG: ['Other / International'], FJ: ['Other / International'],
  AT: ['European Environment Agency (EEA)'], BE: ['European Environment Agency (EEA)'],
  BG: ['European Environment Agency (EEA)'], CY: ['European Environment Agency (EEA)'],
  CZ: ['European Environment Agency (EEA)'], DE: ['European Environment Agency (EEA)'],
  DK: ['European Environment Agency (EEA)'], EE: ['European Environment Agency (EEA)'],
  ES: ['European Environment Agency (EEA)'], FI: ['European Environment Agency (EEA)'],
  FR: ['European Environment Agency (EEA)'], GR: ['European Environment Agency (EEA)'],
  HR: ['European Environment Agency (EEA)'], HU: ['European Environment Agency (EEA)'],
  IT: ['European Environment Agency (EEA)'], LT: ['European Environment Agency (EEA)'],
  LU: ['European Environment Agency (EEA)'], LV: ['European Environment Agency (EEA)'],
  MT: ['European Environment Agency (EEA)'], NL: ['European Environment Agency (EEA)'],
  PL: [PL_NATIONAL_BODY, ...PL_RZGW_BODIES], PT: ['European Environment Agency (EEA)'],
  RO: ['European Environment Agency (EEA)'], SE: ['European Environment Agency (EEA)'],
  SI: ['European Environment Agency (EEA)'], SK: ['European Environment Agency (EEA)'],
  NO: ['European Environment Agency (EEA)'], IS: ['European Environment Agency (EEA)'],
  CH: ['European Environment Agency (EEA)'],
};

/** Returns the REGULATORY_BODIES entries valid for the given country code. */
function getRegBodiesForCountry(countryCode: string): { label: string; region: string }[] {
  if (!countryCode) return REGULATORY_BODIES;
  const allowed = COUNTRY_TO_REG_BODIES[countryCode];
  if (!allowed) return REGULATORY_BODIES.filter(b => b.label === 'Other / International');
  return REGULATORY_BODIES.filter(b => allowed.includes(b.label));
}

/** True when the body is an officially-issued regulatory authority (not 'Other / International'). */
function isOfficialBody(body?: string): boolean {
  return !!body && body !== 'Other / International';
}

/** AU 4-digit postcode → EPA body label */
const AU_STATE_TO_REG_BODY: Record<string, string> = {
  NSW: 'EPA New South Wales',
  VIC: 'EPA Victoria',
  QLD: 'EPA Queensland',
  SA:  'EPA South Australia',
  WA:  'EPA Western Australia',
  TAS: 'EPA Tasmania',
  NT:  'EPA Northern Territory',
  ACT: 'ACT Environment, Planning & Sustainable Development',
};

/** Map UK EA region agency string → regulatory body label in REGULATORY_BODIES */
function ukAgencyToBody(agency: string): string {
  if (agency.includes('Natural Resources Wales') || agency.includes('Wales')) return 'Natural Resources Wales (NRW)';
  if (agency.includes('Scottish') || agency.includes('Scotland'))               return 'Scottish Environment Protection Agency (SEPA)';
  if (agency.includes('Northern Ireland'))                                       return 'Northern Ireland Environment Agency (NIEA)';
  return 'Environment Agency (EA)'; // all EA regions in England
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface Customer {
  id: string;
  serverId?: number;      // DB integer ID
  name: string;
  company: string;
  contactEmail: string;
  phone: string;
  address: string;
  notes: string;
  createdAt: string;
  country?: string;
  industryType?: string;
  standard?: string;
  dischargeRoute?: string;
  isDemo?: boolean;
  // v10 — location
  postcode?: string;
  city?: string;
  siteName?: string;
  siteReference?: string;
  latitude?: number | null;
  longitude?: number | null;
  catchmentArea?: string;
  // v10 — consent / permit
  consentNumber?: string;
  consentIssuedDate?: string;
  consentExpiryDate?: string;
  consentVolumeLimit?: number | null;
  regulatoryBody?: string;
  // v10 — industry ops
  industrySubsector?: string;
  sicCode?: string;
  employeeCount?: number | null;
  operatingHours?: string;
  productionVolume?: string;
  avgFlowRate?: number | null;
  peakFlowRate?: number | null;
  // v10 — billing & admin
  billingAddress?: string;
  accountsEmail?: string;
  environmentalContact?: string;
  environmentalPhone?: string;
  wasteCarrierReg?: string;
}

interface CustomerStats {
  influent: UnifiedSample[];
  effluent: UnifiedSample[];
  trains: UnifiedTrain[];
  proposals: Proposal[];
}

type DetailTab = 'overview' | 'influent' | 'effluent' | 'trains' | 'proposals' | 'sites';

// ─── Storage helpers ──────────────────────────────────────────────────────────
function loadCustomers(): Customer[] {
  try { return JSON.parse(localStorage.getItem('rb_customers') || '[]'); } catch { return []; }
}
function saveCustomers(cs: Customer[]) {
  localStorage.setItem('rb_customers', JSON.stringify(cs));
}

// ─── Param helpers ────────────────────────────────────────────────────────────
// Includes both new (InfluentEntryPage) and legacy key names
const CORE = ['ph', 'cod', 'bod5', 'bod', 'tss', 'nh3n', 'nh3', 'oilgrease', 'oandg', 'og', 'tp', 'tn', 'tds', 'temperature', 'conductivity', 'lead', 'chromium', 'cadmium'];
const PARAM_LABEL: Record<string, string> = {
  ph: 'pH', cod: 'COD', bod: 'BOD', tss: 'TSS', nh3n: 'NH₃-N',
  nh3: 'NH₃', tp: 'TP', tds: 'TDS', temperature: 'Temp',
  flowrate: 'Flow', oilgrease: 'O&G', og: 'O&G', lead: 'Pb', chromium: 'Cr',
};
function paramColor(k: string, v: number): string {
  if (k === 'ph') return v < 6 || v > 9 ? '#ef4444' : v < 6.5 || v > 8.5 ? '#f59e0b' : '#10b981';
  if (k === 'cod') return v > 2000 ? '#ef4444' : v > 500 ? '#f59e0b' : '#10b981';
  if (k === 'tss') return v > 350 ? '#ef4444' : v > 100 ? '#f59e0b' : '#10b981';
  if (k === 'bod') return v > 300 ? '#ef4444' : v > 50 ? '#f59e0b' : '#10b981';
  return 'var(--rb-text-2)';
}

// ─── Status helpers ───────────────────────────────────────────────────────────
const TRAIN_STATUS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  draft:       { label: 'Draft',       color: 'text-slate-400',   bg: 'bg-slate-800',       border: 'border-slate-700' },
  in_progress: { label: 'In Progress', color: 'text-amber-400',   bg: 'bg-amber-500/10',    border: 'border-amber-500/20' },
  assessed:    { label: 'Assessed',    color: 'text-emerald-400', bg: 'bg-emerald-500/10',  border: 'border-emerald-500/20' },
};
const PROPOSAL_STATUS: Record<string, { color: string; bg: string; border: string }> = {
  draft:     { color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20' },
  validated: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
};

// ─── Treatment Efficiency & Effluent Prediction ───────────────────────────────
// Removal efficiencies (%) per equipment key per parameter.
// Based on standard industrial wastewater engineering references.
const TREATMENT_EFFICIENCY: Record<string, Record<string, number>> = {
  screening:  { cod: 5,  bod: 5,  tss: 20, og: 5,  tn: 0,  tp: 0,  nh3n: 0  },
  eq:         { cod: 0,  bod: 0,  tss: 0,  og: 0,  tn: 0,  tp: 0,  nh3n: 0  },
  ph:         { cod: 8,  bod: 5,  tss: 5,  og: 0,  tn: 0,  tp: 5,  nh3n: 0  },
  buffer:     { cod: 0,  bod: 0,  tss: 0,  og: 0,  tn: 0,  tp: 0,  nh3n: 0  },
  daf:        { cod: 55, bod: 55, tss: 87, og: 92, tn: 10, tp: 62, nh3n: 5  },
  lamella:    { cod: 40, bod: 40, tss: 72, og: 62, tn: 5,  tp: 42, nh3n: 5  },
  aeration:   { cod: 82, bod: 90, tss: 30, og: 30, tn: 62, tp: 15, nh3n: 78 },
  secondary:  { cod: 20, bod: 20, tss: 72, og: 10, tn: 5,  tp: 5,  nh3n: 5  },
  multimedia: { cod: 20, bod: 15, tss: 72, og: 30, tn: 5,  tp: 22, nh3n: 5  },
  gac:        { cod: 52, bod: 42, tss: 20, og: 62, tn: 10, tp: 10, nh3n: 10 },
  sludge:     { cod: 0,  bod: 0,  tss: 0,  og: 0,  tn: 0,  tp: 0,  nh3n: 0  },
};

// Maps sample param keys → efficiency table keys
const PARAM_TO_EFF: Record<string, string> = {
  cod: 'cod', bod: 'bod', bod5: 'bod', tss: 'tss',
  og: 'og', oilgrease: 'og', oil: 'og',
  totalNitrogen: 'tn', tn: 'tn', tkn: 'tn',
  totalPhosphorus: 'tp', tp: 'tp',
  nh3: 'nh3n', nh3n: 'nh3n', ammoniaN: 'nh3n',
};

// Apply equipment train to influent params → predicted effluent
function predictEffluent(
  influentParams: Record<string, number>,
  equipmentKeys: string[],
): Record<string, number> {
  const result: Record<string, number> = { ...influentParams };
  for (const eqKey of equipmentKeys) {
    const eff = TREATMENT_EFFICIENCY[eqKey];
    if (!eff) continue;
    for (const [paramKey, value] of Object.entries(result)) {
      const effKey = PARAM_TO_EFF[paramKey.toLowerCase()] ?? PARAM_TO_EFF[paramKey];
      if (effKey && eff[effKey] !== undefined) {
        result[paramKey] = value * (1 - eff[effKey] / 100);
      }
    }
  }
  return result;
}

// Overall removal % for display
function overallRemoval(influent: number, effluent: number): number {
  if (!influent || influent === 0) return 0;
  return Math.round(((influent - effluent) / influent) * 100);
}

// ─── Add Customer Modal ───────────────────────────────────────────────────────
type SectionKey = 'location' | 'regulatory' | 'industry' | 'billing' | 'notes';

function AddCustomerModal({ onClose, onSaved }: { onClose: () => void; onSaved: (c: Customer) => void }) {
  const { userProfile } = useSession();
  const userCountry = (userProfile?.country as string | undefined) ?? '';
  const isAuUser = userCountry === 'AU' || userCountry === 'NZ';

  const emptyForm = {
    // Section 1 — always visible
    company: '', name: '', contactEmail: '', phone: '',
    // Section 2 — Location & Site
    // AU/NZ users default to their country; others must choose
    address: '', city: '', postcode: '', country: isAuUser ? userCountry : '',
    siteName: '', siteReference: '', latitude: '', longitude: '', catchmentArea: '',
    // Section 3 — Regulatory
    manualStandard: '', regulatoryBody: '', dischargeRoute: '',
    consentNumber: '', consentVolumeLimit: '', consentIssuedDate: '', consentExpiryDate: '',
    // Section 4 — Industry & Operations
    industryType: '', industrySubsector: '', sicCode: '',
    employeeCount: '', operatingHours: '', productionVolume: '',
    avgFlowRate: '', peakFlowRate: '',
    // Section 5 — Billing & Admin
    billingAddress: '', accountsEmail: '', wasteCarrierReg: '',
    environmentalContact: '', environmentalPhone: '',
    // Section 6 — Notes
    notes: '',
  };
  const [form, setForm] = useState(emptyForm);
  // Only Location open by default — user opens additional sections as needed
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({
    location: true, regulatory: false, industry: false, billing: false, notes: false,
  });
  const [showOverride, setShowOverride] = useState(false);
  const up = (k: keyof typeof emptyForm, v: string) => setForm(p => ({ ...p, [k]: v }));
  const tog = (s: SectionKey) => setOpen(o => ({ ...o, [s]: !o[s] }));

  // Derive standard only when a country is selected
  const derivedStandard = form.country
    ? (COUNTRY_TO_STANDARD[form.country] || 'UK_EA') as StandardCode
    : null;
  const activeStdCode = (form.manualStandard || derivedStandard || 'UK_EA') as StandardCode;
  const stdInfo = (form.country || form.manualStandard)
    ? (() => { try { return getStandard(activeStdCode); } catch { return null; } })()
    : null;
  const sc = STANDARD_COLORS[activeStdCode] || STANDARD_COLORS.UK_EA;
  const isOverridden = !!form.manualStandard && form.manualStandard !== derivedStandard;

  // Dynamic subsectors based on selected industry sector
  const subsectors = INDUSTRY_SECTORS.find(s => s.label === form.industryType)?.subsectors ?? [];

  const isGb = form.country === 'GB';
  const isAu = form.country === 'AU';

  // ── Auto-detect regulatory body from postcode ──────────────────────────────
  // When GB postcode is entered, use EA region data to identify the correct UK
  // devolved body (EA / NRW / SEPA / NIEA). For AU, map the 4-digit postcode
  // to the relevant state EPA. This runs whenever postcode or country changes.
  useEffect(() => {
    if (!form.postcode) return;
    if (form.country === 'GB') {
      const region = postcodeToUkRegion(form.postcode);
      if (region) {
        const body = ukAgencyToBody(region.agency);
        up('regulatoryBody', body);
        setOpen(o => ({ ...o, regulatory: true }));
      }
    } else if (form.country === 'AU') {
      const state = postcodeToState(form.postcode);
      if (state && AU_STATE_TO_REG_BODY[state.code]) {
        up('regulatoryBody', AU_STATE_TO_REG_BODY[state.code]);
        setOpen(o => ({ ...o, regulatory: true }));
      }
    } else if (form.country === 'PL') {
      const pl = inferPolishRegulator(form.postcode);
      up('regulatoryBody', pl.rzgwLabel);
      setOpen(o => ({ ...o, regulatory: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.postcode, form.country]);

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/50';
  const selCls   = 'w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 focus:outline-none focus:border-rb-brand/50';

  const lbl = (text: string, required = false) => (
    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
      {text}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );
  const field = (label: string, key: keyof typeof emptyForm, ph: string, type = 'text', req = false) => (
    <div className="space-y-0.5">
      {lbl(label, req)}
      <input type={type} value={form[key]} onChange={e => up(key, e.target.value)} placeholder={ph} className={inputCls} />
    </div>
  );

  // Section header button
  const sectionBtn = (key: SectionKey, icon: React.ElementType, title: string, subtitle: string, accent = 'var(--rb-brand)') => {
    const Icon = icon;
    return (
      <button type="button" onClick={() => tog(key)}
        className="w-full flex items-center gap-3 text-left py-3 px-4 rounded-xl border transition-all hover:bg-slate-800/30"
        style={{ borderColor: open[key] ? `${accent}30` : undefined }}
      >
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-slate-200">{title}</p>
          <p className="text-[10px] text-slate-600">{subtitle}</p>
        </div>
        {open[key] ? <ChevronUp className="w-3.5 h-3.5 text-slate-600 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
      </button>
    );
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.company.trim()) { toast.error('Company name and contact name are required'); return; }
    const c: Customer = {
      id: Date.now().toString(),
      name:         form.name.trim(),
      company:      form.company.trim(),
      contactEmail: form.contactEmail.trim(),
      phone:        form.phone.trim(),
      address:      form.address.trim(),
      notes:        form.notes.trim(),
      country:      form.country || undefined,
      industryType: form.industryType || undefined,
      dischargeRoute: form.dischargeRoute || undefined,
      standard:     form.country ? activeStdCode : undefined,  // don't assign a standard if no country set
      createdAt:    new Date().toISOString(),
      // v10
      postcode:     form.postcode.trim() || undefined,
      city:         form.city.trim() || undefined,
      siteName:     form.siteName.trim() || undefined,
      siteReference: form.siteReference.trim() || undefined,
      latitude:     form.latitude ? parseFloat(form.latitude) : null,
      longitude:    form.longitude ? parseFloat(form.longitude) : null,
      catchmentArea: form.catchmentArea.trim() || undefined,
      consentNumber: form.consentNumber.trim() || undefined,
      consentIssuedDate: form.consentIssuedDate || undefined,
      consentExpiryDate: form.consentExpiryDate || undefined,
      consentVolumeLimit: form.consentVolumeLimit ? parseFloat(form.consentVolumeLimit) : null,
      regulatoryBody: form.regulatoryBody || undefined,
      industrySubsector: form.industrySubsector || undefined,
      sicCode:      form.sicCode.trim() || undefined,
      employeeCount: form.employeeCount ? parseInt(form.employeeCount) : null,
      operatingHours: form.operatingHours.trim() || undefined,
      productionVolume: form.productionVolume.trim() || undefined,
      avgFlowRate:  form.avgFlowRate ? parseFloat(form.avgFlowRate) : null,
      peakFlowRate: form.peakFlowRate ? parseFloat(form.peakFlowRate) : null,
      billingAddress: form.billingAddress.trim() || undefined,
      accountsEmail: form.accountsEmail.trim() || undefined,
      environmentalContact: form.environmentalContact.trim() || undefined,
      environmentalPhone: form.environmentalPhone.trim() || undefined,
      wasteCarrierReg: form.wasteCarrierReg.trim() || undefined,
    };
    const all = loadCustomers();
    saveCustomers([c, ...all]);
    toast.success(`${c.company} added — ${stdInfo?.shortName || activeStdCode} standard assigned`);
    onSaved(c);
    onClose();

    // Async API push — fire-and-forget, update localStorage serverId on success
    if (hasAuthToken()) {
      apiSaveCustomer({
        name: c.name, company: c.company, contactEmail: c.contactEmail, phone: c.phone,
        address: c.address, notes: c.notes, country: c.country, standard: c.standard,
        industryType: c.industryType, dischargeRoute: c.dischargeRoute,
        postcode: c.postcode, city: c.city, siteName: c.siteName, siteReference: c.siteReference,
        latitude: c.latitude, longitude: c.longitude, catchmentArea: c.catchmentArea,
        consentNumber: c.consentNumber, consentIssuedDate: c.consentIssuedDate,
        consentExpiryDate: c.consentExpiryDate, consentVolumeLimit: c.consentVolumeLimit,
        regulatoryBody: c.regulatoryBody, industrySubsector: c.industrySubsector,
        sicCode: c.sicCode, employeeCount: c.employeeCount, operatingHours: c.operatingHours,
        productionVolume: c.productionVolume, avgFlowRate: c.avgFlowRate, peakFlowRate: c.peakFlowRate,
        billingAddress: c.billingAddress, accountsEmail: c.accountsEmail,
        environmentalContact: c.environmentalContact, environmentalPhone: c.environmentalPhone,
        wasteCarrierReg: c.wasteCarrierReg,
      }).then((res: any) => {
        if (res?.id) {
          // Enrich the local record with the server ID
          const updated = loadCustomers().map(x => x.id === c.id ? { ...x, serverId: res.id } : x);
          saveCustomers(updated);
        }
      }).catch(() => { /* offline — stays local */ });
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-6 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl overflow-hidden mb-8 rb-section">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-rb-brand/10 to-transparent sticky top-0 z-10 bg-rb-surface">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-rb-brand" />
            <div>
              <h2 className="text-sm font-bold text-slate-100">New Customer Profile</h2>
              <p className="text-[10px] text-slate-500">Complete as many fields as available — standard assigned automatically from country</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">

          {/* ══ SECTION 1 — Company & Contact (always visible) ══ */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Users className="w-3 h-3" /> Company &amp; Contact <span className="text-red-400">— required</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              {field('Company Name', 'company', 'e.g. Windsor Dairy Ltd', 'text', true)}
              {field('Contact Name', 'name', 'e.g. John Windsor', 'text', true)}
              {field('Email', 'contactEmail', 'john@company.com', 'email')}
              {field('Phone', 'phone', '+44 / +61 …')}
            </div>
          </div>

          <div className="border-t border-slate-800/60" />

          {/* ══ SECTION 2 — Location & Site ══ */}
          {sectionBtn('location', MapPin, 'Location & Site', 'Address, postcode, site reference, coordinates', '#14b8a6')}
          <AnimatePresence>
          {open.location && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                {field('Street Address', 'address', 'Street address, building name')}
                <div className="grid grid-cols-3 gap-3">
                  {field('City / Town', 'city', 'e.g. Bristol')}
                  {field('Postcode / ZIP', 'postcode', isGb ? 'e.g. BS1 4DJ' : isAu ? 'e.g. 2000' : 'Postcode')}
                  <div className="space-y-0.5">
                    {lbl('Country')}
                    <select value={form.country} onChange={e => {
                      const c = e.target.value;
                      up('country', c);
                      up('manualStandard', '');
                      setShowOverride(false);
                      // Auto-suggest the relevant regulatory body for this country
                      if (c && Object.prototype.hasOwnProperty.call(COUNTRY_TO_REG_BODY, c)) {
                        up('regulatoryBody', COUNTRY_TO_REG_BODY[c]);
                        // Open Regulatory section so user can see / confirm the auto-filled body
                        if (COUNTRY_TO_REG_BODY[c]) setOpen(o => ({ ...o, regulatory: true }));
                      } else if (c) {
                        up('regulatoryBody', 'Other / International');
                        setOpen(o => ({ ...o, regulatory: true }));
                      }
                    }} className={selCls} disabled={isAuUser}>
                      {isAuUser ? (
                        <option value={userCountry}>{COUNTRY_FLAGS[userCountry] ?? '🌏'} {COUNTRY_NAMES[userCountry] ?? userCountry} — locked to account region</option>
                      ) : (
                        <>
                          <option value="">— Select country —</option>
                          {COUNTRY_GROUPS.map(grp => (
                            <optgroup key={grp.label} label={grp.label}>
                              {grp.countries.map(code => (
                                <option key={code} value={code}>{COUNTRY_FLAGS[code]} {COUNTRY_NAMES[code]}</option>
                              ))}
                            </optgroup>
                          ))}
                        </>
                      )}
                    </select>
                    {isAuUser && (
                      <p className="text-[10px] text-slate-500 mt-0.5">Country locked — your account is registered to {COUNTRY_NAMES[userCountry] ?? userCountry}.</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {field('Primary Site Name', 'siteName', 'e.g. Main Plant, Site A')}
                  {field('Site Reference / ID', 'siteReference', 'e.g. SITE-001')}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {field('Latitude', 'latitude', 'e.g. 51.4545')}
                  {field('Longitude', 'longitude', 'e.g. -2.5879')}
                  {field('Catchment Area', 'catchmentArea', 'e.g. Avon Catchment')}
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ SECTION 3 — Regulatory & Compliance ══ */}
          {sectionBtn('regulatory', ShieldCheck, 'Regulatory & Compliance', 'Standard, regulatory body, consent / discharge permit details')}
          <AnimatePresence>
          {open.regulatory && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                {/* Prompt when no country selected yet */}
                {!form.country && !form.manualStandard && (
                  <div className="flex items-center gap-2 rounded-xl border border-slate-800/40 bg-slate-800/30 p-3">
                    <Globe className="w-4 h-4 text-slate-500 shrink-0" />
                    <p className="text-[11px] text-slate-500">Select a country in the <span className="text-teal-400 font-bold">Location &amp; Site</span> section above — the applicable regulatory standard and body will be filled in automatically.</p>
                  </div>
                )}
                {/* Auto-derived standard banner */}
                {stdInfo && (
                  <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className={`w-4 h-4 shrink-0 ${sc.color}`} />
                        <div>
                          <p className={`text-[11px] font-bold ${sc.color}`}>
                            {stdInfo.flag} {stdInfo.name}
                            {isOverridden && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">MANUAL OVERRIDE</span>}
                          </p>
                          <p className="text-[10px] text-slate-500">{stdInfo.permitType}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${sc.bg} ${sc.border} ${sc.color} shrink-0`}>{stdInfo.shortName}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-1 mt-2">
                      {[{ l: 'COD', v: `≤${stdInfo.limits.cod}` }, { l: 'BOD₅', v: `≤${stdInfo.limits.bod5}` }, { l: 'TSS', v: `≤${stdInfo.limits.tss}` }, { l: 'pH', v: `${stdInfo.limits.phMin}–${stdInfo.limits.phMax}` }].map(({ l, v }) => (
                        <div key={l} className="text-center rounded bg-slate-900/40 py-1 px-0.5">
                          <p className="text-[10px] text-slate-600 font-bold">{l}</p>
                          <p className={`text-[10px] font-black ${sc.color}`}>{v}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[10px] text-slate-500 italic">{stdInfo.actReference}</p>
                      <button type="button" onClick={() => setShowOverride(v => !v)}
                        className="flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-400 transition-colors">
                        <Edit3 className="w-2.5 h-2.5" />
                        {showOverride ? 'Hide override' : 'Override'}
                      </button>
                    </div>
                  </div>
                )}

                {showOverride && (
                  <div className="space-y-1 bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
                    {lbl('Manual Standard Override')}
                    <select value={form.manualStandard} onChange={e => up('manualStandard', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-amber-500/30 bg-slate-800/50 text-sm text-slate-200 focus:outline-none">
                      <option value="">— Use auto-derived ({derivedStandard ? derivedStandard.replace('_', ' ') : 'no country selected'}) —</option>
                      <option value="UK_EA">🇬🇧 UK Environment Agency (UK EA)</option>
                      <option value="AU_EPA">🇦🇺 Australian EPA (AU EPA)</option>
                      <option value="EU_UWWTD">🇪🇺 EU Urban Wastewater (EU UWWTD)</option>
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Regulatory Body')}
                    {/* Dropdown is filtered to bodies valid for the selected country.
                        For GB it auto-narrows via postcode; for AU by state postcode.
                        If no country is set, all bodies are shown as a fallback. */}
                    <select value={form.regulatoryBody} onChange={e => up('regulatoryBody', e.target.value)} className={selCls}>
                      <option value="">— Select body —</option>
                      {getRegBodiesForCountry(form.country).map(b => (
                        <option key={b.label} value={b.label}>{b.label} ({b.region})</option>
                      ))}
                    </select>
                    {form.country && !form.regulatoryBody && (
                      <p className="text-[10px] text-amber-500/80 mt-0.5">
                        {form.country === 'GB' ? 'Enter postcode above to auto-detect EA / NRW / SEPA / NIEA region' :
                         form.country === 'AU' ? 'Enter postcode above to auto-select state EPA' :
                         'Select the applicable regulatory body for this jurisdiction'}
                      </p>
                    )}
                    {form.country && form.regulatoryBody && isOfficialBody(form.regulatoryBody) && (
                      <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">
                        <ShieldCheck className="w-2.5 h-2.5 shrink-0" />
                        Consent / permit fields below are issued by this authority
                      </p>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Discharge Route')}
                    <select value={form.dischargeRoute} onChange={e => up('dischargeRoute', e.target.value)} className={selCls}>
                      <option value="">— Select route —</option>
                      {DISCHARGE_ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                </div>
                {form.dischargeRoute && (
                  <p className="text-[10px] text-slate-500 italic px-1">
                    {DISCHARGE_ROUTES.find(r => r.value === form.dischargeRoute)?.tip}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Consent / Permit Reference')}
                    <input value={form.consentNumber} onChange={e => up('consentNumber', e.target.value)}
                      placeholder={isGb ? 'e.g. EPR/AB1234CD/V001' : isAu ? 'e.g. EPL 12345' : 'Consent number'}
                      className={inputCls} />
                    <p className="text-[10px] text-slate-600">
                      {isGb ? 'UK Environmental Permit or Trade Effluent Consent' : isAu ? 'AU Environment Protection Licence (EPL)' : 'Discharge consent or permit reference'}
                    </p>
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Consented Volume Limit (m³/day)')}
                    <input type="number" min="0" step="0.1" value={form.consentVolumeLimit} onChange={e => up('consentVolumeLimit', e.target.value)}
                      placeholder="e.g. 250" className={inputCls} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Consent Issue Date')}
                    <input type="date" value={form.consentIssuedDate} onChange={e => up('consentIssuedDate', e.target.value)} className={inputCls} />
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Consent Expiry Date')}
                    <input type="date" value={form.consentExpiryDate} onChange={e => up('consentExpiryDate', e.target.value)} className={inputCls} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ SECTION 4 — Industry & Operations ══ */}
          {sectionBtn('industry', Factory, 'Industry & Operations', 'Sector, subsector, SIC code, employee count, flow rates', '#f59e0b')}
          <AnimatePresence>
          {open.industry && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Industry Sector')}
                    <select value={form.industryType} onChange={e => { up('industryType', e.target.value); up('industrySubsector', ''); }} className={selCls}>
                      <option value="">— Select sector —</option>
                      {INDUSTRY_SECTORS.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Industry Subsector')}
                    <select value={form.industrySubsector} onChange={e => up('industrySubsector', e.target.value)} className={selCls}
                      disabled={subsectors.length === 0}>
                      <option value="">{subsectors.length === 0 ? '— Select sector first —' : '— Select subsector —'}</option>
                      {subsectors.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {field('SIC Code', 'sicCode', 'e.g. 10510')}
                  {field('Employee Count', 'employeeCount', 'e.g. 120', 'number')}
                  {field('Operating Hours', 'operatingHours', 'e.g. 24/7 or 8–18 Mon–Fri')}
                </div>

                {field('Production Volume', 'productionVolume', 'e.g. 50,000 L/day milk intake')}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Average Flow Rate (m³/day)')}
                    <input type="number" min="0" step="0.1" value={form.avgFlowRate} onChange={e => up('avgFlowRate', e.target.value)}
                      placeholder="e.g. 150" className={inputCls} />
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Peak Flow Rate (m³/day)')}
                    <input type="number" min="0" step="0.1" value={form.peakFlowRate} onChange={e => up('peakFlowRate', e.target.value)}
                      placeholder="e.g. 300" className={inputCls} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ SECTION 5 — Billing & Admin ══ */}
          {sectionBtn('billing', Building, 'Billing & Administration', 'Billing address, accounts email, waste carrier, environmental contact', '#6366f1')}
          <AnimatePresence>
          {open.billing && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                <div className="space-y-0.5">
                  {lbl('Billing Address')}
                  <textarea value={form.billingAddress} onChange={e => up('billingAddress', e.target.value)}
                    placeholder="If different from site address" rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/50 resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {field('Accounts Email', 'accountsEmail', 'accounts@company.com', 'email')}
                  {field('Waste Carrier Reg.', 'wasteCarrierReg', 'e.g. CBDU12345 (UK) / WC-AU-001 (AU)')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {field('Environmental Contact', 'environmentalContact', 'Name of EHS manager')}
                  {field('Environmental Phone', 'environmentalPhone', '+44 …')}
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ SECTION 6 — Notes ══ */}
          {sectionBtn('notes', Info, 'Notes & Additional Info', 'Free text — special conditions, monitoring requirements, etc.')}
          <AnimatePresence>
          {open.notes && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="pb-2">
                <textarea value={form.notes} onChange={e => up('notes', e.target.value)}
                  placeholder="Permit conditions, PE classification, discharge point, special monitoring requirements, consent history…"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/50 resize-none" />
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Cancel</button>
            <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 text-xs font-black hover:from-[#b8931f] hover:to-[#a07d1a] flex items-center justify-center gap-2">
              <Building2 className="w-3.5 h-3.5" /> Add Customer
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Edit Regulatory Profile Modal ────────────────────────────────────────────
function EditRegulatoryModal({
  customer, onClose, onSaved,
}: {
  customer: Customer;
  onClose: () => void;
  onSaved: (updated: Partial<Customer>) => void;
}) {
  const { userProfile } = useSession();
  const userCountry = (userProfile?.country as string | undefined) ?? '';
  const isAuUser = userCountry === 'AU' || userCountry === 'NZ';
  const [country, setCountry] = useState(customer.country || (isAuUser ? userCountry : 'GB'));
  const [regulatoryBody, setRegulatoryBody] = useState(customer.regulatoryBody || COUNTRY_TO_REG_BODY[customer.country || ''] || '');
  const [industryType, setIndustryType] = useState(customer.industryType || '');
  const [dischargeRoute, setDischargeRoute] = useState(customer.dischargeRoute || '');
  const [manualStandard, setManualStandard] = useState('');
  const [showOverride, setShowOverride] = useState(false);

  const derivedStandard = (COUNTRY_TO_STANDARD[country] || 'UK_EA') as StandardCode;
  const activeStdCode   = (manualStandard || derivedStandard) as StandardCode;
  const stdInfo = (() => { try { return getStandard(activeStdCode); } catch { return null; } })();
  const sc = STANDARD_COLORS[activeStdCode] || STANDARD_COLORS.UK_EA;
  const isOverridden = !!manualStandard && manualStandard !== derivedStandard;

  // When country changes, reset body to the default for that country
  const handleCountryChange = (c: string) => {
    setCountry(c);
    setManualStandard('');
    setShowOverride(false);
    const defaultBody = COUNTRY_TO_REG_BODY[c] || '';
    setRegulatoryBody(defaultBody);
  };

  const selClass = 'w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 focus:outline-none focus:border-rb-brand/50';

  const handleSave = () => {
    onSaved({ country, regulatoryBody, industryType, dischargeRoute, standard: activeStdCode });
    toast.success(`Regulatory profile updated — ${stdInfo?.shortName || activeStdCode}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg overflow-hidden mb-8 rb-section">

        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-rb-brand/10 to-transparent">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-rb-brand" />
            <div>
              <h2 className="text-sm font-bold text-slate-100">Edit Regulatory Profile</h2>
              <p className="text-[10px] text-slate-500">{customer.company}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Country */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              Country / Jurisdiction <span className="text-red-400">*</span>
            </label>
            <select value={country} onChange={e => handleCountryChange(e.target.value)}
              className={selClass} disabled={isAuUser}>
              {isAuUser ? (
                <option value={userCountry}>{COUNTRY_FLAGS[userCountry] ?? '🌏'} {COUNTRY_NAMES[userCountry] ?? userCountry} — locked to account region</option>
              ) : (
                COUNTRY_GROUPS.map(grp => (
                  <optgroup key={grp.label} label={grp.label}>
                    {grp.countries.map(code => (
                      <option key={code} value={code}>{COUNTRY_FLAGS[code]} {COUNTRY_NAMES[code]}</option>
                    ))}
                  </optgroup>
                ))
              )}
            </select>
          </div>

          {/* Regulatory Body — filtered to selected country */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Regulatory Body
            </label>
            <select value={regulatoryBody} onChange={e => setRegulatoryBody(e.target.value)} className={selClass}>
              <option value="">— Select body —</option>
              {getRegBodiesForCountry(country).map(b => (
                <option key={b.label} value={b.label}>{b.label} ({b.region})</option>
              ))}
            </select>
            {isOfficialBody(regulatoryBody) && (
              <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-0.5">
                <Lock className="w-2.5 h-2.5 shrink-0" />
                Consent / permit details are issued by this authority — only amend when a formal variation is received
              </p>
            )}
          </div>

          {/* Industry + Discharge route */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Industry Type</label>
              <select value={industryType} onChange={e => setIndustryType(e.target.value)} className={selClass}>
                <option value="">— Select industry —</option>
                {INDUSTRY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Discharge Route</label>
              <select value={dischargeRoute} onChange={e => setDischargeRoute(e.target.value)} className={selClass}>
                <option value="">— Select route —</option>
                {DISCHARGE_ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          {dischargeRoute && (
            <p className="text-[10px] text-slate-500 italic">
              {DISCHARGE_ROUTES.find(r => r.value === dischargeRoute)?.tip}
            </p>
          )}

          {/* Derived standard preview */}
          {stdInfo && (
            <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className={`w-4 h-4 ${sc.color} shrink-0`} />
                  <div>
                    <p className={`text-[11px] font-bold ${sc.color}`}>
                      {stdInfo.flag} {stdInfo.name}
                      {isOverridden && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">OVERRIDE</span>}
                    </p>
                    <p className="text-[10px] text-slate-500">{stdInfo.permitType}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${sc.bg} ${sc.border} ${sc.color}`}>{stdInfo.shortName}</span>
              </div>
              <div className="grid grid-cols-4 gap-1 mt-2">
                {[
                  { l: 'COD', v: `≤${stdInfo.limits.cod}` },
                  { l: 'BOD₅', v: `≤${stdInfo.limits.bod5}` },
                  { l: 'TSS', v: `≤${stdInfo.limits.tss}` },
                  { l: 'Hg', v: `≤${stdInfo.limits.mercury}` },
                ].map(({ l, v }) => (
                  <div key={l} className="text-center rounded bg-slate-900/40 py-1">
                    <p className="text-[10px] text-slate-600 font-bold">{l}</p>
                    <p className={`text-[10px] font-black ${sc.color}`}>{v}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-600 italic mt-2">{stdInfo.actReference}</p>
            </div>
          )}

          {/* Override toggle */}
          <div>
            <button type="button" onClick={() => setShowOverride(v => !v)}
              className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-300 transition-colors">
              <Edit3 className="w-2.5 h-2.5" />
              {showOverride ? 'Hide override' : 'Manually override standard'}
            </button>
            {showOverride && (
              <div className="mt-2 space-y-1">
                <label className="text-[10px] font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1">
                  <AlertTriangle className="w-2.5 h-2.5" /> Manual Standard Override
                </label>
                <select value={manualStandard} onChange={e => setManualStandard(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-amber-500/30 bg-slate-800/50 text-sm text-slate-200 focus:outline-none">
                  <option value="">— Auto-derive from country ({derivedStandard}) —</option>
                  <option value="UK_EA">🇬🇧 UK Environment Agency (UK EA)</option>
                  <option value="AU_EPA">🇦🇺 Australian EPA (AU EPA)</option>
                  <option value="EU_UWWTD">🇪🇺 EU Urban Wastewater (EU UWWTD)</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Cancel</button>
            <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 text-xs font-black hover:from-[#b8931f] hover:to-[#a07d1a] flex items-center justify-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Save Profile
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Edit Customer Modal ───────────────────────────────────────────────────────
// Full-form edit modal pre-filled with existing customer data.
// Regulatory-issued fields (consent number, dates, volume) show a lock warning
// when the customer has an official regulatory body.
function EditCustomerModal({
  customer, onClose, onSaved,
}: {
  customer: Customer;
  onClose: () => void;
  onSaved: (updated: Partial<Customer>) => void;
}) {
  type SectionKey = 'location' | 'regulatory' | 'industry' | 'billing' | 'notes';
  const emptyForm = {
    company: customer.company || '',
    name: customer.name || '',
    contactEmail: customer.contactEmail || '',
    phone: customer.phone || '',
    address: customer.address || '',
    city: customer.city || '',
    postcode: customer.postcode || '',
    country: customer.country || '',
    siteName: customer.siteName || '',
    siteReference: customer.siteReference || '',
    latitude: customer.latitude != null ? String(customer.latitude) : '',
    longitude: customer.longitude != null ? String(customer.longitude) : '',
    catchmentArea: customer.catchmentArea || '',
    regulatoryBody: customer.regulatoryBody || '',
    dischargeRoute: customer.dischargeRoute || '',
    consentNumber: customer.consentNumber || '',
    consentVolumeLimit: customer.consentVolumeLimit != null ? String(customer.consentVolumeLimit) : '',
    consentIssuedDate: customer.consentIssuedDate || '',
    consentExpiryDate: customer.consentExpiryDate || '',
    industryType: customer.industryType || '',
    industrySubsector: customer.industrySubsector || '',
    sicCode: customer.sicCode || '',
    employeeCount: customer.employeeCount != null ? String(customer.employeeCount) : '',
    operatingHours: customer.operatingHours || '',
    productionVolume: customer.productionVolume || '',
    avgFlowRate: customer.avgFlowRate != null ? String(customer.avgFlowRate) : '',
    peakFlowRate: customer.peakFlowRate != null ? String(customer.peakFlowRate) : '',
    billingAddress: customer.billingAddress || '',
    accountsEmail: customer.accountsEmail || '',
    wasteCarrierReg: customer.wasteCarrierReg || '',
    environmentalContact: customer.environmentalContact || '',
    environmentalPhone: customer.environmentalPhone || '',
    notes: customer.notes || '',
  };

  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({
    location: true, regulatory: true, industry: false, billing: false, notes: false,
  });

  const up = (k: keyof typeof emptyForm, v: string) => setForm(p => ({ ...p, [k]: v }));
  const tog = (s: SectionKey) => setOpen(o => ({ ...o, [s]: !o[s] }));

  const derivedStandard = form.country
    ? (COUNTRY_TO_STANDARD[form.country] || 'UK_EA') as StandardCode
    : (COUNTRY_TO_STANDARD[customer.country || 'GB'] || 'UK_EA') as StandardCode;
  const sc = STANDARD_COLORS[derivedStandard] || STANDARD_COLORS.UK_EA;

  const isGb = form.country === 'GB';
  const isAu = form.country === 'AU';
  const official = isOfficialBody(form.regulatoryBody);

  // Auto-detect body from postcode when country is GB or AU
  useEffect(() => {
    if (!form.postcode) return;
    if (form.country === 'GB') {
      const region = postcodeToUkRegion(form.postcode);
      if (region) up('regulatoryBody', ukAgencyToBody(region.agency));
    } else if (form.country === 'AU') {
      const state = postcodeToState(form.postcode);
      if (state && AU_STATE_TO_REG_BODY[state.code]) up('regulatoryBody', AU_STATE_TO_REG_BODY[state.code]);
    } else if (form.country === 'PL') {
      up('regulatoryBody', inferPolishRegulator(form.postcode).rzgwLabel);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.postcode, form.country]);

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/50';
  const selCls   = 'w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 focus:outline-none focus:border-rb-brand/50';
  const lbl = (text: string, required = false) => (
    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
      {text}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );
  const lockLbl = (text: string) => (
    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1">
      {text}
      {official && <Lock className="w-2.5 h-2.5 text-amber-500/70 shrink-0" title="Regulatory-issued — only amend when a formal permit variation is received" />}
    </label>
  );
  const field = (label: string, key: keyof typeof emptyForm, ph: string, type = 'text', req = false) => (
    <div className="space-y-0.5">
      {lbl(label, req)}
      <input type={type} value={form[key]} onChange={e => up(key, e.target.value)} placeholder={ph} className={inputCls} />
    </div>
  );
  const sectionBtn = (key: SectionKey, Icon: React.ElementType, title: string, subtitle: string, accent = 'var(--rb-brand)') => (
    <button type="button" onClick={() => tog(key)}
      className="w-full flex items-center gap-3 text-left py-3 px-4 rounded-xl border transition-all hover:bg-slate-800/30"
      style={{ borderColor: open[key] ? `${accent}30` : undefined }}>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-slate-200">{title}</p>
        <p className="text-[10px] text-slate-600">{subtitle}</p>
      </div>
      {open[key] ? <ChevronUp className="w-3.5 h-3.5 text-slate-600 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
    </button>
  );
  const subsectors = INDUSTRY_SECTORS.find(s => s.label === form.industryType)?.subsectors ?? [];

  const handleSave = () => {
    if (!form.name.trim() || !form.company.trim()) { toast.error('Company name and contact name are required'); return; }
    const updated: Partial<Customer> = {
      name: form.name.trim(),
      company: form.company.trim(),
      contactEmail: form.contactEmail.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      notes: form.notes.trim(),
      country: form.country || undefined,
      industryType: form.industryType || undefined,
      industrySubsector: form.industrySubsector || undefined,
      dischargeRoute: form.dischargeRoute || undefined,
      standard: form.country ? derivedStandard : undefined,
      postcode: form.postcode.trim() || undefined,
      city: form.city.trim() || undefined,
      siteName: form.siteName.trim() || undefined,
      siteReference: form.siteReference.trim() || undefined,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
      catchmentArea: form.catchmentArea.trim() || undefined,
      consentNumber: form.consentNumber.trim() || undefined,
      consentIssuedDate: form.consentIssuedDate || undefined,
      consentExpiryDate: form.consentExpiryDate || undefined,
      consentVolumeLimit: form.consentVolumeLimit ? parseFloat(form.consentVolumeLimit) : null,
      regulatoryBody: form.regulatoryBody || undefined,
      sicCode: form.sicCode.trim() || undefined,
      employeeCount: form.employeeCount ? parseInt(form.employeeCount) : null,
      operatingHours: form.operatingHours.trim() || undefined,
      productionVolume: form.productionVolume.trim() || undefined,
      avgFlowRate: form.avgFlowRate ? parseFloat(form.avgFlowRate) : null,
      peakFlowRate: form.peakFlowRate ? parseFloat(form.peakFlowRate) : null,
      billingAddress: form.billingAddress.trim() || undefined,
      accountsEmail: form.accountsEmail.trim() || undefined,
      environmentalContact: form.environmentalContact.trim() || undefined,
      environmentalPhone: form.environmentalPhone.trim() || undefined,
      wasteCarrierReg: form.wasteCarrierReg.trim() || undefined,
    };
    onSaved(updated);
    toast.success(`${form.company} — customer details updated`);

    // Async API push
    const serverCid = (customer as any).serverId;
    if (hasAuthToken() && serverCid) {
      apiUpdateCustomer(serverCid, updated).catch(() => { /* offline — stays local */ });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[250] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-6 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl overflow-hidden mb-8 rb-section">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-rb-brand/10 to-transparent sticky top-0 z-10 bg-rb-surface">
          <div className="flex items-center gap-3">
            <Pencil className="w-5 h-5 text-rb-brand" />
            <div>
              <h2 className="text-sm font-bold text-slate-100">Edit Customer — {customer.company}</h2>
              <p className="text-[10px] text-slate-500">
                {official
                  ? `Regulatory body: ${form.regulatoryBody} — permit fields are authority-issued`
                  : 'All fields are freely editable'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">

          {/* ══ Company & Contact ══ */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Users className="w-3 h-3" /> Company &amp; Contact
            </p>
            <div className="grid grid-cols-2 gap-3">
              {field('Company Name', 'company', 'e.g. Windsor Dairy Ltd', 'text', true)}
              {field('Contact Name', 'name', 'e.g. John Windsor', 'text', true)}
              {field('Email', 'contactEmail', 'john@company.com', 'email')}
              {field('Phone', 'phone', '+44 / +61 …')}
            </div>
          </div>

          <div className="border-t border-slate-800/60" />

          {/* ══ Location & Site ══ */}
          {sectionBtn('location', MapPin, 'Location & Site', 'Address, postcode, site reference, coordinates', '#14b8a6')}
          <AnimatePresence>
          {open.location && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                {field('Street Address', 'address', 'Street address, building name')}
                <div className="grid grid-cols-3 gap-3">
                  {field('City / Town', 'city', 'e.g. Bristol')}
                  {field('Postcode / ZIP', 'postcode', isGb ? 'e.g. BS1 4DJ' : isAu ? 'e.g. 2000' : 'Postcode')}
                  <div className="space-y-0.5">
                    {lbl('Country')}
                    <select value={form.country} onChange={e => {
                      const c = e.target.value;
                      up('country', c);
                      const defaultBody = COUNTRY_TO_REG_BODY[c] || (COUNTRY_TO_REG_BODIES[c]?.length === 1 ? COUNTRY_TO_REG_BODIES[c][0] : '');
                      up('regulatoryBody', defaultBody);
                    }} className={selCls}>
                      <option value="">— Select country —</option>
                      {COUNTRY_GROUPS.map(grp => (
                        <optgroup key={grp.label} label={grp.label}>
                          {grp.countries.map(code => (
                            <option key={code} value={code}>{COUNTRY_FLAGS[code]} {COUNTRY_NAMES[code]}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {field('Primary Site Name', 'siteName', 'e.g. Main Plant, Site A')}
                  {field('Site Reference / ID', 'siteReference', 'e.g. SITE-001')}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {field('Latitude', 'latitude', 'e.g. 51.4545')}
                  {field('Longitude', 'longitude', 'e.g. -2.5879')}
                  {field('Catchment Area', 'catchmentArea', 'e.g. Avon Catchment')}
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ Regulatory & Compliance ══ */}
          {sectionBtn('regulatory', ShieldCheck, 'Regulatory & Compliance', 'Regulatory body, consent / permit details')}
          <AnimatePresence>
          {open.regulatory && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">

                {/* Standard banner */}
                {form.country && (() => { try { return getStandard(derivedStandard); } catch { return null; } })() && (
                  <div className={`rounded-xl border p-2.5 ${sc.bg} ${sc.border} flex items-center gap-2`}>
                    <ShieldCheck className={`w-3.5 h-3.5 shrink-0 ${sc.color}`} />
                    <p className={`text-[10px] font-bold ${sc.color}`}>
                      {COUNTRY_FLAGS[form.country]} {derivedStandard.replace('_', ' ')} standard — auto-derived from country
                    </p>
                  </div>
                )}

                {/* Official body warning banner */}
                {official && (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3">
                    <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] font-bold text-amber-400">Regulatory-issued documents</p>
                      <p className="text-[10px] text-amber-400/70 leading-relaxed mt-0.5">
                        Consent number, issue/expiry dates and volume limits are issued by <strong>{form.regulatoryBody}</strong>.
                        Only amend these fields when a formal permit variation or renewal has been received from the authority.
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Regulatory Body')}
                    <select value={form.regulatoryBody} onChange={e => up('regulatoryBody', e.target.value)} className={selCls}>
                      <option value="">— Select body —</option>
                      {getRegBodiesForCountry(form.country).map(b => (
                        <option key={b.label} value={b.label}>{b.label} ({b.region})</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Discharge Route')}
                    <select value={form.dischargeRoute} onChange={e => up('dischargeRoute', e.target.value)} className={selCls}>
                      <option value="">— Select route —</option>
                      {DISCHARGE_ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lockLbl('Consent / Permit Reference')}
                    <input value={form.consentNumber} onChange={e => up('consentNumber', e.target.value)}
                      placeholder={isGb ? 'e.g. EPR/AB1234CD/V001' : isAu ? 'e.g. EPL 12345' : 'Consent number'}
                      className={inputCls} />
                  </div>
                  <div className="space-y-0.5">
                    {lockLbl('Consented Volume Limit (m³/day)')}
                    <input type="number" min="0" step="0.1" value={form.consentVolumeLimit}
                      onChange={e => up('consentVolumeLimit', e.target.value)}
                      placeholder="e.g. 250" className={inputCls} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lockLbl('Consent Issue Date')}
                    <input type="date" value={form.consentIssuedDate} onChange={e => up('consentIssuedDate', e.target.value)} className={inputCls} />
                  </div>
                  <div className="space-y-0.5">
                    {lockLbl('Consent Expiry Date')}
                    <input type="date" value={form.consentExpiryDate} onChange={e => up('consentExpiryDate', e.target.value)} className={inputCls} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ Industry & Operations ══ */}
          {sectionBtn('industry', Factory, 'Industry & Operations', 'Sector, subsector, SIC code, flow rates', '#f59e0b')}
          <AnimatePresence>
          {open.industry && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Industry Sector')}
                    <select value={form.industryType} onChange={e => { up('industryType', e.target.value); up('industrySubsector', ''); }} className={selCls}>
                      <option value="">— Select sector —</option>
                      {INDUSTRY_SECTORS.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Industry Subsector')}
                    <select value={form.industrySubsector} onChange={e => up('industrySubsector', e.target.value)} className={selCls} disabled={subsectors.length === 0}>
                      <option value="">{subsectors.length === 0 ? '— Select sector first —' : '— Select subsector —'}</option>
                      {subsectors.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {field('SIC Code', 'sicCode', 'e.g. 10510')}
                  {field('Employee Count', 'employeeCount', 'e.g. 120', 'number')}
                  {field('Operating Hours', 'operatingHours', 'e.g. 24/7')}
                </div>
                {field('Production Volume', 'productionVolume', 'e.g. 50,000 L/day milk intake')}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    {lbl('Average Flow Rate (m³/day)')}
                    <input type="number" min="0" step="0.1" value={form.avgFlowRate} onChange={e => up('avgFlowRate', e.target.value)} placeholder="e.g. 150" className={inputCls} />
                  </div>
                  <div className="space-y-0.5">
                    {lbl('Peak Flow Rate (m³/day)')}
                    <input type="number" min="0" step="0.1" value={form.peakFlowRate} onChange={e => up('peakFlowRate', e.target.value)} placeholder="e.g. 300" className={inputCls} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ Billing & Admin ══ */}
          {sectionBtn('billing', Building, 'Billing & Administration', 'Billing address, accounts, environmental contact', '#6366f1')}
          <AnimatePresence>
          {open.billing && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="space-y-3 pb-2">
                <div className="space-y-0.5">
                  {lbl('Billing Address')}
                  <textarea value={form.billingAddress} onChange={e => up('billingAddress', e.target.value)}
                    placeholder="If different from site address" rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/50 resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {field('Accounts Email', 'accountsEmail', 'accounts@company.com', 'email')}
                  {field('Waste Carrier Reg.', 'wasteCarrierReg', 'e.g. CBDU12345 (UK)')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {field('Environmental Contact', 'environmentalContact', 'Name of EHS manager')}
                  {field('Environmental Phone', 'environmentalPhone', '+44 …')}
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* ══ Notes ══ */}
          {sectionBtn('notes', Info, 'Notes & Additional Info', 'Permit conditions, monitoring requirements, etc.')}
          <AnimatePresence>
          {open.notes && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="pb-2">
                <textarea value={form.notes} onChange={e => up('notes', e.target.value)}
                  placeholder="Permit conditions, discharge point, special monitoring requirements…"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-800/60 bg-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/50 resize-none" />
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Cancel</button>
            <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 text-xs font-black hover:from-[#b8931f] hover:to-[#a07d1a] flex items-center justify-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Regulatory compliance panel ──────────────────────────────────────────────
function RegulatoryOverview({ customer, onEditRegulatory }: { customer: Customer; onEditRegulatory?: () => void }) {
  const standardCode = (customer.standard || COUNTRY_TO_STANDARD[customer.country || 'GB'] || 'UK_EA') as StandardCode;
  const std = (() => { try { return getStandard(standardCode); } catch { return null; } })();
  const sc = STANDARD_COLORS[standardCode] || STANDARD_COLORS.UK_EA;
  if (!std) return null;

  const KEY_LIMITS = [
    { label: 'COD',    value: `≤ ${std.limits.cod} mg/L`,         critical: false },
    { label: 'BOD₅',  value: `≤ ${std.limits.bod5} mg/L`,         critical: false },
    { label: 'TSS',   value: `≤ ${std.limits.tss} mg/L`,          critical: false },
    { label: 'NH₃-N', value: `≤ ${std.limits.ammoniaN} mg/L`,     critical: false },
    { label: 'pH',    value: `${std.limits.phMin}–${std.limits.phMax}`, critical: false },
    { label: 'Total P', value: `≤ ${std.limits.totalPhosphorus} mg/L`, critical: false },
    { label: 'Total N', value: `≤ ${std.limits.totalNitrogen} mg/L`,   critical: false },
    { label: 'O&G',   value: `≤ ${std.limits.oilGrease} mg/L`,    critical: false },
    { label: 'Pb',    value: `≤ ${std.limits.lead} mg/L`,          critical: false },
    { label: 'Cd',    value: `≤ ${std.limits.cadmium} mg/L`,       critical: true  },
    { label: 'Hg',    value: `≤ ${std.limits.mercury} mg/L`,       critical: true  },
    { label: 'As',    value: `≤ ${std.limits.arsenic} mg/L`,       critical: false },
    { label: 'Phenol',value: `≤ ${std.limits.phenol} mg/L`,        critical: false },
    { label: 'Surf.', value: `≤ ${std.limits.surfactants} mg/L`,   critical: false },
    { label: 'TDS',   value: `≤ ${std.limits.tds} mg/L`,          critical: false },
  ];

  const dischargeInfo = customer.dischargeRoute
    ? DISCHARGE_ROUTES.find(r => r.value === customer.dischargeRoute) : null;

  return (
    <div className="overflow-hidden rb-card">
      {/* Header */}
      <div className={`px-4 py-3 border-b ${sc.border} bg-gradient-to-r from-transparent ${sc.bg}/20`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`w-4 h-4 ${sc.color}`} />
            <p className={`text-[11px] font-bold uppercase tracking-wider ${sc.color}`}>
              {std.flag} {std.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded border ${sc.bg} ${sc.border} ${sc.color}`}>
              {std.shortName}
            </span>
            {onEditRegulatory && (
              <button
                onClick={onEditRegulatory}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-rb-brand/10 hover:text-rb-brand hover:border-rb-brand/30 transition-all"
              >
                <Edit3 className="w-2.5 h-2.5" /> Edit
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mt-0.5">{std.permitType}</p>
        {dischargeInfo && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded bg-slate-800/40 border border-slate-800/40">
            <ArrowRight className="w-2.5 h-2.5 text-rb-brand shrink-0" />
            <span className="text-[10px] font-bold text-slate-300">{dischargeInfo.label}</span>
          </div>
        )}
      </div>

      {/* Discharge limits grid — full 15-param view */}
      <div className="p-3">
        <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-2">
          Discharge Limits — {std.labels.dischargeLimitsTitle}
        </p>
        <div className="grid grid-cols-5 gap-1">
          {KEY_LIMITS.map(lim => (
            <div key={lim.label} className={`rounded-lg border p-2 text-center ${
              lim.critical
                ? 'bg-red-500/5 border-red-500/20'
                : 'bg-slate-800/60 border-slate-800/40'
            }`}>
              <p className={`text-[10px] font-bold mb-0.5 ${lim.critical ? 'text-red-400' : 'text-slate-500'}`}>{lim.label}</p>
              <p className={`text-[10px] font-black ${lim.critical ? 'text-red-300' : sc.color}`}>{lim.value}</p>
            </div>
          ))}
        </div>
        {/* Mercury / cadmium note */}
        <p className="text-[10px] text-slate-600 mt-1.5 italic">
          * Hg (mercury) and Cd (cadmium) highlighted — EQS Priority Hazardous Substances requiring trace-level management
        </p>
      </div>

      {/* Compliance notes */}
      <div className="px-3 pb-3 border-t border-slate-800/60">
        <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-2 mt-2">Regulatory Requirements</p>
        <div className="space-y-1">
          {std.complianceNotes.slice(0, 4).map((note, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <div className={`w-1 h-1 rounded-full ${sc.color.replace('text-', 'bg-')} mt-1.5 shrink-0`} />
              <p className="text-[10px] text-slate-500 leading-relaxed">{note}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-600 mt-2 italic">{std.actReference}</p>
      </div>
    </div>
  );
}

// ─── Site Card ────────────────────────────────────────────────────────────────
import SiteMapPanel from '@/components/SiteMapPanel';

/**
 * SiteEquipmentPanel — fetches site_equipment rows for a site and groups by train.
 * Only renders when site has serverId (DB-backed) and user is authenticated.
 */
function SiteEquipmentPanel({ site }: { site: CustomerSite }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openTrain, setOpenTrain] = useState<string | null>(null);

  useEffect(() => {
    if (!site.serverId) return;
    if (!hasAuth()) return;
    setLoading(true);
    apiListSiteEquipment(site.serverId)
      .then((data) => { setRows(data || []); setError(null); })
      .catch((e) => setError(e?.message || 'Failed to load equipment'))
      .finally(() => setLoading(false));
  }, [site.serverId]);

  if (!site.serverId) {
    return (
      <div className="rb-inner-card p-3 mt-2">
        <p className="text-[11px] text-slate-500">Site is local-only — push to DB to enable equipment assignments.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rb-inner-card p-3 mt-2">
        <p className="text-[11px] text-slate-500">Loading assigned equipment…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rb-inner-card p-3 mt-2 border border-rose-500/20 bg-rose-500/5">
        <p className="text-[11px] text-rose-400">Equipment lookup failed: {error}</p>
        <p className="text-[10px] text-slate-500 mt-1">(Migration not run? Run migration_site_equipment.sql on the prod DB.)</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rb-inner-card p-3 mt-2">
        <p className="text-[11px] text-slate-500">No equipment assigned to this site yet.</p>
      </div>
    );
  }

  // Group by train_key
  const groups = new Map<string, { label: string; trainKey: string; items: any[] }>();
  for (const r of rows) {
    const key = r.train_key || '_default';
    if (!groups.has(key)) {
      groups.set(key, { label: r.train_label || key, trainKey: key, items: [] });
    }
    groups.get(key)!.items.push(r);
  }
  const groupList = Array.from(groups.values()).sort((a, b) => a.trainKey.localeCompare(b.trainKey));

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold text-slate-300 uppercase">Assigned Equipment ({rows.length} items · {groupList.length} train{groupList.length === 1 ? '' : 's'})</p>
      </div>
      {groupList.map(g => {
        const open = openTrain === g.trainKey;
        const last = g.items[g.items.length - 1];
        const outcome = last?.outcome_json || {};
        return (
          <div key={g.trainKey} className="rb-inner-card p-2 space-y-1">
            <div className="w-full flex items-center justify-between gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); setOpenTrain(open ? null : g.trainKey); }}
                className="flex-1 flex items-center justify-between gap-2 text-left min-w-0"
              >
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-slate-200 truncate">{g.label}</p>
                  <p className="text-[10px] text-slate-500">{g.items.length} stage{g.items.length === 1 ? '' : 's'} · final Fe {typeof outcome.feOut === 'number' ? outcome.feOut.toFixed(3) : '—'} mg/L · SDI {typeof outcome.sdiOut === 'number' ? outcome.sdiOut.toFixed(1) : '—'}</p>
                </div>
                <span className="text-[10px] text-slate-600">{open ? '▼' : '▶'}</span>
              </button>
              {g.items[0]?.process_train_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/train-edit?id=${g.items[0].process_train_id}`); }}
                  className="shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-300 font-bold hover:bg-amber-500/20 transition-all"
                  title="Open in live train editor"
                >
                  ⚡ Edit
                </button>
              )}
            </div>
            {open && (
              <div className="space-y-1 pl-2 border-l border-slate-800 mt-1">
                {g.items.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0)).map((it, i) => (
                  <div key={i} className="text-[10px] text-slate-400 py-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-slate-600">{it.sequence_order}.</span>
                      <span className="font-bold text-slate-300">{it.equipment_name}</span>
                      {it.outcome_json?.meetsRoTarget && <span className="text-emerald-400">✓</span>}
                    </div>
                    {it.outcome_json && (
                      <div className="pl-5 text-[10px] text-slate-500">
                        Fe {typeof it.outcome_json.feOut === 'number' ? it.outcome_json.feOut.toFixed(3) : '—'} ·
                        SDI {typeof it.outcome_json.sdiOut === 'number' ? it.outcome_json.sdiOut.toFixed(1) : '—'} ·
                        Turb {typeof it.outcome_json.turbOut === 'number' ? it.outcome_json.turbOut.toFixed(2) : '—'} NTU
                      </div>
                    )}
                    {it.notes && (
                      <div className="pl-5 text-[10px] text-slate-600 italic mt-0.5">{it.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SiteCard({
  site, customer, onDelete, onUpdate, influentCount, effluentCount, lastInfluentDate, lastEffluentDate, effluentSamples,
}: {
  site: CustomerSite;
  customer: Customer;
  onDelete: () => void;
  onUpdate?: (updated: Partial<CustomerSite>) => void;
  influentCount?: number;
  effluentCount?: number;
  lastInfluentDate?: string;
  lastEffluentDate?: string;
  effluentSamples?: UnifiedSample[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [siteTab, setSiteTab] = useState<'details' | 'charges'>('details');
  // Local standard state — allows live update without re-fetching all sites
  const [localStandard, setLocalStandard] = useState<string>(site.standard || '');
  const [editingStandard, setEditingStandard] = useState(false);
  const [savingStandard, setSavingStandard] = useState(false);

  const isAu = site.country === 'AU' || customer.country === 'AU';
  const isGb = site.country === 'GB' || customer.country === 'GB';

  const stateColor = isAu && site.state
    ? { bg: 'bg-teal-500/10', border: 'border-teal-500/25', text: 'text-teal-400' }
    : isGb && site.region
    ? { bg: 'bg-blue-500/10', border: 'border-blue-500/25', text: 'text-blue-400' }
    : { bg: 'bg-slate-800/40', border: 'border-slate-800/40', text: 'text-slate-400' };

  const fmtShort = (d?: string) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }); }
    catch { return null; }
  };

  const dischargeLabel = DISCHARGE_ROUTES.find(r => r.value === site.dischargeRoute)?.label || site.dischargeRoute;
  const stdOverride = localStandard && localStandard !== (customer.standard || 'UK_EA');
  const jurisdiction = isAu
    ? (site.stateName || site.state || 'AU EPA')
    : isGb
    ? (site.region || 'Environment Agency')
    : site.region || site.country;

  const hasSamples = (influentCount || 0) + (effluentCount || 0) > 0;
  // Use localStandard so UI responds immediately when user saves a new standard
  const effectiveStandard = localStandard || customer.standard || 'UK_EA';
  const isSAWater = effectiveStandard === 'SA_WATER' && isAu;

  // Save standard change to localStorage + server
  const handleSaveStandard = async () => {
    setSavingStandard(true);
    try {
      updateLocalSite(site.id, { standard: localStandard || undefined });
      if (hasAuthToken() && site.serverId) {
        await apiUpdateCustomerSite({ id: site.serverId, standard: localStandard || null });
      }
      onUpdate?.({ standard: localStandard || undefined });
      setEditingStandard(false);
      // Auto-switch to Charges tab if SA Water was just selected
      if (localStandard === 'SA_WATER') setSiteTab('charges');
      toast.success('Site standard updated');
    } catch {
      toast.error('Failed to save — changes kept locally');
      setEditingStandard(false);
    } finally {
      setSavingStandard(false);
    }
  };

  return (
    <div
      onClick={() => setExpanded(e => !e)}
      title={expanded ? 'Click to collapse' : 'Click anywhere to expand site details'}
      className={`rounded-xl border overflow-hidden cursor-pointer select-none transition-all duration-200 ${
        isSAWater
          ? expanded
            ? 'bg-[#0c1520] border-orange-500/50 shadow-lg shadow-orange-900/20'
            : 'bg-rb-surface border-orange-500/20 hover:border-orange-400/40 hover:bg-[#0e1a26] hover:shadow-md hover:shadow-orange-900/10'
          : expanded
            ? 'bg-rb-surface border-teal-500/40 shadow-lg shadow-teal-900/20'
            : 'bg-rb-surface border-slate-800/50 hover:border-teal-500/30 hover:bg-[#0c1e35] hover:shadow-md hover:shadow-teal-900/10'
      }`}
    >
      {/* ── Compact header (always visible) ─────────────────────────── */}
      <div className={`flex items-center gap-3 px-4 py-3.5 ${isSAWater ? 'border-l-2 border-orange-500/50' : ''}`}>
        {/* Icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
          isSAWater
            ? expanded ? 'bg-orange-500/20 border border-orange-500/40' : 'bg-orange-500/10 border border-orange-500/25'
            : expanded ? 'bg-teal-500/20 border border-teal-500/40' : 'bg-teal-500/10 border border-teal-500/20'
        }`}>
          <MapPin className={`w-4.5 h-4.5 ${isSAWater ? (expanded ? 'text-orange-300' : 'text-orange-400') : (expanded ? 'text-teal-300' : 'text-teal-400')}`} />
        </div>

        {/* Name + address */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-slate-100 truncate">{site.name}</p>
          <p className="text-[10px] text-slate-500 truncate mt-0.5">
            {site.address}{site.postcode ? `, ${site.postcode}` : ''}
          </p>
        </div>

        {/* Inline summary chips */}
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          {isSAWater && (
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-orange-500/15 text-orange-400 border border-orange-500/25 hidden sm:inline-flex items-center gap-1">
              🇦🇺 SA Water
            </span>
          )}
          {site.permitRef && (
            <span className="text-[9px] px-2 py-0.5 rounded-full font-mono font-bold bg-teal-500/10 text-teal-400 border border-teal-500/20 hidden sm:inline-flex">
              {site.permitRef}
            </span>
          )}
          <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 flex items-center gap-1">
            <Droplets className="w-2.5 h-2.5" /> {influentCount ?? 0} inf
          </span>
          <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 flex items-center gap-1">
            <Beaker className="w-2.5 h-2.5" /> {effluentCount ?? 0} eff
          </span>
          {/* Expand chevron */}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* ── Expanded detail panel ─────────────────────────────────── */}
      {expanded && (
        <div
          onClick={e => e.stopPropagation()} // prevent collapse when clicking inside
          className="border-t border-slate-800/60 bg-[#071220] px-4 py-4 space-y-4"
        >
          {/* Tab switcher — only shown for SA Water sites */}
          {isSAWater && (
            <div className="flex rounded-lg overflow-hidden border border-slate-700 mb-1">
              {(['details', 'charges'] as const).map(t => (
                <button
                  key={t}
                  onClick={e => { e.stopPropagation(); setSiteTab(t); }}
                  className={`flex-1 py-2 text-[11px] font-bold transition-all capitalize ${
                    siteTab === t
                      ? t === 'charges' ? 'bg-blue-600/25 text-blue-300' : 'bg-teal-600/20 text-teal-300'
                      : 'bg-slate-800/40 text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {t === 'charges' ? '💲 SA Water Charges' : '📋 Site Details'}
                </button>
              ))}
            </div>
          )}

          {/* Charges tab */}
          {isSAWater && siteTab === 'charges' ? (
            <div>
              <SAWaterChargesPanel
                samples={effluentSamples || []}
                onViewFullPage={() => {}}
                site={site}
                customer={customer}
              />
              {(effluentSamples?.length ?? 0) === 0 && (
                <p className="text-[10px] text-slate-600 text-center mt-2">
                  No effluent samples linked to this site yet. When adding samples, select this site to link them.
                </p>
              )}
            </div>
          ) : (
            <>

          {/* Dark-themed location map */}
          <SiteMapPanel
            siteName={site.name}
            postcode={site.postcode}
            lat={site.latitude ?? null}
            lng={site.longitude ?? null}
            country={(site.country || customer.country || 'gb').toLowerCase()}
            height={320}
          />

          {/* DB-backed equipment assignments for this site */}
          <SiteEquipmentPanel site={site} />

          {/* Sample counts row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Droplets className="w-3.5 h-3.5 text-cyan-400" />
                <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wide">Influent Samples</p>
              </div>
              <p className="text-2xl font-black text-white">{influentCount ?? 0}</p>
              {lastInfluentDate && (
                <p className="text-[9px] text-slate-500 mt-0.5">Last: {fmtShort(lastInfluentDate)}</p>
              )}
              {!hasSamples && (
                <p className="text-[9px] text-slate-600 mt-0.5">No samples yet</p>
              )}
            </div>
            <div className="rounded-xl border border-rb-brand/20 bg-rb-brand/5 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Beaker className="w-3.5 h-3.5 text-rb-brand" />
                <p className="text-[10px] font-bold text-rb-brand uppercase tracking-wide">Effluent Samples</p>
              </div>
              <p className="text-2xl font-black text-white">{effluentCount ?? 0}</p>
              {lastEffluentDate && (
                <p className="text-[9px] text-slate-500 mt-0.5">Last: {fmtShort(lastEffluentDate)}</p>
              )}
              {!hasSamples && (
                <p className="text-[9px] text-slate-600 mt-0.5">No samples yet</p>
              )}
            </div>
          </div>

          {/* Regulatory jurisdiction strip */}
          {(site.state || site.region) && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${stateColor.bg} ${stateColor.border}`}>
              <Navigation className={`w-3.5 h-3.5 shrink-0 ${stateColor.text}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-[11px] font-bold ${stateColor.text}`}>{jurisdiction}</p>
                <p className="text-[9px] text-slate-500">
                  {isAu ? 'Australian EPA jurisdiction' : isGb ? 'Environment Agency region' : 'Regulatory jurisdiction'}
                </p>
              </div>
              {site.state && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-black ${stateColor.bg} ${stateColor.text} border ${stateColor.border}`}>
                  {site.state}
                </span>
              )}
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[10px]">
            {site.permitRef && (
              <div>
                <p className="text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1">
                  <FileText className="w-2.5 h-2.5" /> Permit / Consent Ref
                </p>
                <p className="text-teal-300 font-mono font-bold">{site.permitRef}</p>
              </div>
            )}
            {dischargeLabel && (
              <div>
                <p className="text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1">
                  <ArrowRight className="w-2.5 h-2.5" /> Discharge Route
                </p>
                <p className="text-slate-300">{dischargeLabel}</p>
              </div>
            )}
            {stdOverride && (
              <div>
                <p className="text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1">
                  <ShieldCheck className="w-2.5 h-2.5" /> Standard Override
                </p>
                <p className="text-amber-400 font-bold">{site.standard}</p>
              </div>
            )}
            {site.country && (
              <div>
                <p className="text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1">
                  <Globe className="w-2.5 h-2.5" /> Country
                </p>
                <p className="text-slate-300">{site.country}</p>
              </div>
            )}
          </div>

          {site.notes && (
            <div>
              <p className="text-slate-600 uppercase font-bold mb-1 text-[10px] flex items-center gap-1">
                <Info className="w-2.5 h-2.5" /> Notes
              </p>
              <p className="text-slate-400 text-[11px] leading-relaxed">{site.notes}</p>
            </div>
          )}

          {/* ── Discharge Standard editor — AU sites only ──────────── */}
          {isAu && (
            <div className={`rounded-xl border p-3 ${isSAWater ? 'border-blue-500/25 bg-blue-950/15' : 'border-slate-800/50 bg-slate-800/20'}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className={`w-3.5 h-3.5 ${isSAWater ? 'text-blue-400' : 'text-slate-500'}`} />
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Discharge Standard</span>
                </div>
                {!editingStandard && (
                  <button
                    onClick={e => { e.stopPropagation(); setEditingStandard(true); }}
                    className="flex items-center gap-1 text-[9px] font-bold text-slate-500 hover:text-slate-300 transition-colors px-2 py-1 rounded-lg hover:bg-slate-700/50"
                  >
                    <Pencil className="w-2.5 h-2.5" /> Edit
                  </button>
                )}
              </div>

              {editingStandard ? (
                <div className="space-y-2" onClick={e => e.stopPropagation()}>
                  <select
                    value={localStandard}
                    onChange={e => setLocalStandard(e.target.value)}
                    className="w-full px-3 py-2 text-slate-200 text-[12px] focus:outline-none focus:border-blue-500/50 transition-all rb-card-sm"
                  >
                    <option value="">— Inherit from customer ({customer.standard || 'UK_EA'}) —</option>
                    <option value="AU_EPA">🇦🇺 Australian EPA (General)</option>
                    <option value="SA_WATER">🇦🇺 SA Water — Trade Waste (South Australia)</option>
                  </select>
                  {localStandard === 'SA_WATER' && (
                    <p className="text-[10px] text-blue-400 flex items-center gap-1">
                      <Info className="w-2.5 h-2.5 shrink-0" /> Excess concentration charges will be calculated for effluent samples at this site
                    </p>
                  )}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSaveStandard}
                      disabled={savingStandard}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-600/25 text-blue-300 border border-blue-500/30 hover:bg-blue-600/35 disabled:opacity-50 transition-all"
                    >
                      {savingStandard ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      Save
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setLocalStandard(site.standard || ''); setEditingStandard(false); }}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-500 hover:text-slate-300 hover:bg-slate-700/50 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {isSAWater ? (
                    <>
                      <span className="text-[11px] font-bold text-blue-300">SA Water — Trade Waste</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20 font-bold">Active</span>
                    </>
                  ) : effectiveStandard === 'AU_EPA' ? (
                    <span className="text-[11px] text-slate-400">Australian EPA (General)</span>
                  ) : (
                    <span className="text-[11px] text-slate-500 italic">Inherited from customer</span>
                  )}
                </div>
              )}
            </div>
          )}

            </>
          )}

          {/* Footer row: date + delete */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-800/60">
            <p className="text-[9px] text-slate-600">
              Added {new Date(site.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              {site.serverId && <span className="ml-2 text-emerald-500">● DB synced</span>}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title="Delete site"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/20 transition-all"
            >
              <Trash2 className="w-3 h-3" /> Remove Site
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add Site Modal ────────────────────────────────────────────────────────────
function AddSiteModal({
  customer, onClose, onSaved,
}: {
  customer: Customer;
  onClose: () => void;
  onSaved: (site: CustomerSite) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    address: '',
    postcode: '',
    country: customer.country || 'GB',
    state: '',
    stateName: '',
    region: '',
    permitRef: '',
    dischargeRoute: customer.dischargeRoute || '',
    standard: '',
    notes: '',
  });

  const isAu = form.country === 'AU';
  const isGb = form.country === 'GB';

  // Auto-detect state/region from postcode
  const detectedRegion = useMemo(() => {
    if (!form.postcode) return null;
    if (isAu) {
      return postcodeToState(form.postcode);
    }
    if (isGb) {
      return postcodeToUkRegion(form.postcode);
    }
    return null;
  }, [form.postcode, isAu, isGb]);

  // Also check address text
  const textRegion = useMemo(() => {
    if (!form.address) return null;
    if (isAu) return guessStateFromText(form.address + ' ' + form.postcode);
    if (isGb) return guessUkRegion(form.address + ' ' + form.postcode);
    return null;
  }, [form.address, form.postcode, isAu, isGb]);

  const finalRegion = detectedRegion || textRegion;

  const autoState = isAu
    ? (finalRegion as any)?.code || ''
    : '';
  const autoStateName = isAu
    ? (finalRegion as any)?.name || ''
    : isGb
    ? (finalRegion as any)?.name || ''
    : '';
  const autoRegionName = isGb
    ? (finalRegion as any)?.agency || (finalRegion as any)?.name || ''
    : '';

  const upd = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Site name is required');
      return;
    }
    const siteData = {
      customerId: customer.id,
      name: form.name.trim(),
      address: form.address.trim(),
      postcode: form.postcode.trim(),
      country: form.country,
      state: form.state || autoState,
      stateName: form.stateName || autoStateName,
      region: form.region || autoRegionName || autoStateName,
      permitRef: form.permitRef.trim(),
      dischargeRoute: form.dischargeRoute,
      standard: form.standard || undefined,
      notes: form.notes.trim(),
    };
    const site = saveLocalSite(siteData);

    // Sync to API when authenticated
    if (hasAuthToken()) {
      const serverCid = (customer as any).serverId || parseInt(customer.id);
      if (serverCid > 0) {
        try {
          const res = await apiSaveCustomerSite({
            customerId: serverCid,
            name: siteData.name,
            address: siteData.address,
            postcode: siteData.postcode,
            country: siteData.country,
            state: siteData.state,
            stateName: siteData.stateName,
            region: siteData.region,
            permitRef: siteData.permitRef,
            dischargeRoute: siteData.dischargeRoute,
            standard: siteData.standard || null,
            notes: siteData.notes,
          });
          if (res?.id) {
            // Store serverId on the local record for future API operations
            updateLocalSite(site.id, { serverId: res.id, serverCustomerId: serverCid });
            site.serverId = res.id;
          }
        } catch {
          toast.warning('Site saved locally — could not sync to server. It will appear here and sync automatically when the connection is restored.');
        }
      }
    }

    onSaved(site);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-[#0d1b30] border border-slate-800/60 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60 bg-teal-500/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-500/15 border border-teal-500/25 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-teal-400" />
            </div>
            <div>
              <p className="text-sm font-black text-slate-100">Add Site Location</p>
              <p className="text-[10px] text-slate-500">{customer.company}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">

          {/* Site Name */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Site Name *</label>
            <input
              value={form.name} onChange={e => upd('name', e.target.value)}
              placeholder="e.g. Main Plant, Factory B, North Lagoon"
              className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40"
            />
          </div>

          {/* Address */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5" /> Address
            </label>
            <input
              value={form.address} onChange={e => upd('address', e.target.value)}
              placeholder="Street address, city"
              className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40"
            />
          </div>

          {/* Postcode + Country */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Postcode / ZIP</label>
              <input
                value={form.postcode} onChange={e => upd('postcode', e.target.value)}
                placeholder={isAu ? 'e.g. 2000' : isGb ? 'e.g. SW1A 2AA' : 'Postcode'}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Country</label>
              <select
                value={form.country} onChange={e => upd('country', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
              >
                {COUNTRY_GROUPS.flatMap(g => g.countries).map(code => (
                  <option key={code} value={code}>{COUNTRY_FLAGS[code] || '🌐'} {COUNTRY_NAMES[code] || code}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Auto-detected State / Region banner */}
          {finalRegion && (
            <div className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border ${isAu ? 'bg-teal-500/8 border-teal-500/25' : 'bg-blue-500/8 border-blue-500/25'}`}>
              <Navigation className={`w-3.5 h-3.5 shrink-0 ${isAu ? 'text-teal-400' : 'text-blue-400'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] font-bold ${isAu ? 'text-teal-400' : 'text-blue-400'}`}>
                  {isAu ? (finalRegion as any).name : (finalRegion as any).agency || (finalRegion as any).name} detected
                </p>
                <p className="text-[10px] text-slate-500">
                  {isAu ? 'State EPA jurisdiction auto-detected from postcode' : isGb ? 'EA / NRW / SEPA / NIEA region auto-detected from postcode' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Permit Reference */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Hash className="w-2.5 h-2.5" /> Discharge Permit / Licence No.
            </label>
            <input
              value={form.permitRef} onChange={e => upd('permitRef', e.target.value)}
              placeholder={isGb ? 'e.g. EPR/AB1234CD/V001' : isAu ? 'e.g. EPL 12345' : 'Permit reference'}
              className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40"
            />
            <p className="text-[10px] text-slate-600">
              {isGb ? 'UK: Environmental Permit (EPR) or Trade Effluent Consent number'
                : isAu ? 'AU: Environment Protection Licence (EPL) or Works Approval number'
                : 'Discharge consent, permit or licence reference'}
            </p>
          </div>

          {/* Discharge Standard — AU only */}
          {isAu && (
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck className="w-2.5 h-2.5" /> Discharge Standard
              </label>
              <select
                value={form.standard} onChange={e => upd('standard', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
              >
                <option value="">— Inherit from customer —</option>
                <option value="AU_EPA">🇦🇺 Australian EPA (General)</option>
                <option value="SA_WATER">🇦🇺 SA Water — Trade Waste (South Australia)</option>
              </select>
              {form.standard === 'SA_WATER' && (
                <p className="text-[10px] text-blue-400 flex items-center gap-1 mt-0.5">
                  <Info className="w-2.5 h-2.5 shrink-0" /> SA Water excess concentration charges will be calculated for effluent samples at this site
                </p>
              )}
            </div>
          )}

          {/* Discharge Route */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <ArrowRight className="w-2.5 h-2.5" /> Discharge Route
            </label>
            <select
              value={form.dischargeRoute} onChange={e => upd('dischargeRoute', e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
            >
              <option value="">— Select route —</option>
              {DISCHARGE_ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Notes</label>
            <textarea
              value={form.notes} onChange={e => upd('notes', e.target.value)}
              placeholder="Process description, monitoring schedule, consent conditions…"
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-slate-800/60">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!form.name.trim()}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 text-white text-xs font-black hover:from-teal-500 hover:to-teal-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <MapPin className="w-3.5 h-3.5" /> Save Site
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Customer List Card ───────────────────────────────────────────────────────
function CustomerListCard({
  customer, stats, isActive, isSelected, onClick,
}: {
  customer: Customer; stats: CustomerStats; isActive: boolean; isSelected: boolean; onClick: () => void;
}) {
  const totalSamples = stats.influent.length + stats.effluent.length;
  const assessedTrains = stats.trains.filter(t => t.status === 'assessed').length;
  const activeProposals = stats.proposals.filter(p => p.status === 'validated').length;
  const flag = customer.country ? (COUNTRY_FLAGS[customer.country] || '🌐') : null;
  const sc = STANDARD_COLORS[customer.standard || 'UK_EA'] || STANDARD_COLORS.UK_EA;

  // Latest influent sample — for param snapshot
  const latestInfluent = stats.influent[0] ?? null;
  const lastDate = latestInfluent
    ? new Date(latestInfluent.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
    : null;

  // Key params from latest influent
  const SNAP_KEYS = ['cod', 'bod', 'bod5', 'tss', 'ph', 'nh3n', 'nh3', 'oilgrease', 'og'];
  const snapParams: { key: string; label: string; val: number; color: string }[] = [];
  if (latestInfluent?.params) {
    for (const k of SNAP_KEYS) {
      const v = latestInfluent.params[k];
      if (v == null) continue;
      const label = k === 'ph' ? 'pH' : k === 'cod' ? 'COD' : k === 'bod5' ? 'BOD' : k === 'bod' ? 'BOD'
        : k === 'tss' ? 'TSS' : k === 'nh3n' || k === 'nh3' ? 'NH₃' : k === 'oilgrease' || k === 'og' ? 'O&G' : k.toUpperCase();
      snapParams.push({ key: k, label, val: v, color: paramColor(k, v) });
      if (snapParams.length >= 4) break;
    }
  }

  // Overall compliance signal from latest sample
  const hasExceedance = snapParams.some(p => {
    if (p.key === 'ph') return false; // skip pH for simple signal
    return (p.key === 'cod' && p.val > 2000) || (p.key === 'tss' && p.val > 350) || (p.key === 'bod' || p.key === 'bod5' ? p.val > 300 : false);
  });
  const complianceSignal = !latestInfluent ? null : hasExceedance ? 'fail' : 'ok';

  // Consent expiry check
  const consentExpiry = customer.consentExpiryDate ? new Date(customer.consentExpiryDate) : null;
  const today = new Date();
  const daysToExpiry = consentExpiry ? Math.ceil((consentExpiry.getTime() - today.getTime()) / 86400000) : null;
  const expiryStatus = daysToExpiry === null ? null
    : daysToExpiry < 0   ? 'expired'
    : daysToExpiry < 30  ? 'critical'
    : daysToExpiry < 90  ? 'warning'
    : null;

  const divider = isSelected ? 'divide-[#cda42e]/15 border-rb-brand/15' : 'divide-slate-800/60 border-slate-800/60';

  return (
    <motion.button
      whileHover={{ x: 1 }}
      onClick={onClick}
      className={`w-full text-left rounded-xl border transition-all ${
        isSelected && isActive
          ? 'border-cyan-400/70 bg-gradient-to-r from-cyan-500/10 to-rb-brand/5 shadow-[0_0_24px_rgba(34,211,238,0.20)]'
          : isSelected
          ? 'border-rb-brand/50 bg-gradient-to-r from-rb-brand/8 to-rb-brand/3 shadow-[0_0_20px_rgba(205,164,46,0.10)]'
          : isActive
          ? 'border-cyan-500/50 bg-[#0c1a2e] shadow-[0_0_16px_rgba(34,211,238,0.12)] ring-1 ring-inset ring-cyan-500/15'
          : expiryStatus === 'expired'
          ? 'border-red-500/40 bg-[#1a0d0d] hover:border-red-500/60'
          : expiryStatus === 'critical'
          ? 'border-red-500/25 bg-[#140d0d] hover:border-red-400/40'
          : expiryStatus === 'warning'
          ? 'border-amber-500/20 bg-[#0d1b30] hover:border-amber-500/40'
          : 'border-slate-800/80 bg-[#0d1b30] hover:border-slate-700/80 hover:bg-[#0f1e35]'
      }`}
    >
      {/* Expiry warning bar */}
      {expiryStatus && (
        <div className={`w-full px-3.5 py-1 flex items-center gap-1.5 text-[9px] font-bold border-b ${
          expiryStatus === 'expired'  ? 'bg-red-500/10 text-red-400 border-red-500/20' :
          expiryStatus === 'critical' ? 'bg-red-500/8  text-red-400 border-red-500/15' :
                                        'bg-amber-500/8 text-amber-400 border-amber-500/15'
        }`}>
          <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
          {expiryStatus === 'expired'
            ? `Consent expired ${Math.abs(daysToExpiry!)} days ago`
            : `Consent expires in ${daysToExpiry} day${daysToExpiry !== 1 ? 's' : ''}`}
        </div>
      )}

      {/* ── Header row ── */}
      <div className="flex items-start gap-3 px-3.5 pt-3 pb-2">
        {/* Avatar + active pulse */}
        <div className="relative shrink-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm transition-all ${
            isSelected ? 'bg-rb-brand/20 border border-rb-brand/40 text-rb-brand'
                       : 'bg-slate-800/80 border border-slate-800/60 text-slate-400'
          }`}>
            {customer.company.charAt(0).toUpperCase()}
          </div>
          {isActive && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-cyan-400 border-2 border-[#0d1b30] animate-pulse" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Company name + flag + badges */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className={`text-[13px] font-bold leading-tight truncate ${isSelected ? 'text-slate-100' : 'text-slate-200'}`}>
              {customer.company}
            </p>
            {flag && <span className="text-xs shrink-0 leading-none">{flag}</span>}
            {customer.isDemo && (
              <span className="px-1 py-0 rounded text-[10px] font-bold bg-violet-500/15 text-violet-400 border border-violet-500/20 shrink-0 leading-4">DEMO</span>
            )}
            {activeProposals > 0 && (
              <span className="px-1 py-0 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 shrink-0 leading-4">✓ PROP</span>
            )}
          </div>

          {/* Contact name · industry */}
          <p className="text-[10px] text-slate-500 truncate mt-0.5 leading-tight">
            {[customer.name, customer.industrySubsector || customer.industryType].filter(Boolean).join(' · ')}
          </p>

          {/* Regulatory body (abbreviated) + consent ref */}
          {(customer.regulatoryBody || customer.consentNumber) && (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {customer.regulatoryBody && (
                <span className="flex items-center gap-0.5 text-[9px] text-slate-600 font-bold truncate">
                  <Shield className="w-2 h-2 shrink-0" />
                  {customer.regulatoryBody.replace(' Environment Agency', ' EA').replace(' Environment Protection Agency', ' EPA').replace(' Environmental Protection Agency', ' EPA').replace(' Environment Protection Agency', ' EPA').replace('ACT Environment, Planning & Sustainable Development', 'ACT EPSD')}
                </span>
              )}
              {customer.consentNumber && (
                <span className="flex items-center gap-0.5 text-[9px] text-slate-600 font-mono truncate">
                  <Hash className="w-2 h-2 shrink-0" />
                  {customer.consentNumber}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right side: standard + compliance signal */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          {customer.standard && (
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black border ${sc.bg} ${sc.border} ${sc.color}`}>
              <ShieldCheck className="w-2 h-2" />
              {customer.standard.replace('_', ' ')}
            </span>
          )}
          {complianceSignal && (
            <span className={`text-[10px] font-black px-1 py-0.5 rounded border leading-none ${
              complianceSignal === 'ok'
                ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                : 'text-red-400 bg-red-500/10 border-red-500/20'
            }`}>
              {complianceSignal === 'ok' ? '✓ PASS' : '⚠ REVIEW'}
            </span>
          )}
        </div>
      </div>

      {/* ── Data counts strip ── */}
      <div className={`grid grid-cols-4 divide-x border-t ${divider}`}>
        {[
          { icon: Droplets, count: stats.influent.length,  label: 'Inf',   color: isSelected ? 'text-cyan-400'   : 'text-slate-500' },
          { icon: Beaker,   count: stats.effluent.length,  label: 'Eff',   color: isSelected ? 'text-rb-brand'  : 'text-slate-500' },
          { icon: Workflow, count: stats.trains.length,    label: 'Trains',color: isSelected ? 'text-amber-400'  : 'text-slate-500' },
          { icon: FileText, count: stats.proposals.length, label: 'Props', color: isSelected ? 'text-violet-400' : 'text-slate-500' },
        ].map(({ icon: Icon, count, label, color }) => (
          <div key={label} className="flex flex-col items-center justify-center py-1.5 gap-0">
            <span className={`text-[12px] font-black tabular-nums leading-tight ${count > 0 ? color : 'text-slate-400'}`}>{count}</span>
            <span className={`text-[10px] uppercase tracking-wide font-bold ${count > 0 ? color : 'text-slate-400'} opacity-70`}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── Latest sample param snapshot ── */}
      {snapParams.length > 0 && (
        <div className={`flex items-center gap-1.5 px-3.5 py-1.5 border-t flex-wrap ${divider}`}>
          {lastDate && (
            <span className="text-[10px] text-slate-600 flex items-center gap-0.5 mr-auto">
              <Clock className="w-2 h-2" />{lastDate}
            </span>
          )}
          {snapParams.map(p => (
            <span key={p.key} className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded border"
              style={{ color: p.color, borderColor: p.color + '40', backgroundColor: p.color + '15' }}>
              {p.label} {p.val % 1 === 0 ? p.val : p.val.toFixed(1)}
            </span>
          ))}
        </div>
      )}

      {/* ── Footer: address + assessed count ── */}
      {(customer.address || assessedTrains > 0 || customer.phone) && (
        <div className={`flex items-center gap-2 px-3.5 py-1.5 border-t text-[10px] ${
          isSelected ? 'border-rb-brand/10' : 'border-slate-800/40'
        }`}>
          {customer.address && (
            <span className="flex items-center gap-1 text-slate-600 truncate flex-1">
              <MapPin className="w-2.5 h-2.5 shrink-0" />{customer.address}
            </span>
          )}
          {!customer.address && customer.phone && (
            <span className="flex items-center gap-1 text-slate-600 truncate flex-1">
              <Phone className="w-2.5 h-2.5 shrink-0" />{customer.phone}
            </span>
          )}
          {assessedTrains > 0 && (
            <span className="flex items-center gap-1 text-emerald-400 font-bold shrink-0 ml-auto">
              <CheckCircle2 className="w-2.5 h-2.5" />{assessedTrains} assessed
            </span>
          )}
        </div>
      )}
    </motion.button>
  );
}

// ─── Parameter labels / units (extended) ──────────────────────────────────────
const ALL_PARAM_LABELS: Record<string, { label: string; unit: string }> = {
  ph:             { label: 'pH',             unit: '' },
  cod:            { label: 'COD',            unit: 'mg/L' },
  bod:            { label: 'BOD',            unit: 'mg/L' },
  bod5:           { label: 'BOD₅',           unit: 'mg/L' },
  tss:            { label: 'TSS',            unit: 'mg/L' },
  nh3:            { label: 'NH₃-N',          unit: 'mg/L' },
  nh3n:           { label: 'NH₃-N',          unit: 'mg/L' },
  ammoniaN:       { label: 'Ammonia-N',      unit: 'mg/L' },
  og:             { label: 'Oil & Grease',   unit: 'mg/L' },
  oilgrease:      { label: 'Oil & Grease',   unit: 'mg/L' },
  tds:            { label: 'TDS',            unit: 'mg/L' },
  temperature:    { label: 'Temperature',    unit: '°C' },
  conductivity:   { label: 'Conductivity',   unit: 'µS/cm' },
  flowrate:       { label: 'Flow Rate',      unit: 'm³/day' },
  flowRate:       { label: 'Flow Rate',      unit: 'm³/day' },
  lead:           { label: 'Lead (Pb)',       unit: 'mg/L' },
  chromium:       { label: 'Chromium (Cr)',   unit: 'mg/L' },
  cadmium:        { label: 'Cadmium (Cd)',    unit: 'mg/L' },
  nickel:         { label: 'Nickel (Ni)',     unit: 'mg/L' },
  zinc:           { label: 'Zinc (Zn)',       unit: 'mg/L' },
  copper:         { label: 'Copper (Cu)',     unit: 'mg/L' },
  totalPhosphorus:{ label: 'Total P',         unit: 'mg/L' },
  tp:             { label: 'Total P',         unit: 'mg/L' },
  totalNitrogen:  { label: 'Total N',         unit: 'mg/L' },
  tkn:            { label: 'TKN',             unit: 'mg/L' },
  turbidity:      { label: 'Turbidity',       unit: 'NTU' },
  phenol:         { label: 'Phenol',          unit: 'mg/L' },
  sulfides:       { label: 'Sulfides',        unit: 'mg/L' },
  surfactants:    { label: 'Surfactants',     unit: 'mg/L' },
  iron:           { label: 'Iron (Fe)',        unit: 'mg/L' },
  manganese:      { label: 'Manganese (Mn)',  unit: 'mg/L' },
  sulphate:       { label: 'Sulphate',        unit: 'mg/L' },
};

// Maps param key → standard.limits key (null = not in standard)
const PARAM_TO_STD: Record<string, keyof import('@/types/standards').WastewaterStandard['limits'] | null> = {
  ph:             null, // special: check phMin/phMax
  cod:            'cod',
  bod:            'bod5',
  bod5:           'bod5',
  tss:            'tss',
  nh3:            'ammoniaN',
  nh3n:           'ammoniaN',
  ammoniaN:       'ammoniaN',
  og:             'oilGrease',
  oilgrease:      'oilGrease',
  tds:            'tds',
  temperature:    'temperature',
  conductivity:   'conductivity',
  lead:           'lead',
  chromium:       'chromium',
  cadmium:        'cadmium',
  nickel:         'nickel',
  zinc:           'zinc',
  copper:         'copper',
  totalPhosphorus:'totalPhosphorus',
  tp:             'totalPhosphorus',
  totalNitrogen:  'totalNitrogen',
  tkn:            'tkn',
  phenol:         'phenol',
  sulfides:       'sulfides',
  sulfide:        'sulfides',
  surfactants:    'surfactants',
  mercury:        'mercury',
  hg:             'mercury',
};

function checkCompliance(
  key: string, value: number,
  stdCode: StandardCode
): { limit: number | null; limitHi?: number; pass: boolean; pct: number; label: string } {
  const std = ALL_STANDARDS.find(s => s.code === stdCode);
  const L = std?.limits;
  if (!L) return { limit: null, pass: true, pct: 0, label: '—' };

  if (key === 'ph') {
    const pass = value >= L.phMin && value <= L.phMax;
    return { limit: L.phMin, limitHi: L.phMax, pass, pct: 0, label: `${L.phMin}–${L.phMax}` };
  }
  const stdKey = PARAM_TO_STD[key.toLowerCase()];
  if (!stdKey) return { limit: null, pass: true, pct: 0, label: '—' };
  const limit = L[stdKey] as number;
  if (!limit) return { limit: null, pass: true, pct: 0, label: '—' };
  const pass = value <= limit;
  const pct = Math.round((value / limit) * 100);
  return { limit, pass, pct, label: `≤ ${limit}` };
}

// ─── Sample Detail Expanded View ───────────────────────────────────────────────
function SampleDetailView({
  sample, stdCode, customer,
}: {
  sample: UnifiedSample; stdCode: StandardCode; customer: Customer;
}) {
  const params = sample.params || {};
  const paramKeys = Object.keys(params).filter(k => params[k] !== undefined && params[k] !== null);

  // Sort: failing first, then by value desc
  const rows = paramKeys
    .map(k => {
      const v = params[k];
      const compliance = checkCompliance(k, v, stdCode);
      const meta = ALL_PARAM_LABELS[k] || { label: k.toUpperCase(), unit: 'mg/L' };
      return { key: k, value: v, meta, compliance };
    })
    .sort((a, b) => {
      if (!a.compliance.pass && b.compliance.pass) return -1;
      if (a.compliance.pass && !b.compliance.pass) return 1;
      // Then sort by pct descending (closest to / exceeding limit first)
      return (b.compliance.pct || 0) - (a.compliance.pct || 0);
    });

  const checked  = rows.filter(r => r.compliance.limit !== null);
  const failing  = checked.filter(r => !r.compliance.pass);
  const passing  = checked.filter(r => r.compliance.pass);
  const pct      = checked.length > 0 ? Math.round(passing.length / checked.length * 100) : 100;

  const handleExportPDF = () => {
    generateTrendReportPDF({
      id: String(sample.id),
      reportNumber: sample.reportNumber || `RB-${sample.id}`,
      sampleName: sample.sampleName,
      customerName: customer.name,
      customerCompany: customer.company,
      standard: customer.standard || 'UK_EA',
      date: new Date(sample.createdAt),
      preparerName: 'Reynolds & Bauhm',
      preparerCompany: 'Reynolds & Bauhm Ltd',
      params,
    });
  };

  return (
    <div className="mt-2 rounded-xl border border-slate-800/60 bg-[#06101f] overflow-hidden">
      {/* Compliance bar */}
      <div className={`px-3 py-2 flex items-center justify-between gap-3 border-b ${
        failing.length === 0 ? 'border-emerald-500/20 bg-emerald-500/5'
        : failing.length <= 2 ? 'border-amber-500/20 bg-amber-500/5'
        : 'border-red-500/20 bg-red-500/5'
      }`}>
        <div className="flex items-center gap-2">
          {failing.length === 0
            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            : <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
          <span className={`text-[10px] font-bold ${failing.length === 0 ? 'text-emerald-400' : failing.length <= 2 ? 'text-amber-400' : 'text-red-400'}`}>
            {failing.length === 0 ? 'All limits met' : `${failing.length} parameter${failing.length > 1 ? 's' : ''} exceed limit`}
          </span>
          <span className="text-[10px] text-slate-500">
            ({pct}% compliant · {passing.length}/{checked.length} checked)
          </span>
        </div>
        <button
          onClick={handleExportPDF}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all"
        >
          <Download className="w-3 h-3" /> Export PDF
        </button>
      </div>

      {/* Parameter table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left px-3 py-1.5 text-slate-600 font-bold uppercase text-[10px]">Parameter</th>
              <th className="text-right px-3 py-1.5 text-slate-600 font-bold uppercase text-[10px]">Value</th>
              <th className="text-left  px-3 py-1.5 text-slate-600 font-bold uppercase text-[10px]">Unit</th>
              <th className="text-right px-3 py-1.5 text-slate-600 font-bold uppercase text-[10px]">Limit</th>
              <th className="text-center px-3 py-1.5 text-slate-600 font-bold uppercase text-[10px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, value, meta, compliance }) => {
              const { pass, limit, label: lbl } = compliance;
              const hasLimit = limit !== null;
              const rowBg = hasLimit && !pass ? 'bg-red-500/5' : '';
              const valColor = !hasLimit
                ? 'text-slate-300'
                : pass
                  ? compliance.pct >= 75 ? 'text-amber-400' : 'text-emerald-400'
                  : 'text-red-400 font-black';
              return (
                <tr key={key} className={`border-b border-slate-800/40 last:border-0 ${rowBg}`}>
                  <td className="px-3 py-1.5 text-slate-400 font-medium">{meta.label}</td>
                  <td className={`px-3 py-1.5 text-right font-mono font-bold ${valColor}`}>
                    {value % 1 === 0 ? value : value.toFixed(2)}
                  </td>
                  <td className="px-3 py-1.5 text-slate-600">{meta.unit}</td>
                  <td className="px-3 py-1.5 text-right text-slate-500 font-mono">
                    {hasLimit ? lbl : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {!hasLimit
                      ? <span className="text-[10px] text-slate-600">No limit</span>
                      : pass
                        ? <CheckCircle2 className={`w-3 h-3 mx-auto ${compliance.pct >= 75 ? 'text-amber-400' : 'text-emerald-400'}`} />
                        : (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-black text-red-400">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            {compliance.pct > 0 ? `${compliance.pct}%` : 'FAIL'}
                          </span>
                        )
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Linked sample note */}
      {sample.linkedSampleId && (
        <div className="px-3 py-2 border-t border-slate-800/60 flex items-center gap-1.5 text-[10px] text-cyan-400">
          <Link2 className="w-3 h-3" />
          Linked to sample #{sample.linkedSampleId}
        </div>
      )}
    </div>
  );
}

// ─── Train Detail Expanded View ────────────────────────────────────────────────
const CATEGORY_COLOR: Record<string, string> = {
  'pre-treatment':    'text-sky-400 bg-sky-500/10 border-sky-500/20',
  'primary':          'text-blue-400 bg-blue-500/10 border-blue-500/20',
  'secondary':        'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  'tertiary':         'text-violet-400 bg-violet-500/10 border-violet-500/20',
  'nutrient':         'text-teal-400 bg-teal-500/10 border-teal-500/20',
  'disinfection':     'text-amber-400 bg-amber-500/10 border-amber-500/20',
  'sludge':           'text-orange-400 bg-orange-500/10 border-orange-500/20',
  'polishing':        'text-purple-400 bg-purple-500/10 border-purple-500/20',
  'default':          'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

function TrainDetailView({ train }: { train: UnifiedTrain }) {
  const eqList: { key: string; label: string; category: string; shortLabel: string }[] = train.equipmentList || [];
  const result: any = (train as any).assessmentResult;

  // Group equipment by category
  const byCat: Record<string, typeof eqList> = {};
  eqList.forEach(eq => {
    const cat = (eq.category || 'default').toLowerCase();
    if (!byCat[cat]) byCat[cat] = [];
    byCat[cat].push(eq);
  });

  return (
    <div className="mt-2 rounded-xl border border-slate-800/60 bg-[#06101f] overflow-hidden">
      {/* Equipment pipeline */}
      <div className="px-3 py-2.5 border-b border-slate-800/60">
        <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-2">Equipment Pipeline — {eqList.length} unit{eqList.length !== 1 ? 's' : ''}</p>
        {eqList.length === 0 ? (
          <p className="text-[10px] text-slate-600 italic">No equipment defined</p>
        ) : (
          <div className="flex flex-wrap items-center gap-1">
            {eqList.map((eq, i) => {
              const cat = (eq.category || 'default').toLowerCase();
              const cls = CATEGORY_COLOR[cat] || CATEGORY_COLOR.default;
              return (
                <span key={i} className="flex items-center gap-0.5">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${cls}`}>
                    {eq.shortLabel || eq.label || eq.key}
                  </span>
                  {i < eqList.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-slate-400 shrink-0" />}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Category breakdown */}
      {Object.keys(byCat).length > 1 && (
        <div className="px-3 py-2 border-b border-slate-800/60">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1.5">By Stage</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byCat).map(([cat, items]) => {
              const cls = CATEGORY_COLOR[cat] || CATEGORY_COLOR.default;
              return (
                <div key={cat} className={`flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] ${cls}`}>
                  <span className="font-bold capitalize">{cat}</span>
                  <span className="opacity-60">·</span>
                  <span>{items.map(e => e.shortLabel || e.key).join(', ')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Assessment result */}
      {result && (
        <div className="px-3 py-2 border-b border-slate-800/60">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1.5">Assessment Result</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {result.removalEfficiency !== undefined && (
              <div className="text-center p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Removal Eff.</p>
                <p className="text-sm font-black text-emerald-400">{result.removalEfficiency}%</p>
              </div>
            )}
            {result.codRemoval !== undefined && (
              <div className="text-center p-2 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <p className="text-[10px] text-slate-500 uppercase font-bold">COD Removal</p>
                <p className="text-sm font-black text-cyan-400">{result.codRemoval}%</p>
              </div>
            )}
            {result.bodRemoval !== undefined && (
              <div className="text-center p-2 rounded-lg bg-sky-500/5 border border-sky-500/20">
                <p className="text-[10px] text-slate-500 uppercase font-bold">BOD Removal</p>
                <p className="text-sm font-black text-sky-400">{result.bodRemoval}%</p>
              </div>
            )}
            {result.tssRemoval !== undefined && (
              <div className="text-center p-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <p className="text-[10px] text-slate-500 uppercase font-bold">TSS Removal</p>
                <p className="text-sm font-black text-amber-400">{result.tssRemoval}%</p>
              </div>
            )}
            {result.effluent_cod !== undefined && (
              <div className="text-center p-2 rounded-lg bg-slate-800 border border-slate-700">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Est. Effluent COD</p>
                <p className="text-sm font-black text-slate-300">{result.effluent_cod} mg/L</p>
              </div>
            )}
            {result.finalCOD !== undefined && (
              <div className="text-center p-2 rounded-lg bg-slate-800 border border-slate-700">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Final COD</p>
                <p className="text-sm font-black text-slate-300">{result.finalCOD} mg/L</p>
              </div>
            )}
          </div>
          {result.notes && (
            <p className="text-[10px] text-slate-500 mt-2 italic">{result.notes}</p>
          )}
        </div>
      )}

      {/* Train description if any */}
      {(train as any).description && (
        <div className="px-3 py-2">
          <p className="text-[10px] text-slate-500 leading-relaxed">{(train as any).description}</p>
        </div>
      )}
    </div>
  );
}

// ─── SA Water Charges Drawer — full ergonomic calculator modal ───────────────

interface SavedSAWEstimate {
  id?: number;
  reference_number: string;
  sample_count: number;
  annual_kl: number;
  flow_m3h: number | null;
  operating_days: number | null;
  total_charge: number;
  sample_label: string;
  created_at?: string;
  estimate_data?: any;
  site_id?: number | null;
}

function SAWaterChargesDrawer({
  customer,
  sites,
  allSamples,
  onClose,
}: {
  customer: Customer;
  sites: CustomerSite[];
  allSamples: UnifiedSample[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const sawSites = sites.filter(s => {
    const eff = s.standard || customer.standard || 'UK_EA';
    return eff === 'SA_WATER';
  });

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(sawSites.length === 1 ? sawSites[0].id : null);
  const [flowM3h, setFlowM3h] = useState<number>(3);
  const [operatingDays, setOperatingDays] = useState<number>(250);
  const [sampleCount, setSampleCount] = useState<number | 'all'>('all');
  const [savedEstimates, setSavedEstimates] = useState<SavedSAWEstimate[]>([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentRef] = useState<string>(() => {
    const yr = new Date().getFullYear();
    const a = Math.floor(1000 + Math.random() * 9000);
    const b = Math.floor(1000 + Math.random() * 9000);
    return `SAW-${yr}-${a}-${b}`;
  });

  const selectedSite = sawSites.find(s => s.id === selectedSiteId) ?? null;

  // ── Load saved estimates when site changes ──────────────────────────────────
  useEffect(() => {
    if (!selectedSite) { setSavedEstimates([]); return; }
    setLoadingEstimates(true);
    const siteServerId = (selectedSite as any).serverId ?? null;
    const custServerId = (customer as any).serverId ?? parseInt(customer.id);

    if (hasAuth() && custServerId > 0) {
      apiListSAWaterEstimates(custServerId, siteServerId)
        .then(data => setSavedEstimates(Array.isArray(data) ? data : []))
        .catch(() => setSavedEstimates([]))
        .finally(() => setLoadingEstimates(false));
    } else {
      // Fallback: localStorage
      try {
        const all = JSON.parse(localStorage.getItem('rb_saw_estimates') || '[]');
        setSavedEstimates(all.filter((e: any) => e.site_id === selectedSite.id || !e.site_id));
      } catch { setSavedEstimates([]); }
      setLoadingEstimates(false);
    }
  }, [selectedSite?.id]);

  // ── Site samples — effluent only, from the selected SA Water site exclusively ──
  // SA Water charges are calculated on effluent data for this specific discharge point.
  // No cross-site fallback: if no samples are linked to this site, the user must link them.
  const siteSamples = useMemo(() => {
    if (!selectedSite) return [];
    const siteServerId = String((selectedSite as any).serverId ?? '');
    const siteLocalId = selectedSite.id;
    // Match by server ID or local ID — exclude any sample explicitly typed as influent
    return allSamples.filter(s => {
      const sid = String(s.siteId ?? '');
      const matchesSite = sid === siteLocalId || (siteServerId && sid === siteServerId);
      const isEffluent = s.sampleType !== 'influent'; // exclude only explicit influent; keep effluent + untyped
      return matchesSite && isEffluent;
    });
  }, [selectedSite, allSamples]);

  const activeSampleCount = sampleCount === 'all' ? siteSamples.length : Math.min(sampleCount, siteSamples.length);
  const activeSamples = siteSamples.slice(0, activeSampleCount);

  // ── Volume from flow rate ───────────────────────────────────────────────────
  const annualKL = flowM3h * 24 * operatingDays;  // m³/h × 24h × days = m³/yr = kL/yr

  // ── Parameter averages ──────────────────────────────────────────────────────
  const avgs = useMemo(() => {
    const result: Record<string, number | null> = {};
    for (const r of SA_WATER_RATES_DATA) {
      const vals = activeSamples
        .map(s => {
          const p: Record<string, unknown> = typeof s.params === 'string'
            ? (() => { try { return JSON.parse(s.params as string); } catch { return {}; } })()
            : ((s.params as Record<string, unknown>) || {});
          const aliases: Record<string, string[]> = {
            bod5: ['bod5','bod','BOD5','BOD'], tss: ['tss','TSS','ss','SS'],
            tds: ['tds','TDS'], tn: ['tn','TN','tkn','TKN','totalNitrogen'],
            totalPhosphorus: ['totalPhosphorus','tp','TP','phosphorus'],
          };
          for (const k of (aliases[r.key] ?? [r.key])) {
            const v = p[k];
            if (typeof v === 'number' && v >= 0) return v;
          }
          return null;
        })
        .filter((v): v is number => v !== null);
      result[r.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }
    return result;
  }, [activeSamples]);

  const volumeTriggered = annualKL >= 10000;
  const rows = SA_WATER_RATES_DATA.map(r => {
    const avg = avgs[r.key] ?? null;
    const loadKg = avg !== null && annualKL > 0 ? avg * annualKL * 0.001 : null;
    const highRateApplied = r.threshold !== null && avg !== null && avg > r.threshold;
    const rate = highRateApplied ? r.highRate : r.lowRate;
    const triggered = volumeTriggered || (r.triggerKg !== null && loadKg !== null && loadKg > r.triggerKg);
    const exceeds = r.tknCondition ? (avg !== null && avg > 650) : true;
    const charge = triggered && exceeds && loadKg !== null ? loadKg * rate : null;
    return { ...r, avg, loadKg, rate, charge, triggered, exceeds, highRateApplied };
  });

  const totalCharge = rows.reduce((sum, r) => sum + (r.charge ?? 0), 0);
  const anyCharged = rows.some(r => r.charge !== null && r.charge > 0);
  const chargedRows = rows.filter(r => r.charge !== null && r.charge! > 0);

  const fmtNum = (n: number, dp = 1) => n.toFixed(dp).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fmtAUD = (n: number) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sampleLabel = sampleCount === 'all'
    ? `Combined — ${activeSamples.length} samples`
    : `${activeSampleCount} most recent sample${activeSampleCount !== 1 ? 's' : ''}`;

  // ── Save estimate ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!annualKL || activeSamples.length === 0) { toast.error('Enter flow rate and ensure samples are loaded'); return; }
    setSaving(true);
    const custServerId = (customer as any).serverId ?? parseInt(customer.id);
    const siteServerId = (selectedSite as any)?.serverId ?? null;
    const estimateData = {
      rows: rows.map(r => ({ label: r.label, avg: r.avg, loadKg: r.loadKg, rate: r.rate, charge: r.charge })),
      flowM3h: flowM3h,
      operatingDays,
      sampleCount: activeSamples.length,
    };
    try {
      if (hasAuth() && custServerId > 0) {
        const result = await apiSaveSAWaterEstimate({
          customer_id: custServerId,
          site_id: siteServerId,
          reference_number: currentRef,
          sample_count: activeSamples.length,
          annual_kl: annualKL,
          flow_m3h: flowM3h,
          operating_days: operatingDays,
          total_charge: totalCharge,
          sample_label: sampleLabel,
          estimate_data: estimateData,
        });
        toast.success(`Estimate ${currentRef} saved to database (ID: ${result.id})`);
      } else {
        const local: SavedSAWEstimate = {
          reference_number: currentRef,
          sample_count: activeSamples.length,
          annual_kl: annualKL,
          flow_m3h: flowM3h,
          operating_days: operatingDays,
          total_charge: totalCharge,
          sample_label: sampleLabel,
          created_at: new Date().toISOString(),
          estimate_data: estimateData,
          site_id: selectedSite?.id ? parseInt(selectedSite.id) : null,
        };
        const stored = JSON.parse(localStorage.getItem('rb_saw_estimates') || '[]');
        stored.unshift(local);
        localStorage.setItem('rb_saw_estimates', JSON.stringify(stored.slice(0, 50)));
        setSavedEstimates(prev => [local, ...prev]);
        toast.success(`Estimate ${currentRef} saved locally`);
      }
    } catch (err: any) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Generate PDF ────────────────────────────────────────────────────────────
  const handleGeneratePdf = async (mode: 'preview' | 'download') => {
    setGeneratingPdf(true);
    try {
      const pdfRows: SAWaterChargeRow[] = rows.map(r => ({
        label: r.label, fullName: r.fullName, avg: r.avg, loadKg: r.loadKg,
        rate: r.rate, charge: r.charge, triggered: r.triggered, exceeds: r.exceeds,
        highRateApplied: r.highRateApplied,
      }));
      const result = generateSAWaterChargesReport({
        customerCompany: customer.company || customer.name || '',
        siteName: selectedSite?.name || selectedSite?.address || 'Site',
        siteAddress: [selectedSite?.address, selectedSite?.city].filter(Boolean).join(', ') || '',
        sitePostcode: selectedSite?.postcode,
        sampleLabel,
        annualKL,
        flowRateM3day: flowM3h * 24,  // convert m³/h to m³/day for report
        operatingDays,
        volumeMethod: 'flowrate',
        rows: pdfRows,
        totalCharge,
        referenceNumber: currentRef,
        mode,
      });
      if (mode === 'preview' && result) {
        setPdfUrl(result);
        setShowPdfModal(true);
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  // ── Load saved estimate ─────────────────────────────────────────────────────
  const handleLoadEstimate = (est: SavedSAWEstimate) => {
    if (est.flow_m3h != null) setFlowM3h(est.flow_m3h);
    if (est.operating_days != null) setOperatingDays(est.operating_days);
    const cnt = est.sample_count;
    setSampleCount(cnt >= siteSamples.length ? 'all' : cnt);
    toast.success(`Loaded estimate ${est.reference_number}`);
  };

  return (
    <>
    <div className="bg-[#080e1a] border-b-2 border-teal-500/20 overflow-hidden">
      <div className="overflow-y-auto" style={{ maxHeight: '58vh' }}>

        {/* ── Panel Header ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800/60 bg-gradient-to-r from-teal-900/20 to-transparent sticky top-0 z-10 bg-[#080e1a]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-500/15 border border-teal-500/25 flex items-center justify-center shrink-0">
              <Calculator className="w-4.5 h-4.5 text-teal-400" />
            </div>
            <div>
              <p className="text-sm font-black text-slate-100">SA Water Trade Waste Calculator</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{customer.company} — Excess Concentration Charges</p>
            </div>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-400 border border-teal-500/20 font-bold uppercase tracking-wider">AUD excl. GST</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onClose(); navigate('/sa-water-charges'); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/15 border border-teal-500/30 text-teal-300 text-[10px] font-bold hover:bg-teal-500/25 transition-all"
              title="Open full SA Water Charges calculator"
            >
              <ExternalLink className="w-3 h-3" />
              Full Page
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center transition-all">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="p-5 space-y-5">

            {/* ── Site Selection ── */}
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                SA Water Sites <span className="text-teal-400">({sawSites.length})</span>
              </p>
              {sawSites.length === 0 ? (
                <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700 text-center">
                  <p className="text-[11px] text-slate-500">No SA Water sites found for this customer.</p>
                  <p className="text-[10px] text-slate-600 mt-1">Apply SA Water standard to a site in the Sites tab.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {sawSites.map(site => (
                    <button
                      key={site.id}
                      onClick={() => setSelectedSiteId(prev => prev === site.id ? null : site.id)}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                        selectedSiteId === site.id
                          ? 'bg-teal-500/10 border-teal-500/40 ring-1 ring-teal-500/20'
                          : 'bg-slate-800/40 border-slate-700 hover:border-teal-500/25 hover:bg-teal-500/5'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-[11px] font-black ${selectedSiteId === site.id ? 'text-teal-300' : 'text-slate-200'}`}>
                            {site.name || site.address || `Site ${site.id}`}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            {[site.address, site.state || site.stateName].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-400 border border-teal-500/20 font-bold">SA WATER</span>
                          {selectedSiteId === site.id && <ChevronDown className="w-3.5 h-3.5 text-teal-400" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Calculator (expanded when site selected) ── */}
            {selectedSite && (
              <div className="space-y-4 rounded-2xl border border-teal-500/20 bg-teal-500/3 overflow-hidden">

                {/* Site header */}
                <div className="flex items-center gap-3 px-5 py-3 bg-teal-900/20 border-b border-teal-500/15">
                  <MapPin className="w-3.5 h-3.5 text-teal-400 shrink-0" />
                  <div>
                    <p className="text-[11px] font-black text-teal-300">{selectedSite.name || selectedSite.address}</p>
                    {selectedSite.address && selectedSite.name && (
                      <p className="text-[9px] text-slate-500 mt-0.5">{selectedSite.address}{selectedSite.postcode ? ` ${selectedSite.postcode}` : ''}</p>
                    )}
                  </div>
                  <span className="ml-auto text-[9px] text-teal-500 font-bold">{siteSamples.length} effluent sample{siteSamples.length !== 1 ? 's' : ''}</span>
                </div>

                <div className="px-5 pb-5 space-y-4">

                  {/* Charge trigger info */}
                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/8 border border-blue-500/15">
                    <Info className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-blue-300 leading-relaxed">
                      Charges trigger when annual discharge exceeds <strong>10 t BOD or SS</strong>, <strong>20 t TDS</strong>, or <strong>10,000 kL</strong> volume.
                    </p>
                  </div>

                  {/* ── Flow Rate + Sample selector row ── */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Flow Rate — stepper, no slider */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Flow Rate <span className="text-teal-400 font-black">m³/h</span>
                      </label>
                      <div className="flex items-center gap-0">
                        <button
                          onClick={() => setFlowM3h(v => Math.max(0.5, parseFloat((v - 0.5).toFixed(1))))}
                          className="w-9 h-9 rounded-l-xl bg-teal-500/20 border border-teal-500/30 text-teal-300 text-lg font-black hover:bg-teal-500/30 transition-all flex items-center justify-center select-none"
                        >−</button>
                        <input
                          type="number" value={flowM3h}
                          onChange={e => setFlowM3h(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                          min="0.1" step="0.5"
                          className="flex-1 py-2 bg-teal-500/10 border-t border-b border-teal-500/30 text-teal-200 text-[15px] font-black font-mono text-center focus:outline-none focus:bg-teal-500/15 tabular-nums transition-all"
                        />
                        <button
                          onClick={() => setFlowM3h(v => parseFloat((v + 0.5).toFixed(1)))}
                          className="w-9 h-9 rounded-r-xl bg-teal-500/20 border border-teal-500/30 text-teal-300 text-lg font-black hover:bg-teal-500/30 transition-all flex items-center justify-center select-none"
                        >+</button>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-slate-500">per day:</span>
                          <span className="text-slate-300 font-mono tabular-nums font-bold">{fmtNum(flowM3h * 24, 1)} m³/day</span>
                        </div>
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-slate-500">annual kL:</span>
                          <span className={`font-mono tabular-nums font-black ${annualKL >= 10000 ? 'text-red-400' : 'text-teal-400'}`}>
                            {fmtNum(annualKL, 0)} kL/yr
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Sample count — button group */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Samples to Use
                        {siteSamples.length > 0 && (
                          <span className="ml-1.5 text-teal-400 font-black">{siteSamples.length} available</span>
                        )}
                      </label>
                      {siteSamples.length === 0 ? (
                        <p className="text-[10px] text-amber-400 px-1 leading-snug">
                          No effluent samples linked to this SA Water site — add effluent samples and select this site when recording them.
                        </p>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          {([2, 3, 5, 'all'] as const).filter(n => n === 'all' || n <= siteSamples.length).map(n => (
                            <button
                              key={String(n)}
                              onClick={() => setSampleCount(n)}
                              className={`flex-1 min-w-[44px] px-2 py-1.5 rounded-lg text-[10px] font-black border transition-all ${
                                sampleCount === n
                                  ? 'bg-teal-500/25 text-teal-200 border-teal-500/50'
                                  : 'bg-slate-800/60 text-slate-400 border-slate-800/60 hover:bg-teal-500/10 hover:text-teal-300 hover:border-teal-500/30'
                              }`}
                            >
                              {n === 'all' ? `All ${siteSamples.length}` : `${n}`}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Reference number list */}
                      {activeSamples.length > 0 && (
                        <div className="space-y-0.5 max-h-[72px] overflow-y-auto scrollbar-thin">
                          {activeSamples.map((s, i) => {
                            const ref = s.reportNumber || null;
                            const name = s.sampleName || null;
                            const date = s.createdAt || (s as any).created_at;
                            return (
                              <div key={s.id} className="flex items-center gap-2 px-2 py-0.5 rounded bg-slate-800/40">
                                <div className="min-w-0 flex-1">
                                  {ref ? (
                                    <span className="text-[8px] font-mono font-black text-teal-400 truncate block">{ref}</span>
                                  ) : (
                                    <span className="text-[8px] font-mono text-slate-500 truncate block">{name || `S-${i + 1}`}</span>
                                  )}
                                </div>
                                {date && (
                                  <span className="text-[8px] text-slate-600 shrink-0">
                                    {new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Operating days slider */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Operating Days / Year</label>
                      <span className="text-[11px] font-black text-teal-400 tabular-nums">{operatingDays} days</span>
                    </div>
                    <input
                      type="range" min={50} max={365} step={5}
                      value={operatingDays}
                      onChange={e => setOperatingDays(Number(e.target.value))}
                      className="rb-slider w-full"
                      style={{
                        color: '#14b8a6',
                        background: `linear-gradient(to right, #14b8a6 ${((operatingDays - 50) / 315) * 100}%, #1e293b ${((operatingDays - 50) / 315) * 100}%)`,
                      }}
                    />
                    <div className="flex justify-between text-[9px] text-slate-600">
                      <span>50 days</span><span>365 days</span>
                    </div>
                  </div>

                  {/* Volume trigger status */}
                  {annualKL > 0 && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[10px] font-bold ${
                      volumeTriggered ? 'bg-red-500/10 border-red-500/25 text-red-300' : 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                    }`}>
                      {volumeTriggered ? <AlertTriangle className="w-3 h-3 shrink-0" /> : <CheckCircle2 className="w-3 h-3 shrink-0" />}
                      {fmtNum(annualKL, 0)} kL/yr —{' '}
                      {volumeTriggered ? 'Volume trigger EXCEEDED — all parameters chargeable' : 'Below 10,000 kL/yr trigger'}
                    </div>
                  )}

                  {/* Charges table */}
                  {activeSamples.length === 0 ? (
                    <div className="py-6 text-center">
                      <Beaker className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                      <p className="text-[11px] text-slate-500">No effluent samples linked to this site.</p>
                      <p className="text-[10px] text-slate-600 mt-0.5">When adding effluent samples, select this site to link them.</p>
                    </div>
                  ) : annualKL === 0 ? (
                    <div className="py-4 text-center">
                      <Gauge className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                      <p className="text-[11px] text-slate-500">Enter flow rate (m³/h) to calculate charges.</p>
                    </div>
                  ) : (
                    <>
                      {/* Sample references strip */}
                      {activeSamples.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {activeSamples.slice(0, 8).map((s, i) => (
                            <span key={i} className="text-[8px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400 font-mono border border-slate-800/40">
                              {(s as any).reportNumber || (s as any).report_number || `S${i + 1}`}
                            </span>
                          ))}
                          {activeSamples.length > 8 && (
                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-500 font-mono">+{activeSamples.length - 8} more</span>
                          )}
                        </div>
                      )}
                      <div className="overflow-x-auto rounded-lg border border-slate-800/50">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="border-b border-slate-800/60 bg-slate-800/40">
                              <th className="text-left py-2 px-3 text-slate-500 font-bold uppercase tracking-wider">Param</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold">Avg (mg/L)</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold">Load (kg/yr)</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold">$/kg</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold">Est. Charge</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map(r => {
                              const noData = r.avg === null;
                              const charged = r.charge !== null && r.charge > 0;
                              return (
                                <tr key={r.key} className={`border-b border-slate-800/40 ${charged ? 'bg-red-500/5' : ''}`}>
                                  <td className="py-2 px-3">
                                    <span className={`font-black ${charged ? 'text-red-300' : noData ? 'text-slate-600' : 'text-slate-300'}`}>{r.label}</span>
                                    {r.highRateApplied && <span className="ml-1 text-[9px] text-red-400 font-bold">▲ HIGH</span>}
                                    {r.tknCondition && <span className="ml-1 text-[9px] text-slate-600">if avg&gt;650</span>}
                                  </td>
                                  <td className={`text-right py-2 px-3 tabular-nums font-mono ${noData ? 'text-slate-400' : r.highRateApplied ? 'text-red-400 font-bold' : 'text-slate-300'}`}>
                                    {noData ? '—' : fmtNum(r.avg!, r.avg! < 10 ? 2 : 1)}
                                  </td>
                                  <td className={`text-right py-2 px-3 tabular-nums font-mono ${!r.loadKg ? 'text-slate-400' : r.triggered ? 'text-amber-400 font-bold' : 'text-slate-500'}`}>
                                    {!r.loadKg ? '—' : fmtNum(r.loadKg, 0)}
                                  </td>
                                  <td className="text-right py-2 px-3 tabular-nums font-mono text-slate-500">
                                    {noData ? '—' : `$${r.rate.toFixed(3)}`}
                                  </td>
                                  <td className={`text-right py-2 px-3 tabular-nums font-mono font-black ${charged ? (r.charge! > 5000 ? 'text-red-400' : 'text-amber-400') : 'text-slate-600'}`}>
                                    {noData ? '—'
                                      : !r.triggered ? <span className="text-[9px] font-normal text-emerald-600">Below trigger</span>
                                      : !r.exceeds   ? <span className="text-[9px] font-normal text-slate-600">≤ 650 mg/L</span>
                                      : fmtAUD(r.charge!)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Period breakdown */}
                      {anyCharged && chargedRows.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Period Breakdown</p>
                          <div className="overflow-x-auto rounded-lg border border-slate-800/50">
                            <table className="w-full text-[10px]">
                              <thead>
                                <tr className="border-b border-slate-800/60 bg-slate-800/30">
                                  <th className="text-left py-1.5 px-3 text-slate-600 font-bold">Parameter</th>
                                  <th className="text-right py-1.5 px-3 text-slate-600 font-bold">Monthly</th>
                                  <th className="text-right py-1.5 px-3 text-slate-600 font-bold">Quarterly</th>
                                  <th className="text-right py-1.5 px-3 text-slate-600 font-bold">Annual</th>
                                </tr>
                              </thead>
                              <tbody>
                                {chargedRows.map(r => (
                                  <tr key={r.key} className="border-b border-slate-800/30">
                                    <td className="py-1.5 px-3 text-slate-400 font-bold">{r.label}</td>
                                    <td className="text-right py-1.5 px-3 tabular-nums font-mono text-slate-400">{fmtAUD(r.charge! / 12)}</td>
                                    <td className="text-right py-1.5 px-3 tabular-nums font-mono text-slate-400">{fmtAUD(r.charge! / 4)}</td>
                                    <td className="text-right py-1.5 px-3 tabular-nums font-mono font-bold text-amber-400">{fmtAUD(r.charge!)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-slate-600/50 bg-slate-800/20">
                                  <td className="py-2 px-3 font-black text-slate-200">TOTAL</td>
                                  <td className="text-right py-2 px-3 tabular-nums font-mono font-black text-slate-200">{fmtAUD(totalCharge / 12)}</td>
                                  <td className="text-right py-2 px-3 tabular-nums font-mono font-black text-slate-200">{fmtAUD(totalCharge / 4)}</td>
                                  <td className="text-right py-2 px-3 tabular-nums font-mono font-black text-red-400">{fmtAUD(totalCharge)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <p className="text-[9px] text-slate-600 text-right">
                            incl. GST → monthly {fmtAUD(totalCharge/12*1.1)} · quarterly {fmtAUD(totalCharge/4*1.1)} · annual {fmtAUD(totalCharge*1.1)}
                          </p>
                        </div>
                      )}

                      {/* Total */}
                      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${anyCharged ? 'bg-red-500/10 border-red-500/25' : 'bg-slate-800/40 border-slate-700'}`}>
                        <div className="flex items-center gap-2">
                          <DollarSign className={`w-4 h-4 ${anyCharged ? 'text-red-400' : 'text-slate-500'}`} />
                          <div>
                            <p className={`text-[11px] font-black ${anyCharged ? 'text-red-300' : 'text-slate-400'}`}>
                              Estimated Annual Excess Charges
                            </p>
                            <p className="text-[9px] text-slate-600 mt-0.5">
                              {sampleLabel} · {fmtNum(annualKL, 0)} kL/yr · excl. GST
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-black tabular-nums ${anyCharged ? 'text-red-400' : 'text-emerald-400'}`}>{fmtAUD(totalCharge)}</p>
                          {anyCharged && <p className="text-[9px] text-slate-500 font-bold">incl. GST: {fmtAUD(totalCharge * 1.1)}</p>}
                        </div>
                      </div>

                      {/* Reference */}
                      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-800/50">
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Reference No.</span>
                        <span className="text-[10px] font-mono font-bold text-teal-400">{currentRef}</span>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleGeneratePdf('preview')}
                          disabled={generatingPdf}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-teal-600/20 hover:bg-teal-600/30 border border-teal-500/30 text-teal-300 text-[10px] font-bold transition-all disabled:opacity-50"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {generatingPdf ? 'Generating…' : 'Preview Report'}
                        </button>
                        <button
                          onClick={() => handleGeneratePdf('download')}
                          disabled={generatingPdf}
                          className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-slate-300 text-[10px] font-bold transition-all disabled:opacity-50"
                        >
                          <Download className="w-3.5 h-3.5" />
                          PDF
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving || annualKL === 0}
                          className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold transition-all disabled:opacity-50"
                        >
                          <Save className="w-3.5 h-3.5" />
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── Saved Estimates ── */}
            {selectedSite && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <List className="w-3.5 h-3.5 text-slate-500" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Saved Estimates — {selectedSite.name || 'This Site'}
                  </p>
                  {loadingEstimates && <RefreshCw className="w-3 h-3 text-slate-600 animate-spin" />}
                </div>

                {savedEstimates.length === 0 && !loadingEstimates ? (
                  <p className="text-[10px] text-slate-600 text-center py-3">No saved estimates for this site yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {savedEstimates.map((est, idx) => (
                      <div key={est.id ?? idx} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-800/60 hover:border-teal-500/25 transition-all group">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[10px] font-mono font-bold text-teal-400 truncate">{est.reference_number}</p>
                            <span className={`text-[9px] font-black tabular-nums ${est.total_charge > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmtAUD(est.total_charge)}/yr</span>
                          </div>
                          <p className="text-[9px] text-slate-500 mt-0.5 truncate">
                            {est.sample_label} · {fmtNum(est.annual_kl, 0)} kL/yr
                            {est.created_at ? ` · ${new Date(est.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleLoadEstimate(est)}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-teal-500/15 text-teal-400 text-[9px] font-bold hover:bg-teal-500/25 transition-all"
                          >
                            <RotateCcw className="w-2.5 h-2.5" /> Load
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>

    {/* ── PDF Preview Modal ── */}
    {showPdfModal && (
      <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={() => setShowPdfModal(false)}>
        <div className="w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col rb-section-elevated"
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-teal-400" />
              <div>
                <p className="text-sm font-black text-slate-200">SA Water Charge Estimate Report</p>
                <p className="text-[10px] font-mono text-slate-500">{currentRef}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleGeneratePdf('download')} disabled={generatingPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600/20 hover:bg-teal-600/30 border border-teal-500/30 text-teal-300 text-[10px] font-bold transition-all disabled:opacity-50">
                <Download className="w-3 h-3" /> Download PDF
              </button>
              <button onClick={() => setShowPdfModal(false)}
                className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center transition-all">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden bg-slate-950 min-h-[500px]">
            {pdfUrl
              ? <iframe src={`${pdfUrl}#view=FitH`} className="w-full h-full" style={{ minHeight: '500px' }} title="SA Water Estimate Report" />
              : <div className="flex items-center justify-center h-full text-slate-600"><FileText className="w-8 h-8 opacity-20 mr-2" />Generating…</div>
            }
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── SA Water rate data (shared) ─────────────────────────────────────────────
const SA_WATER_RATES_DATA = [
  { key: 'bod5',            label: 'BOD',  fullName: 'Biochemical Oxygen Demand', unit: 'mg/L', lowRate: 0.251, highRate: 0.378, threshold: 1000,  triggerKg: 10000, tknCondition: false },
  { key: 'tss',             label: 'SS',   fullName: 'Suspended Solids',           unit: 'mg/L', lowRate: 0.218, highRate: 0.315, threshold: 500,   triggerKg: 10000, tknCondition: false },
  { key: 'tds',             label: 'TDS',  fullName: 'Total Dissolved Solids',     unit: 'mg/L', lowRate: 0.126, highRate: 0.126, threshold: null,  triggerKg: 20000, tknCondition: false },
  { key: 'tn',              label: 'TKN',  fullName: 'Total Kjeldahl Nitrogen',    unit: 'mg/L', lowRate: 0.392, highRate: 0.392, threshold: null,  triggerKg: null,  tknCondition: true  },
  { key: 'totalPhosphorus', label: 'TP',   fullName: 'Total Phosphorus',           unit: 'mg/L', lowRate: 1.908, highRate: 1.908, threshold: null,  triggerKg: null,  tknCondition: false },
] as const;

// ─── SA Water Excess Concentration Charges Calculator ────────────────────────
const SA_WATER_RATES = [
  { key: 'bod5',            label: 'BOD',  fullName: 'Biochemical Oxygen Demand', unit: 'mg/L', lowRate: 0.251, highRate: 0.378, threshold: 1000,  triggerKg: 10000, tknCondition: false },
  { key: 'tss',             label: 'SS',   fullName: 'Suspended Solids',           unit: 'mg/L', lowRate: 0.218, highRate: 0.315, threshold: 500,   triggerKg: 10000, tknCondition: false },
  { key: 'tds',             label: 'TDS',  fullName: 'Total Dissolved Solids',      unit: 'mg/L', lowRate: 0.126, highRate: 0.126, threshold: null,  triggerKg: 20000, tknCondition: false },
  { key: 'tn',              label: 'TKN',  fullName: 'Total Kjeldahl Nitrogen',     unit: 'mg/L', lowRate: 0.392, highRate: 0.392, threshold: null,  triggerKg: null,  tknCondition: true  },
  { key: 'totalPhosphorus', label: 'TP',   fullName: 'Total Phosphorus',            unit: 'mg/L', lowRate: 1.908, highRate: 1.908, threshold: null,  triggerKg: null,  tknCondition: false },
] as const;

function SAWaterChargesPanel({
  samples,
  onViewFullPage,
  site,
  customer,
}: {
  samples: UnifiedSample[];
  onViewFullPage: () => void;
  site?: CustomerSite;
  customer?: Customer;
}) {
  // ── Sample selection ──────────────────────────────────────────────────────────
  const [sampleMode, setSampleMode] = useState<'combined' | 'individual'>('combined');
  const [selectedSampleId, setSelectedSampleId] = useState<number | null>(null);

  // ── Volume mode ───────────────────────────────────────────────────────────────
  const [volumeMode, setVolumeMode] = useState<'direct' | 'flowrate'>('direct');
  const [annualVolKL, setAnnualVolKL] = useState<string>('');
  const [flowRateM3day, setFlowRateM3day] = useState<string>('');
  const [operatingDays, setOperatingDays] = useState<number>(250);

  // ── Report modal ──────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [reportRef] = useState<string>(() => {
    const yr = new Date().getFullYear();
    const a = Math.floor(1000 + Math.random() * 9000);
    const b = Math.floor(1000 + Math.random() * 9000);
    return `SAW-${yr}-${a}-${b}`;
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [savedEstimate, setSavedEstimate] = useState(false);

  // ── Active sample set ─────────────────────────────────────────────────────────
  const activeSamples = useMemo(() => {
    if (sampleMode === 'individual' && selectedSampleId !== null) {
      return samples.filter(s => s.id === selectedSampleId);
    }
    return samples;
  }, [sampleMode, selectedSampleId, samples]);

  // ── Averages ──────────────────────────────────────────────────────────────────
  const avgs = useMemo(() => {
    const result: Record<string, number | null> = {};
    for (const r of SA_WATER_RATES) {
      const vals = activeSamples
        .map(s => {
          const p: Record<string, unknown> = typeof s.params === 'string'
            ? (() => { try { return JSON.parse(s.params as string); } catch { return {}; } })()
            : ((s.params as Record<string, unknown>) || {});
          const aliases: Record<string, string[]> = {
            bod5: ['bod5','bod','BOD5','BOD'], tss: ['tss','TSS','ss','SS'],
            tds: ['tds','TDS'], tn: ['tn','TN','tkn','TKN','totalNitrogen'],
            totalPhosphorus: ['totalPhosphorus','tp','TP','phosphorus'],
          };
          const keys = aliases[r.key] ?? [r.key];
          for (const k of keys) {
            const v = p[k];
            if (typeof v === 'number' && v >= 0) return v;
          }
          return null;
        })
        .filter((v): v is number => v !== null);
      result[r.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }
    return result;
  }, [activeSamples]);

  // ── Annual volume ─────────────────────────────────────────────────────────────
  const effectiveAnnualKL = useMemo(() => {
    if (volumeMode === 'direct') return parseFloat(annualVolKL) || 0;
    return (parseFloat(flowRateM3day) || 0) * operatingDays;
  }, [volumeMode, annualVolKL, flowRateM3day, operatingDays]);

  const volumeTriggered = effectiveAnnualKL >= 10000;

  // ── Charge rows ───────────────────────────────────────────────────────────────
  const rows = SA_WATER_RATES.map(r => {
    const avg = avgs[r.key] ?? null;
    const loadKg = avg !== null && effectiveAnnualKL > 0 ? avg * effectiveAnnualKL * 0.001 : null;
    const highRateApplied = r.threshold !== null && avg !== null && avg > r.threshold;
    const rate = highRateApplied ? r.highRate : r.lowRate;
    const triggered = volumeTriggered || (r.triggerKg !== null && loadKg !== null && loadKg > r.triggerKg);
    const exceeds = r.tknCondition ? (avg !== null && avg > 650) : true;
    const charge = triggered && exceeds && loadKg !== null ? loadKg * rate : null;
    return { ...r, avg, loadKg, rate, charge, triggered, exceeds, highRateApplied };
  });

  const totalCharge = rows.reduce((sum, r) => sum + (r.charge ?? 0), 0);
  const anyCharged = rows.some(r => r.charge !== null && r.charge > 0);
  const chargedRows = rows.filter(r => r.charge !== null && r.charge! > 0);

  const fmtNum = (n: number, dp = 1) => n.toFixed(dp).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fmtAUD = (n: number) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sampleLabel = sampleMode === 'combined'
    ? `Combined — ${activeSamples.length} sample${activeSamples.length !== 1 ? 's' : ''}`
    : activeSamples.length > 0
      ? `Sample: ${new Date(activeSamples[0].date || (activeSamples[0] as any).created_at || '').toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
      : 'Individual sample';

  // ── PDF generation ────────────────────────────────────────────────────────────
  const handleGeneratePdf = async (mode: 'preview' | 'download') => {
    setGeneratingPdf(true);
    try {
      const pdfRows: SAWaterChargeRow[] = rows.map(r => ({
        label: r.label,
        fullName: r.fullName,
        avg: r.avg,
        loadKg: r.loadKg,
        rate: r.rate,
        charge: r.charge,
        triggered: r.triggered,
        exceeds: r.exceeds,
        highRateApplied: r.highRateApplied,
      }));
      const result = generateSAWaterChargesReport({
        customerCompany: customer?.company || 'Unknown Customer',
        siteName: site?.name || site?.address || 'Site',
        siteAddress: [site?.address, site?.city].filter(Boolean).join(', ') || '',
        sitePostcode: site?.postcode,
        sampleLabel,
        annualKL: effectiveAnnualKL,
        flowRateM3day: volumeMode === 'flowrate' ? (parseFloat(flowRateM3day) || undefined) : undefined,
        operatingDays: volumeMode === 'flowrate' ? operatingDays : undefined,
        volumeMethod: volumeMode,
        rows: pdfRows,
        totalCharge,
        referenceNumber: reportRef,
        mode,
      });
      if (mode === 'preview' && result) {
        setPreviewUrl(result);
        setShowModal(true);
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSaveEstimate = () => {
    try {
      const estimates = JSON.parse(localStorage.getItem('rb_saw_estimates') || '[]');
      estimates.unshift({
        ref: reportRef,
        customerCompany: customer?.company || '',
        siteName: site?.name || site?.address || '',
        totalCharge,
        annualKL: effectiveAnnualKL,
        sampleLabel,
        createdAt: new Date().toISOString(),
        rows: chargedRows.map(r => ({ label: r.label, charge: r.charge })),
      });
      localStorage.setItem('rb_saw_estimates', JSON.stringify(estimates.slice(0, 50)));
      setSavedEstimate(true);
      toast.success(`Estimate ${reportRef} saved locally`);
    } catch {
      toast.error('Failed to save estimate');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="rounded-xl border border-blue-500/25 bg-blue-950/15 overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-blue-500/15 bg-blue-900/10">
        <div className="flex items-center gap-2">
          <Receipt className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">SA Water — Excess Concentration Charges</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20 font-bold">AUD excl. GST</span>
        </div>
        <button onClick={onViewFullPage} className="flex items-center gap-1 text-[9px] text-blue-500 hover:text-blue-300 transition-colors font-bold">
          Full Calculator <ArrowRight className="w-2.5 h-2.5" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* ── Info banner ── */}
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/8 border border-blue-500/15">
          <Info className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
          <p className="text-[10px] text-blue-300 leading-relaxed">
            Charges triggered when annual discharge exceeds <strong>10 t BOD or SS</strong>, <strong>20 t TDS</strong>, or <strong>10,000 kL</strong> volume.
          </p>
        </div>

        {/* ── Sample selector ── */}
        {samples.length > 1 && (
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Sample Basis</label>
            <div className="flex rounded-lg overflow-hidden border border-slate-700">
              {(['combined', 'individual'] as const).map(m => (
                <button key={m} onClick={() => { setSampleMode(m); if (m === 'combined') setSelectedSampleId(null); }}
                  className={`flex-1 py-2 text-[10px] font-bold transition-all ${sampleMode === m ? 'bg-blue-600/25 text-blue-300' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300'}`}>
                  {m === 'combined' ? `Combined (${samples.length})` : 'Individual Sample'}
                </button>
              ))}
            </div>
            {sampleMode === 'individual' && (
              <select
                value={selectedSampleId ?? ''}
                onChange={e => setSelectedSampleId(Number(e.target.value) || null)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-200 text-[10px] focus:outline-none focus:border-blue-500/50"
              >
                <option value="">— Select a sample —</option>
                {samples.map(s => {
                  const d = s.date || (s as any).created_at;
                  const lbl = d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : `Sample #${s.id}`;
                  return <option key={s.id} value={s.id}>{lbl}</option>;
                })}
              </select>
            )}
          </div>
        )}

        {/* ── Volume mode toggle ── */}
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Discharge Volume</label>
          <div className="flex rounded-lg overflow-hidden border border-slate-700">
            {(['direct', 'flowrate'] as const).map(m => (
              <button key={m} onClick={() => setVolumeMode(m)}
                className={`flex-1 py-2 text-[10px] font-bold transition-all ${volumeMode === m ? 'bg-blue-600/25 text-blue-300' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300'}`}>
                {m === 'direct' ? 'Direct entry (kL/yr)' : 'Flow rate (m³/day)'}
              </button>
            ))}
          </div>

          {volumeMode === 'direct' ? (
            <div className="relative">
              <input
                type="number" value={annualVolKL}
                onChange={e => setAnnualVolKL(e.target.value)}
                placeholder="e.g. 6985" min="0"
                className="w-full pl-3 pr-14 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-200 text-sm placeholder-slate-600 focus:outline-none focus:border-blue-500/50 font-mono text-right tabular-nums"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none">kL/yr</span>
            </div>
          ) : (
            <div className="space-y-3 px-3 py-3 rounded-lg bg-slate-800/30 border border-slate-800/60">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 font-bold block mb-1">Flow Rate</label>
                  <div className="relative">
                    <input
                      type="number" value={flowRateM3day}
                      onChange={e => setFlowRateM3day(e.target.value)}
                      placeholder="e.g. 28" min="0"
                      className="w-full pl-3 pr-16 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-200 text-sm placeholder-slate-600 focus:outline-none focus:border-blue-500/50 font-mono text-right tabular-nums"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 pointer-events-none">m³/day</span>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 font-bold block mb-1">
                    Operating Days <span className="text-blue-400 font-black ml-1">{operatingDays}</span>
                  </label>
                  <input
                    type="range" min={50} max={365} step={5}
                    value={operatingDays}
                    onChange={e => setOperatingDays(Number(e.target.value))}
                    className="rb-slider w-full"
                    style={{
                      color: '#3b82f6',
                      background: `linear-gradient(to right, #3b82f6 ${((operatingDays - 50) / 315) * 100}%, #1e293b ${((operatingDays - 50) / 315) * 100}%)`,
                    }}
                  />
                  <div className="flex justify-between text-[9px] text-slate-600 mt-0.5">
                    <span>50d</span><span>365d</span>
                  </div>
                </div>
              </div>
              {effectiveAnnualKL > 0 && (
                <div className="flex items-center gap-2 text-[10px] pt-1 border-t border-slate-800/50">
                  <span className="text-slate-500">Calculated annual volume:</span>
                  <span className="text-blue-300 font-black font-mono tabular-nums">{fmtNum(effectiveAnnualKL, 0)} kL</span>
                  <span className="text-slate-600 text-[9px]">({fmtNum(parseFloat(flowRateM3day)||0,1)} × {operatingDays} days)</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Volume trigger status ── */}
        {effectiveAnnualKL > 0 && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[10px] font-bold ${
            volumeTriggered ? 'bg-red-500/10 border-red-500/25 text-red-300' : 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
          }`}>
            {volumeTriggered ? <AlertTriangle className="w-3 h-3 shrink-0" /> : <CheckCircle2 className="w-3 h-3 shrink-0" />}
            {fmtNum(effectiveAnnualKL, 0)} kL/yr —{' '}
            {volumeTriggered ? 'Volume trigger EXCEEDED — all parameters chargeable' : 'Below 10,000 kL/yr volume trigger'}
          </div>
        )}

        {/* ── Charges table ── */}
        {activeSamples.length === 0 ? (
          <p className="text-center py-4 text-[11px] text-slate-500">
            {sampleMode === 'individual' && selectedSampleId === null
              ? 'Select a sample above to calculate charges.'
              : 'No effluent samples — add samples to calculate charges.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="border-b border-slate-800/60">
                  <th className="text-left py-1.5 pr-2 text-slate-500 font-bold uppercase tracking-wider">Param</th>
                  <th className="text-right py-1.5 px-2 text-slate-500 font-bold uppercase tracking-wider">Avg (mg/L)</th>
                  <th className="text-right py-1.5 px-2 text-slate-500 font-bold uppercase tracking-wider">Load (kg/yr)</th>
                  <th className="text-right py-1.5 px-2 text-slate-500 font-bold uppercase tracking-wider">$/kg</th>
                  <th className="text-right py-1.5 pl-2 text-slate-500 font-bold uppercase tracking-wider">Est. Charge</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const noData = r.avg === null;
                  const charged = r.charge !== null && r.charge > 0;
                  return (
                    <tr key={r.key} className={`border-b border-slate-800/40 ${charged ? 'bg-red-500/5' : ''}`}>
                      <td className="py-2 pr-2">
                        <span className={`font-black ${charged ? 'text-red-300' : noData ? 'text-slate-600' : 'text-slate-300'}`}>{r.label}</span>
                        {r.tknCondition && <span className="text-[9px] text-slate-600 ml-1">if avg&gt;650</span>}
                      </td>
                      <td className={`text-right py-2 px-2 tabular-nums font-mono ${noData ? 'text-slate-400' : r.highRateApplied ? 'text-red-400 font-bold' : 'text-slate-300'}`}>
                        {noData ? '—' : fmtNum(r.avg!, r.avg! < 10 ? 2 : 1)}
                        {r.highRateApplied && <span className="ml-1 text-[9px] text-red-500">▲</span>}
                      </td>
                      <td className={`text-right py-2 px-2 tabular-nums font-mono ${!r.loadKg ? 'text-slate-400' : r.triggered ? 'text-amber-400 font-bold' : 'text-slate-500'}`}>
                        {!r.loadKg ? '—' : fmtNum(r.loadKg, 0)}
                      </td>
                      <td className="text-right py-2 px-2 tabular-nums font-mono text-slate-500">
                        {noData ? '—' : `$${r.rate.toFixed(3)}`}
                      </td>
                      <td className={`text-right py-2 pl-2 tabular-nums font-mono font-black ${charged ? (r.charge! > 5000 ? 'text-red-400' : 'text-amber-400') : 'text-slate-600'}`}>
                        {noData || effectiveAnnualKL === 0 ? '—'
                          : !r.triggered ? <span className="text-[9px] font-normal text-emerald-600">Below trigger</span>
                          : !r.exceeds   ? <span className="text-[9px] font-normal text-slate-600">≤ 650 mg/L</span>
                          : fmtAUD(r.charge!)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Period breakdown ── */}
        {effectiveAnnualKL > 0 && anyCharged && chargedRows.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Period Breakdown</p>
            <div className="overflow-x-auto rounded-lg border border-slate-800/50">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-slate-800/60 bg-slate-800/30">
                    <th className="text-left py-1.5 px-3 text-slate-600 font-bold">Parameter</th>
                    <th className="text-right py-1.5 px-3 text-slate-600 font-bold">Monthly</th>
                    <th className="text-right py-1.5 px-3 text-slate-600 font-bold">Quarterly</th>
                    <th className="text-right py-1.5 px-3 text-slate-600 font-bold">Annual</th>
                  </tr>
                </thead>
                <tbody>
                  {chargedRows.map(r => (
                    <tr key={r.key} className="border-b border-slate-800/30">
                      <td className="py-1.5 px-3 text-slate-400 font-bold">{r.label}</td>
                      <td className="text-right py-1.5 px-3 tabular-nums font-mono text-slate-400">{fmtAUD(r.charge! / 12)}</td>
                      <td className="text-right py-1.5 px-3 tabular-nums font-mono text-slate-400">{fmtAUD(r.charge! / 4)}</td>
                      <td className="text-right py-1.5 px-3 tabular-nums font-mono font-bold text-amber-400">{fmtAUD(r.charge!)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-600/50 bg-slate-800/20">
                    <td className="py-2 px-3 font-black text-slate-200">TOTAL</td>
                    <td className="text-right py-2 px-3 tabular-nums font-mono font-black text-slate-200">{fmtAUD(totalCharge / 12)}</td>
                    <td className="text-right py-2 px-3 tabular-nums font-mono font-black text-slate-200">{fmtAUD(totalCharge / 4)}</td>
                    <td className="text-right py-2 px-3 tabular-nums font-mono font-black text-red-400">{fmtAUD(totalCharge)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[9px] text-slate-600 text-right">
              incl. GST → monthly {fmtAUD(totalCharge / 12 * 1.1)} · quarterly {fmtAUD(totalCharge / 4 * 1.1)} · annual {fmtAUD(totalCharge * 1.1)}
            </p>
          </div>
        )}

        {/* ── Total summary ── */}
        {effectiveAnnualKL > 0 && activeSamples.length > 0 && (
          <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${anyCharged ? 'bg-red-500/10 border-red-500/25' : 'bg-slate-800/40 border-slate-700'}`}>
            <div className="flex items-center gap-2">
              <DollarSign className={`w-4 h-4 ${anyCharged ? 'text-red-400' : 'text-slate-500'}`} />
              <div>
                <p className={`text-[11px] font-black ${anyCharged ? 'text-red-300' : 'text-slate-400'}`}>
                  Estimated Annual Excess Charges
                </p>
                <p className="text-[9px] text-slate-600 mt-0.5">
                  {sampleLabel} · {fmtNum(effectiveAnnualKL, 0)} kL/yr · excl. GST
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-lg font-black tabular-nums ${anyCharged ? 'text-red-400' : 'text-emerald-400'}`}>
                {fmtAUD(totalCharge)}
              </p>
              {anyCharged && (
                <p className="text-[9px] text-slate-500 font-bold">incl. GST: {fmtAUD(totalCharge * 1.1)}</p>
              )}
            </div>
          </div>
        )}

        {/* ── Generate report buttons ── */}
        {effectiveAnnualKL > 0 && activeSamples.length > 0 && (
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => handleGeneratePdf('preview')}
              disabled={generatingPdf}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 text-[10px] font-bold transition-all disabled:opacity-50"
            >
              <Eye className="w-3.5 h-3.5" />
              {generatingPdf ? 'Generating…' : 'Preview Estimate Report'}
            </button>
            <button
              onClick={() => handleGeneratePdf('download')}
              disabled={generatingPdf}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-slate-300 text-[10px] font-bold transition-all disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" />
              PDF
            </button>
          </div>
        )}

        {activeSamples.length > 1 && sampleMode === 'combined' && (
          <p className="text-[9px] text-slate-600 text-center">
            Averages across {activeSamples.length} samples · SA Water Fees &amp; Charges Schedule
          </p>
        )}
      </div>
    </div>

    {/* ── Report preview modal ── */}
    {showModal && (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
        onClick={() => setShowModal(false)}
      >
        <div
          className="w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col rb-section-elevated"
          onClick={e => e.stopPropagation()}
        >
          {/* Modal header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <FileText className="w-4.5 h-4.5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-black text-slate-200">SA Water Charge Estimate</p>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{reportRef}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!savedEstimate ? (
                <button
                  onClick={handleSaveEstimate}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/25 text-emerald-300 text-[10px] font-bold transition-all"
                >
                  <Database className="w-3 h-3" />
                  Save Estimate
                </button>
              ) : (
                <span className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-emerald-400 font-bold">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              )}
              <button
                onClick={() => handleGeneratePdf('download')}
                disabled={generatingPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 text-[10px] font-bold transition-all disabled:opacity-50"
              >
                <Download className="w-3 h-3" />
                Download PDF
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center transition-all"
              >
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Modal summary strip */}
          <div className="px-5 py-3 border-b border-slate-800 bg-rb-surface shrink-0">
            <div className="grid grid-cols-4 gap-4 text-[10px]">
              <div>
                <p className="text-slate-600 font-bold uppercase tracking-wider mb-0.5">Reference No.</p>
                <p className="text-blue-300 font-mono font-bold">{reportRef}</p>
              </div>
              <div>
                <p className="text-slate-600 font-bold uppercase tracking-wider mb-0.5">Sample Basis</p>
                <p className="text-slate-300 font-bold">{sampleLabel}</p>
              </div>
              <div>
                <p className="text-slate-600 font-bold uppercase tracking-wider mb-0.5">Annual Volume</p>
                <p className="text-slate-300 font-mono font-bold">{fmtNum(effectiveAnnualKL, 0)} kL/yr</p>
              </div>
              <div>
                <p className="text-slate-600 font-bold uppercase tracking-wider mb-0.5">Est. Annual Charge</p>
                <p className={`font-black font-mono ${anyCharged ? 'text-red-400' : 'text-emerald-400'}`}>{fmtAUD(totalCharge)}</p>
              </div>
            </div>
          </div>

          {/* PDF iframe */}
          <div className="flex-1 overflow-hidden bg-slate-950 min-h-[420px]">
            {previewUrl ? (
              <iframe
                src={`${previewUrl}#view=FitH`}
                className="w-full h-full"
                style={{ minHeight: '420px' }}
                title="SA Water Charges Estimate Report"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-slate-600 text-sm">
                <div className="text-center">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p>Generating PDF preview…</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function CustomerDetailPanel({
  customer, stats, isActive, onSetActive, onDelete, navigate, onActivateAndGo, onEditCustomer, forceTab,
}: {
  customer: Customer; stats: CustomerStats; isActive: boolean;
  onSetActive: () => void; onDelete: () => void; navigate: ReturnType<typeof useNavigate>;
  onActivateAndGo: (path: string) => void;
  onEditCustomer: (updated: Partial<Customer>) => void;
  forceTab?: { tab: DetailTab; nonce: number } | null;
}) {
  const [tab, setTab] = useState<DetailTab>('overview');

  // When parent requests a specific tab (e.g. from stats bar click), switch to it
  useEffect(() => {
    if (forceTab) setTab(forceTab.tab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceTab?.nonce]);
  const [expandedSampleId, setExpandedSampleId] = useState<number | null>(null);
  const [expandedTrainId, setExpandedTrainId] = useState<string | null>(null);
  const [effluentPreviewUrl, setEffluentPreviewUrl] = useState<string | null>(null);
  const [effluentPreviewTitle, setEffluentPreviewTitle] = useState('');
  const [effluentPreviewDownloader, setEffluentPreviewDownloader] = useState<() => void>(() => () => {});

  const closeEffluentPreview = () => {
    if (effluentPreviewUrl) URL.revokeObjectURL(effluentPreviewUrl);
    setEffluentPreviewUrl(null);
  };

  const handleEffluentPreview = (s: any) => {
    const params: Record<string, number> = typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {});
    const reportOpts = {
      sampleName: s.sampleName || s.sample_name || 'Effluent Sample',
      sampleDate: s.createdAt || new Date(),
      customerName: customer.company || customer.name,
      siteName: (() => { const site = sites.find(st => st.id === s.siteId); return site?.name; })(),
      siteAddress: (() => { const site = sites.find(st => st.id === s.siteId); return site?.address; })(),
      permitRef: customer.permitRef || s.reportNumber,
      regulatoryAuthority: customer.regulatoryBody || stdCode,
      standard: stdCode,
      linkedSampleId: s.linkedSampleId,
      notes: s.notes,
      isDafEffluent: (s.sampleType === 'effluent' && s.sampleName?.toLowerCase().includes('daf')) || false,
    };
    try {
      const url = generateEffluentSamplePDF(params, reportOpts, 'preview');
      if (!url) { toast.error('Preview generation failed'); return; }
      setEffluentPreviewUrl(url);
      setEffluentPreviewTitle(`${reportOpts.sampleName} — Discharge Compliance Report`);
      setEffluentPreviewDownloader(() => () => {
        generateEffluentSamplePDF(params, reportOpts, 'save');
      });
    } catch (err: any) {
      toast.error(`Preview failed: ${err.message}`);
    }
  };
  const [editRegulatoryOpen, setEditRegulatoryOpen] = useState(false);
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [sites, setSites] = useState<CustomerSite[]>([]);
  const [previewProposalId, setPreviewProposalId] = useState<string | null>(null);
  const [showSAWDrawer, setShowSAWDrawer] = useState(false);
  const { enterProposalMode } = useProposal();

  // Load sites: localStorage first, then merge from API if authenticated
  useEffect(() => {
    const local = getLocalSites(customer.id);
    setSites(local);
    if (hasAuthToken()) {
      const serverCid = (customer as any).serverId || parseInt(customer.id);
      if (serverCid > 0) {
        apiListCustomerSites(serverCid).then(serverSites => {
          if (!Array.isArray(serverSites)) return;
          // Merge: server is authoritative — map to CustomerSite shape and update localStorage
          const merged: CustomerSite[] = serverSites.map((s: any) => ({
            id: String(s.id),         // use serverId as local id for server-sourced sites
            serverId: s.id,
            customerId: customer.id,
            serverCustomerId: serverCid,
            name: s.name || '',
            address: s.address || '',
            postcode: s.postcode || '',
            country: s.country || customer.country || '',
            state: s.state || undefined,
            stateName: s.stateName || undefined,
            region: s.region || undefined,
            permitRef: s.permitRef || undefined,
            dischargeRoute: s.dischargeRoute || undefined,
            standard: s.standard || undefined,
            notes: s.notes || undefined,
            createdAt: s.createdAt || new Date().toISOString(),
            updatedAt: s.updatedAt || new Date().toISOString(),
          }));
          // Merge into localStorage:
          // · server sites are authoritative for their records
          // · locally-saved unsynced sites (no serverId) are preserved — they haven't reached the server yet
          // · sites belonging to other customers are kept untouched
          const allLocal = getLocalSites();
          const otherCustomerSites = allLocal.filter(ls => ls.customerId !== customer.id);
          const localUnsyncedThisCustomer = allLocal.filter(
            ls => ls.customerId === customer.id && !ls.serverId,
          );
          const mergedServerIds = new Set(merged.map(s => String(s.serverId)));
          const stillUnsynced = localUnsyncedThisCustomer.filter(
            ls => !mergedServerIds.has(String(ls.id)),
          );
          const allSites = [...merged, ...stillUnsynced, ...otherCustomerSites];
          localStorage.setItem('rb_customer_sites', JSON.stringify(allSites));
          setSites([...merged, ...stillUnsynced]);
        }).catch(() => { /* backend offline — use local */ });
      }
    }
  }, [customer.id, addSiteOpen]);

  const TABS: { id: DetailTab; label: string; icon: React.ElementType; count?: number; color: string }[] = [
    { id: 'overview',   label: 'Overview',   icon: BarChart3,    color: 'text-rb-brand' },
    { id: 'sites',      label: 'Sites',      icon: MapPin,       count: sites.length,           color: 'text-teal-400' },
    { id: 'influent',   label: 'Influent',   icon: Droplets,     count: stats.influent.length,  color: 'text-cyan-400' },
    { id: 'effluent',   label: 'Effluent',   icon: Beaker,       count: stats.effluent.length,  color: 'text-rb-brand' },
    { id: 'trains',     label: 'Trains',     icon: Workflow,     count: stats.trains.length,    color: 'text-amber-400' },
    { id: 'proposals',  label: 'Proposals',  icon: FileText,     count: stats.proposals.length, color: 'text-violet-400' },
  ];

  const tabColor: Record<DetailTab, string> = {
    overview: 'border-rb-brand', sites: 'border-teal-500', influent: 'border-cyan-500',
    effluent: 'border-rb-brand', trains: 'border-amber-500', proposals: 'border-violet-500',
  };

  const stdCode = (customer.standard || COUNTRY_TO_STANDARD[customer.country || 'GB'] || 'UK_EA') as StandardCode;
  const sc = STANDARD_COLORS[stdCode] || STANDARD_COLORS.UK_EA;
  const flag = customer.country ? (COUNTRY_FLAGS[customer.country] || '🌐') : null;

  return (
    <div className="h-full flex flex-col bg-[#0d1b30] border border-slate-800 rounded-2xl overflow-hidden">

      {/* ── Effluent PDF Preview Modal ── */}
      <AnimatePresence>
        {effluentPreviewUrl && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
            onClick={e => { if (e.target === e.currentTarget) closeEffluentPreview(); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-rb-surface rounded-2xl w-full max-w-5xl flex flex-col shadow-2xl overflow-hidden"
              style={{ height: '92vh' }}
            >
              <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-rb-brand/20 shrink-0">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-rb-brand" />
                  <span className="text-sm font-bold text-white truncate max-w-md">{effluentPreviewTitle}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rb-brand/15 text-rb-brand border border-rb-brand/25 font-bold uppercase tracking-wider">Regulatory PDF</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={effluentPreviewDownloader} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-brand text-slate-200 hover:bg-yellow-400 transition-colors">
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                  <button onClick={closeEffluentPreview} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <iframe src={effluentPreviewUrl} className="flex-1 w-full bg-rb-surface" title="Effluent PDF Preview" style={{ border: 'none' }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Panel Header ── */}
      <div className="px-5 py-4 border-b border-slate-800/60 bg-gradient-to-r from-rb-brand/5 to-transparent shrink-0">
        {/* Company identity row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-rb-brand/15 border border-rb-brand/25 flex items-center justify-center text-xl font-black text-rb-brand shrink-0">
              {customer.company.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-black text-slate-100">{customer.company}</h2>
                {flag && <span className="text-base">{flag}</span>}
                {isActive && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 flex items-center gap-1 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> ACTIVE SESSION
                  </span>
                )}
                {(stdCode === 'SA_WATER' || sites.some(s => s.standard === 'SA_WATER')) && (
                  <button
                    onClick={() => setShowSAWDrawer(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-black border transition-all shrink-0 ${
                      showSAWDrawer
                        ? 'bg-teal-500/25 text-teal-100 border-teal-400/60 shadow-lg shadow-teal-500/15'
                        : 'bg-gradient-to-r from-teal-600/25 to-cyan-500/15 text-teal-200 border-teal-500/50 hover:from-teal-600/35 hover:to-cyan-500/25 hover:border-teal-400/70 hover:shadow-lg hover:shadow-teal-500/10 animate-pulse hover:animate-none'
                    }`}
                  >
                    <DollarSign className="w-3.5 h-3.5 text-teal-300" />
                    <span>SA WATER CHARGES</span>
                    {showSAWDrawer
                      ? <ChevronUp className="w-3.5 h-3.5" />
                      : <ChevronDown className="w-3.5 h-3.5" />
                    }
                  </button>
                )}
                {customer.isDemo && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-500/15 text-violet-400 border border-violet-500/20 shrink-0">DEMO</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap text-[10px] text-slate-500">
                {customer.name && <span className="flex items-center gap-1"><Users className="w-3 h-3 shrink-0" />{customer.name}</span>}
                {customer.industryType && <span className="flex items-center gap-1"><Factory className="w-3 h-3 shrink-0" />{customer.industryType}</span>}
                {customer.contactEmail && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" />{customer.contactEmail}</span>}
                {customer.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" />{customer.phone}</span>}
              </div>
              {customer.standard && (
                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${sc.bg} ${sc.border} ${sc.color}`}>
                  <ShieldCheck className="w-2.5 h-2.5" />
                  {customer.standard.replace('_', ' ')} — {flag} {customer.country ? COUNTRY_NAMES[customer.country] : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
            <button onClick={() => setEditCustomerOpen(true)} title="Edit customer details"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-slate-800/60 border border-slate-700 text-slate-400 hover:bg-rb-brand/10 hover:text-rb-brand hover:border-rb-brand/30 transition-all">
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button onClick={onDelete} title="Delete customer"
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Action buttons row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {isActive ? (
            <div className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              <CheckCircle2 className="w-3.5 h-3.5" /> Active Session
            </div>
          ) : (
            <button onClick={onSetActive}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-slate-800/60 text-slate-300 border border-slate-700 hover:bg-cyan-500/10 hover:text-cyan-400 hover:border-cyan-500/20 transition-all">
              <Star className="w-3.5 h-3.5" /> Set as Active
            </button>
          )}
          <button onClick={() => onActivateAndGo('/influent-entry')}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 active:scale-95 transition-all">
            <Droplets className="w-3.5 h-3.5" /> Add Influent
          </button>
          <button onClick={() => onActivateAndGo('/effluent-entry')}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 active:scale-95 transition-all">
            <Beaker className="w-3.5 h-3.5" /> Add Effluent
          </button>
          <button onClick={() => onActivateAndGo('/process-db')}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 active:scale-95 transition-all">
            <Workflow className="w-3.5 h-3.5" /> Build Train
          </button>
        </div>
      </div>

      {/* ── SA Water Charges Inline Panel ── */}
      <AnimatePresence>
        {showSAWDrawer && (
          <motion.div
            key="saw-calc"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden shrink-0"
          >
            <SAWaterChargesDrawer
              customer={customer}
              sites={sites}
              allSamples={stats.effluent}
              onClose={() => setShowSAWDrawer(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-0.5 px-3 pt-2 border-b border-slate-800/60 shrink-0 overflow-x-auto scrollbar-none bg-[#070d1c]/60">
        {TABS.map(t => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative flex items-center gap-2 px-3.5 py-2.5 text-[11px] font-bold whitespace-nowrap transition-all rounded-t-lg ${
                isActive
                  ? `${t.color} bg-slate-800/90`
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40'
              }`}
            >
              <t.icon className={`w-3.5 h-3.5 ${isActive ? '' : 'opacity-60'}`} />
              <span className={isActive ? 'opacity-100' : 'opacity-75'}>{t.label}</span>
              {t.count !== undefined && t.count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-black leading-none ${
                  isActive
                    ? 'bg-rb-surface/15 ' + t.color
                    : 'bg-slate-700 text-slate-400'
                }`}>
                  {t.count}
                </span>
              )}
              {isActive && (
                <span className={`absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full`} style={{
                  background: t.id === 'overview' ? 'var(--rb-brand)'
                    : t.id === 'sites' ? '#14b8a6'
                    : t.id === 'influent' ? '#22d3ee'
                    : t.id === 'effluent' ? 'var(--rb-brand)'
                    : t.id === 'trains' ? '#f59e0b'
                    : '#8b5cf6',
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Tab Content ── */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }} className="p-4 space-y-4">

            {/* ═══ OVERVIEW ═══ */}
            {tab === 'overview' && (
              <>
                {/* Interactive data portal cards — click to switch tab */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      tabId: 'sites' as DetailTab,
                      label: 'Sites / Locations',
                      sub: sites.length > 0
                        ? `${sites.filter(s => s.permitRef).length} with permits`
                        : 'No sites added',
                      value: sites.length,
                      icon: MapPin,
                      color: 'text-teal-400',
                      border: 'border-teal-500/25',
                      bg: 'bg-teal-500/5 hover:bg-teal-500/10',
                      btn: 'Add Site',
                      btnAction: () => setTab('sites'),
                      btnColor: 'bg-teal-500/10 text-teal-400 border-teal-500/20 hover:bg-teal-500/20',
                    },
                    {
                      tabId: 'influent' as DetailTab,
                      label: 'Influent Samples',
                      sub: stats.influent.length > 0
                        ? `Latest: ${new Date(stats.influent[0].createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                        : 'No samples yet',
                      value: stats.influent.length,
                      icon: Droplets,
                      color: 'text-cyan-400',
                      border: 'border-cyan-500/25',
                      bg: 'bg-cyan-500/5 hover:bg-cyan-500/10',
                      btn: 'Add Influent',
                      btnAction: () => onActivateAndGo('/influent-entry'),
                      btnColor: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/20',
                    },
                    {
                      tabId: 'effluent' as DetailTab,
                      label: 'Effluent Samples',
                      sub: stats.effluent.length > 0
                        ? `Latest: ${new Date(stats.effluent[0].createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                        : 'No samples yet',
                      value: stats.effluent.length,
                      icon: Beaker,
                      color: 'text-rb-brand',
                      border: 'border-rb-brand/20',
                      bg: 'bg-rb-brand/5 hover:bg-rb-brand/10',
                      btn: 'Add Effluent',
                      btnAction: () => onActivateAndGo('/effluent-entry'),
                      btnColor: 'bg-rb-brand/10 text-rb-brand border-rb-brand/20 hover:bg-rb-brand/20',
                    },
                    {
                      tabId: 'trains' as DetailTab,
                      label: 'Process Trains',
                      sub: stats.trains.filter(t => t.status === 'assessed').length > 0
                        ? `${stats.trains.filter(t => t.status === 'assessed').length} assessed`
                        : stats.trains.length > 0 ? 'In progress' : 'None built yet',
                      value: stats.trains.length,
                      icon: Workflow,
                      color: 'text-amber-400',
                      border: 'border-amber-500/20',
                      bg: 'bg-amber-500/5 hover:bg-amber-500/10',
                      btn: 'Build Train',
                      btnAction: () => onActivateAndGo('/process-db'),
                      btnColor: 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20',
                    },
                    {
                      tabId: 'proposals' as DetailTab,
                      label: 'Proposals',
                      sub: stats.proposals.filter(p => p.status === 'validated').length > 0
                        ? `${stats.proposals.filter(p => p.status === 'validated').length} validated`
                        : stats.proposals.length > 0 ? 'In draft' : 'No proposals yet',
                      value: stats.proposals.length,
                      icon: FileText,
                      color: 'text-violet-400',
                      border: 'border-violet-500/20',
                      bg: 'bg-violet-500/5 hover:bg-violet-500/10',
                      btn: 'View Proposals',
                      btnAction: () => onActivateAndGo('/proposals'),
                      btnColor: 'bg-violet-500/10 text-violet-400 border-violet-500/20 hover:bg-violet-500/20',
                    },
                  ].map(card => (
                    <div key={card.tabId} className={`rounded-xl border ${card.border} ${card.bg} p-3.5 transition-all`}>
                      <div
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => setTab(card.tabId)}
                      >
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <card.icon className={`w-4 h-4 ${card.color}`} />
                            <span className={`text-2xl font-black ${card.color}`}>{card.value}</span>
                          </div>
                          <p className={`text-[10px] font-bold ${card.color}`}>{card.label}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{card.sub}</p>
                        </div>
                        <ChevronRight className={`w-3.5 h-3.5 ${card.color} opacity-50 mt-0.5 shrink-0`} />
                      </div>
                      <button
                        onClick={card.btnAction}
                        className={`mt-2.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${card.btnColor}`}
                      >
                        <Plus className="w-2.5 h-2.5" /> {card.btn}
                      </button>
                    </div>
                  ))}
                </div>

                {/* PDF report shortcut when samples exist */}
                {stats.influent.length > 0 && (
                  <button
                    onClick={() => {
                      const s = stats.influent[0];
                      generateTrendReportPDF({
                        id: String(s.id),
                        reportNumber: s.reportNumber || `RB-${s.id}`,
                        sampleName: s.sampleName,
                        customerName: customer.name,
                        customerCompany: customer.company,
                        standard: customer.standard || 'UK_EA',
                        date: new Date(s.createdAt),
                        preparerName: 'Reynolds & Bauhm',
                        preparerCompany: 'Reynolds & Bauhm Ltd',
                        params: s.params,
                      });
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[11px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all w-full justify-center"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export PDF Report — {stats.influent[0].sampleName}
                  </button>
                )}

                {/* Regulatory compliance overview */}
                <RegulatoryOverview customer={customer} onEditRegulatory={() => setEditRegulatoryOpen(true)} />

                {/* SA Water excess concentration charges — inline calculator */}
                {stdCode === 'SA_WATER' && (
                  <SAWaterChargesPanel
                    samples={stats.effluent}
                    onViewFullPage={() => navigate('/sa-water-charges')}
                    customer={customer}
                  />
                )}

                {/* Contact info */}
                <div className="overflow-hidden rb-card">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800/60 bg-gradient-to-r from-slate-800/20 to-transparent">
                    <Users className="w-3.5 h-3.5 text-slate-500" />
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Contact &amp; Profile</p>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Row 1: core contact */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: Users,      label: 'Contact Name', val: customer.name },
                        { icon: Mail,       label: 'Email',        val: customer.contactEmail || '—' },
                        { icon: Phone,      label: 'Phone',        val: customer.phone || '—' },
                        { icon: Globe,      label: 'Country',      val: customer.country ? `${COUNTRY_FLAGS[customer.country]} ${COUNTRY_NAMES[customer.country]}` : '—' },
                      ].map(({ icon: Icon, label, val }) => (
                        <div key={label}>
                          <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Icon className="w-2.5 h-2.5" />{label}</p>
                          <p className="text-[11px] text-slate-300 truncate">{val}</p>
                        </div>
                      ))}
                      {/* Address spans full width */}
                      <div className="col-span-2">
                        <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />Address</p>
                        <p className="text-[11px] text-slate-300">
                          {[customer.address, customer.city, customer.postcode].filter(Boolean).join(', ') || '—'}
                        </p>
                      </div>
                    </div>

                    {/* Row 2: industry & operations (only show if any are set) */}
                    {(customer.industryType || customer.industrySubsector || customer.sicCode || customer.employeeCount || customer.avgFlowRate || customer.peakFlowRate) && (
                      <>
                        <div className="border-t border-slate-800/60 pt-3">
                          <p className="text-[10px] font-bold text-amber-500/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Factory className="w-2.5 h-2.5" /> Industry &amp; Operations
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            {customer.industryType && (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5">Sector</p>
                                <p className="text-[11px] text-slate-300">{customer.industryType}</p>
                              </div>
                            )}
                            {customer.industrySubsector && (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5">Subsector</p>
                                <p className="text-[11px] text-slate-300">{customer.industrySubsector}</p>
                              </div>
                            )}
                            {customer.sicCode && (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Hash className="w-2.5 h-2.5" />SIC Code</p>
                                <p className="text-[11px] text-slate-300 font-mono">{customer.sicCode}</p>
                              </div>
                            )}
                            {customer.employeeCount && (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Users className="w-2.5 h-2.5" />Employees</p>
                                <p className="text-[11px] text-slate-300">{customer.employeeCount.toLocaleString()}</p>
                              </div>
                            )}
                            {customer.operatingHours && (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />Hours</p>
                                <p className="text-[11px] text-slate-300">{customer.operatingHours}</p>
                              </div>
                            )}
                            {customer.productionVolume && (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Layers className="w-2.5 h-2.5" />Production</p>
                                <p className="text-[11px] text-slate-300">{customer.productionVolume}</p>
                              </div>
                            )}
                            {(customer.avgFlowRate || customer.peakFlowRate) && (
                              <div className="col-span-2 grid grid-cols-2 gap-3">
                                {customer.avgFlowRate && (
                                  <div className="rounded-lg bg-cyan-500/5 border border-cyan-500/15 p-2 text-center">
                                    <p className="text-[10px] text-slate-600 uppercase font-bold">Avg Flow</p>
                                    <p className="text-sm font-black text-cyan-400">{customer.avgFlowRate.toLocaleString()}</p>
                                    <p className="text-[10px] text-slate-600">m³/day</p>
                                  </div>
                                )}
                                {customer.peakFlowRate && (
                                  <div className="rounded-lg bg-amber-500/5 border border-amber-500/15 p-2 text-center">
                                    <p className="text-[10px] text-slate-600 uppercase font-bold">Peak Flow</p>
                                    <p className="text-sm font-black text-amber-400">{customer.peakFlowRate.toLocaleString()}</p>
                                    <p className="text-[10px] text-slate-600">m³/day</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Row 3: consent / permit (only show if set) */}
                    {(customer.consentNumber || customer.consentExpiryDate || customer.consentVolumeLimit || customer.regulatoryBody) && (
                      <div className="border-t border-slate-800/60 pt-3">
                        <p className="text-[10px] font-bold text-rb-brand/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ShieldCheck className="w-2.5 h-2.5" /> Consent &amp; Permit
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {customer.regulatoryBody && (
                            <div className="col-span-2">
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5">Regulatory Body</p>
                              <p className="text-[11px] text-slate-300">{customer.regulatoryBody}</p>
                            </div>
                          )}
                          {customer.consentNumber && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><FileText className="w-2.5 h-2.5" />Consent Ref</p>
                              <p className="text-[11px] text-slate-300 font-mono">{customer.consentNumber}</p>
                            </div>
                          )}
                          {customer.consentVolumeLimit && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Droplets className="w-2.5 h-2.5" />Volume Limit</p>
                              <p className="text-[11px] text-slate-300">{customer.consentVolumeLimit.toLocaleString()} m³/day</p>
                            </div>
                          )}
                          {customer.consentIssuedDate && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Issued</p>
                              <p className="text-[11px] text-slate-300">{new Date(customer.consentIssuedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                            </div>
                          )}
                          {customer.consentExpiryDate && (() => {
                            const expiry = new Date(customer.consentExpiryDate);
                            const daysLeft = Math.ceil((expiry.getTime() - Date.now()) / 86400000);
                            const isExpired = daysLeft < 0;
                            const isWarning = !isExpired && daysLeft < 90;
                            return (
                              <div>
                                <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Expiry</p>
                                <p className={`text-[11px] font-bold ${isExpired ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-slate-300'}`}>
                                  {expiry.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  {isExpired && ' · EXPIRED'}
                                  {isWarning && !isExpired && ` · ${daysLeft}d`}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Row 4: billing & admin (only show if set) */}
                    {(customer.accountsEmail || customer.environmentalContact || customer.wasteCarrierReg) && (
                      <div className="border-t border-slate-800/60 pt-3">
                        <p className="text-[10px] font-bold text-indigo-400/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Building className="w-2.5 h-2.5" /> Administration
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {customer.accountsEmail && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />Accounts Email</p>
                              <p className="text-[11px] text-slate-300 truncate">{customer.accountsEmail}</p>
                            </div>
                          )}
                          {customer.wasteCarrierReg && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Hash className="w-2.5 h-2.5" />Waste Carrier Reg</p>
                              <p className="text-[11px] text-slate-300 font-mono">{customer.wasteCarrierReg}</p>
                            </div>
                          )}
                          {customer.environmentalContact && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Users className="w-2.5 h-2.5" />Env. Contact</p>
                              <p className="text-[11px] text-slate-300">{customer.environmentalContact}</p>
                            </div>
                          )}
                          {customer.environmentalPhone && (
                            <div>
                              <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />Env. Phone</p>
                              <p className="text-[11px] text-slate-300">{customer.environmentalPhone}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Row 5: discharge + registered */}
                    <div className="border-t border-slate-800/60 pt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><ArrowRight className="w-2.5 h-2.5" />Discharge</p>
                          <p className="text-[11px] text-slate-300">{customer.dischargeRoute ? (DISCHARGE_ROUTES.find(r => r.value === customer.dischargeRoute)?.label || customer.dischargeRoute) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Registered</p>
                          <p className="text-[11px] text-slate-300">{new Date(customer.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        </div>
                      </div>
                    </div>

                    {customer.notes && (
                      <div className="border-t border-slate-800/60 pt-3">
                        <p className="text-[10px] text-slate-600 uppercase font-bold mb-0.5 flex items-center gap-1"><Info className="w-2.5 h-2.5" />Notes</p>
                        <p className="text-[11px] text-slate-400 leading-relaxed">{customer.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recent activity */}
                {(() => {
                  type ActivityItem = {
                    type: string; label: string; sub: string; date: string;
                    icon: React.ElementType; color: string; route: string;
                    activateFirst?: boolean;
                  };
                  const items: ActivityItem[] = [
                    ...stats.influent.map(s => ({
                      type: 'influent', label: s.sampleName, sub: s.reportNumber || `#${s.id}`,
                      date: s.createdAt, icon: Droplets, color: 'text-cyan-400',
                      route: '/influent-entry', activateFirst: true,
                    })),
                    ...stats.effluent.map(s => ({
                      type: 'effluent', label: s.sampleName, sub: s.reportNumber || `#${s.id}`,
                      date: s.createdAt, icon: Beaker, color: 'text-rb-brand',
                      route: '/effluent-entry', activateFirst: true,
                    })),
                    ...stats.trains.map(t => ({
                      type: 'train', label: t.name, sub: `${t.status} · ${t.equipmentList.length} units`,
                      date: t.createdAt, icon: Workflow, color: 'text-amber-400',
                      route: '/process-db', activateFirst: true,
                    })),
                    ...stats.proposals.map(p => ({
                      type: 'proposal', label: p.title, sub: p.proposalNumber,
                      date: p.createdAt, icon: FileText, color: 'text-violet-400',
                      route: '/proposals', activateFirst: true,
                    })),
                  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8);

                  const typeLabel: Record<string, string> = {
                    influent: 'Influent', effluent: 'Effluent', train: 'Process Train', proposal: 'Proposal',
                  };

                  if (items.length === 0) return (
                    <div className="p-6 text-center rb-card">
                      <Activity className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-xs text-slate-500">No activity yet for this customer</p>
                      <p className="text-[10px] text-slate-600 mt-1">Set as active, then add an influent sample to get started</p>
                      <button onClick={() => onActivateAndGo('/influent-entry')}
                        className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 mx-auto">
                        <Plus className="w-3 h-3" /> Add First Sample
                      </button>
                    </div>
                  );

                  return (
                    <div className="overflow-hidden rb-card">
                      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800/60">
                        <Activity className="w-3.5 h-3.5 text-slate-500" />
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Recent Activity</p>
                        <span className="text-[9px] text-slate-600 ml-auto">Click to open</span>
                      </div>
                      <div className="divide-y divide-slate-800/60">
                        {items.map((item, i) => (
                          <button
                            key={i}
                            onClick={() => item.activateFirst ? onActivateAndGo(item.route) : navigate(item.route)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800/30 transition-colors text-left group"
                          >
                            <item.icon className={`w-3.5 h-3.5 shrink-0 ${item.color}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-semibold text-slate-300 truncate group-hover:text-white transition-colors">{item.label}</p>
                              <p className="text-[10px] text-slate-600 font-mono truncate">{item.sub}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-[8px] px-1.5 py-0.5 rounded-full border font-bold hidden group-hover:inline-flex ${
                                item.type === 'influent' ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20' :
                                item.type === 'effluent' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                                item.type === 'train' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                'bg-violet-500/10 text-violet-400 border-violet-500/20'
                              }`}>{typeLabel[item.type]}</span>
                              <p className="text-[10px] text-slate-600">{new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                              <ChevronRight className="w-3 h-3 text-slate-400 group-hover:text-slate-400 transition-colors" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* ═══ SITES ═══ */}
            {tab === 'sites' && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-slate-200">{sites.length} Site{sites.length !== 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-slate-500">Physical discharge locations — permit references, state/region auto-detected from postcode</p>
                  </div>
                  <button onClick={() => setAddSiteOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold bg-teal-500/10 text-teal-400 border border-teal-500/20 hover:bg-teal-500/20 active:scale-95 transition-all shrink-0">
                    <Plus className="w-3.5 h-3.5" /> Add Site
                  </button>
                </div>

                {sites.length === 0 ? (
                  <div className="text-center py-10 rb-card">
                    <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mx-auto mb-3">
                      <MapPin className="w-6 h-6 text-teal-500/40" />
                    </div>
                    <p className="text-sm font-bold text-slate-400">No sites added yet</p>
                    <p className="text-[11px] text-slate-600 mt-1 mb-4 max-w-xs mx-auto">Add a physical site / discharge location to link samples and permits to a specific place</p>
                    <button onClick={() => setAddSiteOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold bg-teal-500/10 text-teal-400 border border-teal-500/20 hover:bg-teal-500/20 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Add First Site
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[10px] text-slate-600 mb-2 flex items-center gap-1">
                      <ChevronDown className="w-3 h-3" /> Click any site card to expand full details
                    </p>
                    {sites.map(site => {
                      const siteInfluent = stats.influent.filter(s => s.siteId === site.id);
                      const siteEffluent = stats.effluent.filter(s => s.siteId === site.id);
                      const lastInf = siteInfluent[0]?.createdAt;
                      const lastEff = siteEffluent[0]?.createdAt;
                      return (
                        <SiteCard
                          key={site.id}
                          site={site}
                          customer={customer}
                          influentCount={siteInfluent.length}
                          effluentCount={siteEffluent.length}
                          effluentSamples={siteEffluent}
                          lastInfluentDate={lastInf}
                          lastEffluentDate={lastEff}
                          onUpdate={(updated) => {
                            setSites(prev => prev.map(s => s.id === site.id ? { ...s, ...updated } : s));
                          }}
                          onDelete={() => {
                            deleteLocalSite(site.id);
                            setSites(prev => prev.filter(s => s.id !== site.id));
                            // API sync
                            if (hasAuthToken() && site.serverId) {
                              apiDeleteCustomerSite(site.serverId).catch(() => {});
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                )}

                {addSiteOpen && (
                  <AddSiteModal
                    customer={customer}
                    onClose={() => setAddSiteOpen(false)}
                    onSaved={(newSite) => {
                      setSites(prev => [newSite, ...prev]);
                      setAddSiteOpen(false);
                    }}
                  />
                )}
              </>
            )}

            {/* ═══ INFLUENT SAMPLES ═══ */}
            {tab === 'influent' && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-slate-200">{stats.influent.length} Influent Sample{stats.influent.length !== 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-slate-500">Raw wastewater entering treatment — monitoring completeness records</p>
                  </div>
                  <button onClick={() => onActivateAndGo('/influent-entry')}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 active:scale-95 transition-all shrink-0">
                    <Plus className="w-3.5 h-3.5" /> Add Influent Sample
                  </button>
                </div>

                {stats.influent.length === 0 ? (
                  <div className="rounded-xl border py-10 text-center rb-blink-amber"
                    style={{ background: 'rgba(245,158,11,0.03)' }}>
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mx-auto mb-3">
                      <Droplets className="w-6 h-6 text-amber-500/50" />
                    </div>
                    <p className="text-sm font-bold text-amber-400">No influent samples recorded</p>
                    <p className="text-[11px] text-amber-400 mt-1 mb-1 max-w-xs mx-auto leading-snug">
                      Influent (raw wastewater) samples have not been added for this customer yet.
                    </p>
                    <p className="text-[10px] text-slate-600 mb-4 max-w-xs mx-auto">
                      Add an influent sample to start monitoring incoming wastewater quality and track treatment performance.
                    </p>
                    <button onClick={() => onActivateAndGo('/influent-entry')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/25 hover:bg-amber-500/20 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Add First Influent Sample
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {stats.influent.map(s => {
                      const visibleParams = CORE.filter(k => s.params[k] != null).slice(0, 8);
                      const extraCount = Object.keys(s.params).length - visibleParams.length;
                      const isExpanded = expandedSampleId === s.id;
                      // Quick compliance snapshot
                      const cChecked = Object.keys(s.params).filter(k => PARAM_TO_STD[k]);
                      const cFailing = cChecked.filter(k => {
                        const r = checkCompliance(k, s.params[k], stdCode);
                        return !r.pass && r.limit !== null;
                      });
                      const hasComplianceData = cChecked.length > 0;
                      return (
                        <div key={s.id} className={`bg-rb-surface rounded-xl border transition-all ${isExpanded ? 'border-cyan-500/30' : 'border-slate-800/80 hover:border-slate-700'}`}>
                          <div className="p-3.5">
                            {/* Sample header */}
                            <div className="flex items-start justify-between gap-2 mb-2.5">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shrink-0">
                                  <Droplets className="w-4 h-4 text-cyan-400" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[13px] font-bold text-slate-200 truncate">{s.sampleName}</p>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <span className="text-[10px] font-mono text-slate-600">{s.reportNumber || `ID:${s.id}`}</span>
                                    <span className="text-[10px] text-slate-600">·</span>
                                    <span className="text-[10px] text-slate-500">{new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                    <span className={`text-[10px] font-black px-1.5 py-0 rounded border ${sc.bg} ${sc.border} ${sc.color} leading-4`}>{s.standard || 'UK_EA'}</span>
                                    {s.siteId && (() => { const site = sites.find(st => st.id === s.siteId); return site ? <span className="flex items-center gap-0.5 text-[10px] text-teal-400 font-bold"><MapPin className="w-2 h-2" />{site.name}</span> : null; })()}
                                  </div>
                                </div>
                              </div>
                              {/* Inline compliance snapshot */}
                              <div className="flex items-center gap-1.5 shrink-0">
                                {hasComplianceData && (
                                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${
                                    cFailing.length === 0
                                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                      : cFailing.length <= 2
                                        ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        : 'bg-red-500/10 text-red-400 border-red-500/20'
                                  }`}>
                                    {cFailing.length === 0
                                      ? <><CheckCircle2 className="w-2.5 h-2.5" /> All clear</>
                                      : <><AlertTriangle className="w-2.5 h-2.5" /> {cFailing.length} flag{cFailing.length > 1 ? 's' : ''}</>
                                    }
                                  </span>
                                )}
                                <button
                                  onClick={() => setExpandedSampleId(isExpanded ? null : s.id)}
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                                    isExpanded
                                      ? 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30'
                                      : 'bg-slate-800/60 text-slate-400 border-slate-700 hover:text-cyan-400 hover:border-cyan-500/30'
                                  }`}
                                >
                                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  {isExpanded ? 'Collapse' : 'Details'}
                                </button>
                              </div>
                            </div>
                            {/* Param chips */}
                            <div className="flex flex-wrap gap-1">
                              {visibleParams.map(k => {
                                const v = s.params[k];
                                const color = paramColor(k, v);
                                return (
                                  <div key={k} className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-slate-800/70 border border-slate-800/60">
                                    <span className="text-[10px] text-slate-500 font-bold uppercase">{PARAM_LABEL[k] || k}</span>
                                    <span className="text-[10px] font-black tabular-nums ml-1" style={{ color }}>{v == null ? '—' : Number.isInteger(v) ? v : Number(v).toFixed(1)}</span>
                                  </div>
                                );
                              })}
                              {extraCount > 0 && (
                                <div className="flex items-center px-2 py-1 rounded-lg bg-slate-800/40 border border-slate-800/40">
                                  <span className="text-[10px] text-slate-600 font-bold">+{extraCount} more</span>
                                </div>
                              )}
                              <div className="flex items-center px-2 py-1 rounded-lg bg-slate-800/30 border border-slate-800/30">
                                <span className="text-[10px] text-slate-600">{Object.keys(s.params).length} total params</span>
                              </div>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-3.5 pb-3.5">
                              <SampleDetailView sample={s} stdCode={stdCode} customer={customer} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ═══ EFFLUENT SAMPLES ═══ */}
            {tab === 'effluent' && (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-slate-200">{stats.effluent.length} Effluent Sample{stats.effluent.length !== 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-slate-500">Treated discharge — regulatory compliance analysis</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => onActivateAndGo('/effluent-entry')}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[11px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 active:scale-95 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Add Effluent
                    </button>
                    <button onClick={() => navigate('/effluent')}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all">
                      <Shield className="w-3.5 h-3.5" /> Discharge Gates
                    </button>
                  </div>
                </div>

                {stats.effluent.length === 0 ? (
                  <div className="text-center py-10 rb-card">
                    <div className="w-12 h-12 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center mx-auto mb-3">
                      <Beaker className="w-6 h-6 text-rb-brand/40" />
                    </div>
                    <p className="text-sm font-bold text-slate-400">No effluent samples yet</p>
                    <p className="text-[11px] text-slate-600 mt-1 mb-4 max-w-xs mx-auto">Record treated effluent to verify discharge compliance against regulatory limits</p>
                    <button onClick={() => onActivateAndGo('/effluent-entry')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Add First Effluent Sample
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {stats.effluent.map(s => {
                      const visibleParams = CORE.filter(k => s.params[k] != null).slice(0, 8);
                      const extraCount = Object.keys(s.params).length - visibleParams.length;
                      const isExpanded = expandedSampleId === s.id;
                      // Inline compliance check
                      const cChecked = Object.keys(s.params).filter(k => PARAM_TO_STD[k]);
                      const cFailing = cChecked.filter(k => {
                        const r = checkCompliance(k, s.params[k], stdCode);
                        return !r.pass && r.limit !== null;
                      });
                      const hasComplianceData = cChecked.length > 0;
                      return (
                        <div key={s.id} className={`bg-rb-surface rounded-xl border transition-all ${isExpanded ? 'border-rb-brand/35' : 'border-slate-800/80 hover:border-slate-700'}`}>
                          {/* Compliance bar at top */}
                          {hasComplianceData && (() => {
                            if (stdCode === 'SA_WATER') {
                              // SA Water: show charge risk info instead of generic compliance
                              const SAW_LIMITS: Record<string, { limit: number; label: string }> = {
                                bod5: { limit: 3000, label: 'BOD₅' }, bod: { limit: 3000, label: 'BOD₅' },
                                tss: { limit: 500, label: 'SS' }, ss: { limit: 500, label: 'SS' },
                                tds: { limit: 1500, label: 'TDS' },
                                tn: { limit: 130, label: 'TKN' }, tkn: { limit: 130, label: 'TKN' },
                                totalphosphorus: { limit: 15, label: 'TP' }, tp: { limit: 15, label: 'TP' },
                              };
                              const SAW_THRESH: Record<string, number> = {
                                bod5: 1000, bod: 1000, tss: 500, ss: 500,
                              };
                              const paramKeys = Object.keys(s.params).map(k => k.toLowerCase());
                              const chargeableParams = paramKeys.filter(k => {
                                const def = SAW_LIMITS[k];
                                if (!def) return false;
                                const val = (s.params as any)[Object.keys(s.params).find(pk => pk.toLowerCase() === k)!];
                                return typeof val === 'number' && val > 0;
                              }).map(k => SAW_LIMITS[k].label);
                              const uniqueChargeable = [...new Set(chargeableParams)];
                              const highRateParams = paramKeys.filter(k => {
                                const thresh = SAW_THRESH[k];
                                if (!thresh) return false;
                                const val = (s.params as any)[Object.keys(s.params).find(pk => pk.toLowerCase() === k)!];
                                return typeof val === 'number' && val > thresh;
                              }).map(k => SAW_THRESH[k] ? SAW_LIMITS[k]?.label : null).filter(Boolean);
                              const permitExceed = paramKeys.filter(k => {
                                const def = SAW_LIMITS[k];
                                if (!def) return false;
                                const val = (s.params as any)[Object.keys(s.params).find(pk => pk.toLowerCase() === k)!];
                                return typeof val === 'number' && val > def.limit;
                              }).map(k => SAW_LIMITS[k].label);
                              const uniqueExceed = [...new Set(permitExceed)];
                              return (
                                <div className={`flex items-center gap-2 px-3.5 py-1.5 border-b text-[10px] font-bold rounded-t-xl ${
                                  uniqueExceed.length > 0
                                    ? 'bg-red-500/8 border-red-500/20 text-red-400'
                                    : highRateParams.length > 0
                                      ? 'bg-amber-500/8 border-amber-500/20 text-amber-400'
                                      : 'bg-teal-500/5 border-teal-500/15 text-teal-400'
                                }`}>
                                  {uniqueExceed.length > 0
                                    ? <AlertTriangle className="w-3 h-3 shrink-0" />
                                    : highRateParams.length > 0
                                      ? <DollarSign className="w-3 h-3 shrink-0" />
                                      : <CheckCircle2 className="w-3 h-3 shrink-0" />
                                  }
                                  {uniqueExceed.length > 0
                                    ? `Permit limit exceeded: ${uniqueExceed.join(', ')} — check SA Water`
                                    : highRateParams.length > 0
                                      ? `High-rate charge applies: ${[...new Set(highRateParams)].join(', ')} — above SA Water threshold`
                                      : uniqueChargeable.length > 0
                                        ? `SA Water chargeable params: ${uniqueChargeable.join(', ')}`
                                        : 'Within SA Water permit limits'
                                  }
                                  <span className="ml-auto text-slate-600 font-normal shrink-0">SA Water</span>
                                </div>
                              );
                            }
                            return (
                              <div className={`flex items-center gap-2 px-3.5 py-1.5 border-b text-[10px] font-bold rounded-t-xl ${
                                cFailing.length === 0
                                  ? 'bg-emerald-500/5 border-emerald-500/15 text-emerald-400'
                                  : cFailing.length <= 2
                                    ? 'bg-amber-500/5 border-amber-500/15 text-amber-400'
                                    : 'bg-red-500/5 border-red-500/15 text-red-400'
                              }`}>
                                {cFailing.length === 0
                                  ? <><CheckCircle2 className="w-3 h-3 shrink-0" /> All discharge limits met</>
                                  : <><AlertTriangle className="w-3 h-3 shrink-0" /> {cFailing.length} parameter{cFailing.length > 1 ? 's' : ''} exceeding discharge limit</>
                                }
                                <span className="ml-auto text-slate-600 font-normal">{cChecked.length} checked</span>
                              </div>
                            );
                          })()}
                          <div className="p-3.5">
                            <div className="flex items-start justify-between gap-2 mb-2.5">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center shrink-0">
                                  <Beaker className="w-4 h-4 text-rb-brand" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[13px] font-bold text-slate-200 truncate">{s.sampleName}</p>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <span className="text-[10px] font-mono text-slate-600">{s.reportNumber || `ID:${s.id}`}</span>
                                    <span className="text-[10px] text-slate-600">·</span>
                                    <span className="text-[10px] text-slate-500">{new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                    {s.linkedSampleId && (
                                      <span className="flex items-center gap-0.5 text-[10px] text-cyan-400 font-bold">
                                        <Link2 className="w-2.5 h-2.5" /> Linked #{s.linkedSampleId}
                                      </span>
                                    )}
                                    {s.siteId && (() => { const site = sites.find(st => st.id === s.siteId); return site ? <span className="flex items-center gap-0.5 text-[10px] text-teal-400 font-bold"><MapPin className="w-2 h-2" />{site.name}</span> : null; })()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  onClick={() => handleEffluentPreview(s)}
                                  title="Preview regulatory discharge compliance PDF"
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border bg-rb-active/60 text-rb-brand border-rb-brand/30 hover:bg-rb-active transition-all"
                                >
                                  <Eye className="w-3 h-3" /> PDF
                                </button>
                                <button
                                  onClick={() => setExpandedSampleId(isExpanded ? null : s.id)}
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border shrink-0 transition-all ${
                                    isExpanded
                                      ? 'bg-rb-brand/15 text-rb-brand border-rb-brand/30'
                                      : 'bg-slate-800/60 text-slate-400 border-slate-700 hover:text-rb-brand hover:border-rb-brand/30'
                                  }`}
                                >
                                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  {isExpanded ? 'Collapse' : 'Details'}
                                </button>
                              </div>
                            </div>
                            {/* Param chips */}
                            <div className="flex flex-wrap gap-1">
                              {visibleParams.map(k => {
                                const v = s.params[k];
                                // For effluent, use compliance color
                                const compl = checkCompliance(k, v, stdCode);
                                const color = compl.limit !== null
                                  ? compl.pass ? (compl.pct >= 75 ? '#f59e0b' : '#10b981') : '#ef4444'
                                  : paramColor(k, v);
                                return (
                                  <div key={k} className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-slate-800/70 border border-slate-800/60">
                                    <span className="text-[10px] text-slate-500 font-bold uppercase">{PARAM_LABEL[k] || k}</span>
                                    <span className="text-[10px] font-black tabular-nums ml-1" style={{ color }}>{v == null ? '—' : Number.isInteger(v) ? v : Number(v).toFixed(1)}</span>
                                  </div>
                                );
                              })}
                              {extraCount > 0 && (
                                <div className="flex items-center px-2 py-1 rounded-lg bg-slate-800/40 border border-slate-800/40">
                                  <span className="text-[10px] text-slate-600 font-bold">+{extraCount} more</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-3.5 pb-3.5">
                              <SampleDetailView sample={s} stdCode={stdCode} customer={customer} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ═══ PROCESS TRAINS ═══ */}
            {tab === 'trains' && (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-slate-200">{stats.trains.length} Process Train{stats.trains.length !== 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-slate-500">Equipment sequences for treatment design & sizing</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => navigate('/process-train')}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[11px] font-bold bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all">
                      <Eye className="w-3.5 h-3.5" /> View Trains
                    </button>
                    <button onClick={() => onActivateAndGo('/process-db')}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 active:scale-95 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Build New Train
                    </button>
                  </div>
                </div>

                {stats.trains.length === 0 ? (
                  <div className="text-center py-10 rb-card">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-3">
                      <Workflow className="w-6 h-6 text-amber-500/40" />
                    </div>
                    <p className="text-sm font-bold text-slate-400">No process trains yet</p>
                    <p className="text-[11px] text-slate-600 mt-1 mb-4 max-w-xs mx-auto">Build a treatment equipment sequence based on this customer's influent data</p>
                    <button onClick={() => onActivateAndGo('/process-db')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Build First Train
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.trains.map(t => {
                      const sc2 = TRAIN_STATUS[t.status] || TRAIN_STATUS.draft;
                      const eqList: any[] = t.equipmentList || [];
                      const isExpanded = expandedTrainId === String(t.id);
                      const result: any = (t as any).assessmentResult;
                      return (
                        <div key={t.id} className={`bg-rb-surface rounded-xl border transition-all ${isExpanded ? 'border-amber-500/30' : 'border-slate-800/80 hover:border-slate-700'}`}>
                          <div className="p-3.5">
                            {/* Train header */}
                            <div className="flex items-start justify-between gap-2 mb-2.5">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${sc2.bg} border ${sc2.border}`}>
                                  {t.status === 'assessed'
                                    ? <CheckCircle2 className={`w-4 h-4 ${sc2.color}`} />
                                    : t.status === 'in_progress'
                                      ? <Clock className={`w-4 h-4 ${sc2.color}`} />
                                      : <Workflow className={`w-4 h-4 ${sc2.color}`} />
                                  }
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[13px] font-bold text-slate-200 truncate">{t.name}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`px-1.5 py-0 rounded text-[10px] font-black border leading-4 ${sc2.bg} ${sc2.border} ${sc2.color}`}>{sc2.label}</span>
                                    <span className="text-[10px] text-slate-600">{eqList.length} unit{eqList.length !== 1 ? 's' : ''}</span>
                                    <span className="text-[10px] text-slate-600">·</span>
                                    <span className="text-[10px] text-slate-500">{new Date(t.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  onClick={() => {
                                    const custId = (customer as any).serverId ?? '';
                                    // Single editor for every train — TrainEditPage now models the
                                    // wastewater units too, with a live influent→effluent cascade.
                                    navigate(`/train-edit?id=${t.id}&customer_id=${custId}`);
                                  }}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20 active:scale-95 transition-all"
                                  title="Open in the Train Editor — adjust equipment with a live per-stage cascade. Discharge compliance is checked on the Process Train page."
                                >
                                  <Workflow className="w-3 h-3" /> Open Train
                                </button>
                                <button
                                  onClick={() => setExpandedTrainId(isExpanded ? null : String(t.id))}
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                                    isExpanded
                                      ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                                      : 'bg-slate-800/60 text-slate-400 border-slate-700 hover:text-amber-400 hover:border-amber-500/30'
                                  }`}
                                >
                                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  {isExpanded ? 'Collapse' : 'Details'}
                                </button>
                              </div>
                            </div>

                            {/* Description */}
                            {t.description && (
                              <p className="text-[10px] text-slate-500 mb-2 italic">{t.description}</p>
                            )}

                            {/* Equipment pipeline chips */}
                            {eqList.length > 0 && (
                              <div className="flex items-center gap-1 flex-wrap">
                                {eqList.map((eq: any, i: number) => {
                                  const cat = (eq.category || 'default').toLowerCase();
                                  const cls = CATEGORY_COLOR[cat] || CATEGORY_COLOR.default;
                                  return (
                                    <span key={i} className="flex items-center gap-0.5">
                                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${cls}`}>
                                        {eq.shortLabel || eq.label || eq.key}
                                      </span>
                                      {i < eqList.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-slate-400 shrink-0" />}
                                    </span>
                                  );
                                })}
                              </div>
                            )}

                            {/* Assessment result snippet */}
                            {result && (result.removalEfficiency !== undefined || result.codRemoval !== undefined) && (
                              <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-slate-800/60">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Assessment:</span>
                                {result.removalEfficiency !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold border border-emerald-500/20">
                                    {result.removalEfficiency}% removal eff.
                                  </span>
                                )}
                                {result.codRemoval !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 text-[10px] font-bold border border-cyan-500/20">
                                    COD -{result.codRemoval}%
                                  </span>
                                )}
                                {result.bodRemoval !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-sky-500/10 text-sky-400 text-[10px] font-bold border border-sky-500/20">
                                    BOD -{result.bodRemoval}%
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {isExpanded && (
                            <div className="px-3.5 pb-3.5">
                              <TrainDetailView train={t} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ═══ PROPOSALS ═══ */}
            {tab === 'proposals' && (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-slate-200">
                      {stats.proposals.length} Proposal{stats.proposals.length !== 1 ? 's' : ''}
                      {stats.proposals.filter(p => p.status === 'validated').length > 0 && (
                        <span className="ml-2 text-[11px] text-emerald-400 font-normal">
                          · {stats.proposals.filter(p => p.status === 'validated').length} validated
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-slate-500">Treatment proposals with linked samples and process trains</p>
                  </div>
                  <button onClick={() => onActivateAndGo('/proposals')}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[11px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 active:scale-95 transition-all shrink-0">
                    <FileText className="w-3.5 h-3.5" /> All Proposals
                  </button>
                </div>

                {stats.proposals.length === 0 ? (
                  <div className="text-center py-10 rb-card">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-3">
                      <FileText className="w-6 h-6 text-violet-500/40" />
                    </div>
                    <p className="text-sm font-bold text-slate-400">No proposals yet</p>
                    <p className="text-[11px] text-slate-600 mt-1 mb-4 max-w-xs mx-auto">Create a formal treatment proposal linking samples, process trains and equipment specifications</p>
                    <button onClick={() => onActivateAndGo('/proposals')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 transition-all">
                      <Plus className="w-3.5 h-3.5" /> Create First Proposal
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.proposals.map(p => {
                      const psc = PROPOSAL_STATUS[p.status] || PROPOSAL_STATUS.draft;
                      const isPreviewing = previewProposalId === p.id;
                      // Get trains linked to this proposal
                      const linkedTrains = stats.trains.filter(t =>
                        p.processTrainIds?.includes(String(t.id))
                      );
                      // Get samples linked to this proposal
                      const linkedInfluent = stats.influent.filter(s =>
                        p.sampleIds?.includes(String(s.id))
                      );
                      const linkedEffluent = stats.effluent.filter(s =>
                        p.sampleIds?.includes(String(s.id))
                      );
                      const standardLabel = p.countryCode
                        ? (COUNTRY_STANDARD[p.countryCode] || 'ISO')
                        : (COUNTRY_STANDARD[customer.country || 'GB'] || 'ISO');

                      return (
                        <div key={p.id} className="rounded-xl border border-slate-800/80 hover:border-violet-500/30 transition-all overflow-hidden">
                          {/* ── Card header bar ── */}
                          <div className="bg-rb-surface flex items-center justify-between px-3.5 py-2 border-b border-slate-800/60">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-[11px] font-black text-violet-300">{p.proposalNumber}</span>
                              <span className={`px-1.5 py-0 rounded text-[10px] font-black border leading-4 flex items-center gap-0.5 ${psc.bg} ${psc.border} ${psc.color}`}>
                                {p.isImmutable && <Lock className="w-2 h-2" />}
                                {(p.status || 'draft').toUpperCase()}
                              </span>
                              {p.revisionNumber > 0 && (
                                <span className="px-1.5 py-0 rounded bg-sky-500/10 text-sky-400 text-[10px] font-bold border border-sky-500/15 leading-4">Rev {p.revisionNumber}</span>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-600 shrink-0">{new Date(p.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>

                          {/* ── Card body ── */}
                          <div className="bg-rb-surface p-3.5">
                            <p className="text-[13px] font-bold text-slate-200 mb-0.5">{p.title}</p>
                            {p.description && !isPreviewing && (
                              <p className="text-[10px] text-slate-500 line-clamp-2 mb-2">{p.description.split('\n')[0]}</p>
                            )}

                            {/* Stats + action strip */}
                            <div className="flex items-center gap-3 flex-wrap mt-2">
                              {linkedTrains.length > 0 && (
                                <span className="flex items-center gap-1 text-[10px] text-amber-400 font-bold">
                                  <Workflow className="w-3 h-3" />{linkedTrains.length} train{linkedTrains.length !== 1 ? 's' : ''}
                                </span>
                              )}
                              {(p.sampleIds?.length ?? 0) > 0 && (
                                <span className="flex items-center gap-1 text-[10px] text-sky-400 font-bold">
                                  <FlaskConical className="w-3 h-3" />{p.sampleIds!.length} sample{p.sampleIds!.length !== 1 ? 's' : ''}
                                </span>
                              )}
                              {p.countryCode && (
                                <span className="text-[10px] text-slate-600 flex items-center gap-1">
                                  {COUNTRY_FLAGS[p.countryCode] || <Globe className="w-2.5 h-2.5" />} {standardLabel}
                                </span>
                              )}
                              <div className="ml-auto flex items-center gap-1.5">
                                <button
                                  onClick={() => setPreviewProposalId(isPreviewing ? null : p.id)}
                                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border active:scale-95 transition-all ${
                                    isPreviewing
                                      ? 'bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25'
                                      : 'bg-slate-700/40 text-slate-300 border-slate-800/60 hover:bg-slate-700/70'
                                  }`}
                                >
                                  {isPreviewing ? <ChevronUp className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  {isPreviewing ? 'Hide' : 'Preview'}
                                </button>
                                <button
                                  onClick={() => { enterProposalMode(p); navigate('/proposals'); toast.success(`Entered proposal mode: ${p.proposalNumber}`); }}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 active:scale-95 transition-all"
                                >
                                  <FileText className="w-3 h-3" /> Open
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* ── Inline White-Background Preview ── */}
                          <AnimatePresence>
                            {isPreviewing && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden border-t border-slate-800/60"
                              >
                                {/* White paper document */}
                                <div className="bg-rb-surface text-slate-200 mx-3 my-3 rounded-lg shadow-xl overflow-hidden" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                                  {/* Document header stripe */}
                                  <div className="bg-gray-900 px-6 py-4 flex items-start justify-between">
                                    <div>
                                      <p className="text-white text-[15px] font-bold tracking-tight" style={{ fontFamily: 'system-ui, sans-serif' }}>Reynolds &amp; Bauhm</p>
                                      <p className="text-slate-400 text-[10px] mt-0.5" style={{ fontFamily: 'system-ui, sans-serif' }}>Smart Wastewater Assessment Tool</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-mono text-[13px] font-bold text-white">{p.proposalNumber}</p>
                                      <p className="text-[10px] text-slate-400 mt-0.5" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                        {(p.status || 'draft').toUpperCase()} · Rev {p.revisionNumber} · {standardLabel}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="px-6 pt-5 pb-6 space-y-5">
                                    {/* ── Title block ── */}
                                    <div className="border-b border-slate-800/60 pb-4">
                                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1" style={{ fontFamily: 'system-ui, sans-serif' }}>Treatment Proposal</p>
                                      <h2 className="text-[20px] font-bold text-slate-200 leading-tight">{p.title}</h2>
                                    </div>

                                    {/* ── Reference table ── */}
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[11px]" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                      {[
                                        ['Proposal Number', p.proposalNumber],
                                        ['Customer', p.customerName || customer.company || '—'],
                                        ['Status', (p.status || 'draft').charAt(0).toUpperCase() + (p.status || 'draft').slice(1)],
                                        ['Revision', `R${p.revisionNumber}`],
                                        ['Standard', standardLabel],
                                        ['Date', new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })],
                                        ...(customer.company ? [['Site / Company', customer.company]] : []),
                                        ...(customer.industryType ? [['Industry', customer.industryType]] : []),
                                        ...(customer.consentNumber ? [['Consent Number', customer.consentNumber]] : []),
                                        ...(customer.regulatoryBody ? [['Regulatory Body', customer.regulatoryBody]] : []),
                                      ].map(([label, value]) => (
                                        <div key={label} className="flex gap-2">
                                          <span className="font-semibold text-slate-400 w-36 shrink-0">{label}:</span>
                                          <span className="text-slate-300">{value}</span>
                                        </div>
                                      ))}
                                    </div>

                                    {/* ── Process Description ── */}
                                    {p.description && (
                                      <div>
                                        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-800/60 pb-1 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>Process Description</h3>
                                        <div className="text-[12px] text-slate-400 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                          {p.description}
                                        </div>
                                      </div>
                                    )}

                                    {/* ══ TREATMENT ANALYSIS — per train ══ */}
                                    {linkedTrains.length > 0 && (() => {
                                      // Resolve standard limits for compliance assessment
                                      const stdCode = (p.standard?.replace(' ', '_') || p.countryCode
                                        ? (COUNTRY_TO_STANDARD[p.countryCode || customer.country || 'GB'] || 'UK_EA')
                                        : 'UK_EA') as import('@/types/standards').StandardCode;
                                      let stdLimits: import('@/types/standards').WastewaterStandard['limits'] | null = null;
                                      try { stdLimits = getStandard(stdCode).limits; } catch { /* ignore */ }

                                      return (
                                        <div>
                                          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 border-b-2 border-gray-800 pb-1 mb-3" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                            Treatment Analysis — Process Train{linkedTrains.length > 1 ? 's' : ''} &amp; Water Quality
                                          </h3>
                                          <div className="space-y-6">
                                            {linkedTrains.map((train, ti) => {
                                              const eqList = (train as any).equipmentList as Array<{ key: string; label: string; category: string }> || [];
                                              const eqKeys = eqList.map((e: any) => e.key);

                                              // Find influent sample linked to this train (by sampleId)
                                              const trainSampleId = (train as any).sampleId;
                                              const influentSample = trainSampleId
                                                ? stats.influent.find(s => s.id === trainSampleId) || stats.influent[0]
                                                : stats.influent[0]; // fallback: first influent for customer
                                              const influentParams = influentSample?.params || {};
                                              const hasInfluent = Object.keys(influentParams).length > 0;

                                              // Predicted effluent
                                              const predictedParams = hasInfluent
                                                ? predictEffluent(influentParams, eqKeys)
                                                : {};

                                              // Measured effluent (if linked via linkedSampleId)
                                              const measuredEffluent = influentSample?.linkedSampleId
                                                ? stats.effluent.find(s => s.id === influentSample.linkedSampleId)
                                                : null;

                                              // Key params to display in analysis (only those present in influent)
                                              const analysisParams = Object.keys(influentParams).filter(k =>
                                                !['flowrate', 'flowRate', 'temperature', 'conductivity', 'tds'].includes(k)
                                              );

                                              // Stage-by-stage breakdown for key params (COD, BOD, TSS, TN, TP, O&G)
                                              const STAGE_PARAMS = ['cod', 'bod', 'bod5', 'tss', 'og', 'oilgrease', 'tn', 'totalNitrogen', 'tp', 'totalPhosphorus', 'nh3', 'nh3n'];
                                              const stageParams = analysisParams.filter(k => STAGE_PARAMS.includes(k));

                                              return (
                                                <div key={train.id} className="border-2 border-gray-800 rounded-lg overflow-hidden" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                                  {/* Train header */}
                                                  <div className="bg-gray-800 px-4 py-2.5 flex items-center justify-between">
                                                    <div>
                                                      <p className="text-[13px] font-bold text-white">{ti + 1}. {train.name}</p>
                                                      {influentSample && (
                                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                                          Based on: {influentSample.sampleName}
                                                          {influentSample.reportNumber ? ` · Ref: ${influentSample.reportNumber}` : ''}
                                                          {influentSample.createdAt ? ` · ${new Date(influentSample.createdAt).toLocaleDateString('en-GB')}` : ''}
                                                        </p>
                                                      )}
                                                    </div>
                                                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-gray-700 text-gray-300">{(train as any).status || 'draft'}</span>
                                                  </div>

                                                  <div className="p-4 space-y-4">
                                                    {/* ── Equipment pipeline ── */}
                                                    <div>
                                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Process Sequence</p>
                                                      <div className="flex flex-wrap items-center gap-1">
                                                        {eqList.map((eq: any, ei: number) => {
                                                          const catColour: Record<string, string> = {
                                                            pretreatment: '#0d9488', primary: '#d97706',
                                                            secondary: '#16a34a', tertiary: '#0284c7', sludge: '#dc2626',
                                                          };
                                                          return (
                                                            <span key={ei} className="flex items-center gap-1">
                                                              <span style={{ background: `${catColour[eq.category] || '#6b7280'}18`, border: `1px solid ${catColour[eq.category] || '#6b7280'}40`, color: catColour[eq.category] || '#374151', fontFamily: 'system-ui, sans-serif' }}
                                                                className="px-2 py-1 text-[10px] font-bold rounded">
                                                                {eq.label}
                                                              </span>
                                                              {ei < eqList.length - 1 && <span className="text-gray-300 text-[11px] font-bold">→</span>}
                                                            </span>
                                                          );
                                                        })}
                                                      </div>
                                                      {(train as any).description && (
                                                        <p className="text-[11px] text-gray-600 mt-2 italic">{(train as any).description}</p>
                                                      )}
                                                    </div>

                                                    {/* ── Influent Data Table ── */}
                                                    {hasInfluent && (
                                                      <div>
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                                                          Influent Water Quality — {influentSample?.sampleName || 'Basis of Design'}
                                                        </p>
                                                        <div className="overflow-x-auto">
                                                          <table className="w-full text-[11px] border-collapse">
                                                            <thead>
                                                              <tr className="bg-rb-elevated">
                                                                <th className="text-left px-2 py-1.5 font-bold text-gray-600 border border-slate-800/60 w-36">Parameter</th>
                                                                <th className="text-right px-2 py-1.5 font-bold text-gray-600 border border-slate-800/60 w-24">Value</th>
                                                                <th className="text-left px-2 py-1.5 font-bold text-gray-600 border border-slate-800/60 w-16">Unit</th>
                                                                {stdLimits && <th className="text-right px-2 py-1.5 font-bold text-gray-600 border border-slate-800/60 w-24">Limit</th>}
                                                                {stdLimits && <th className="text-center px-2 py-1.5 font-bold text-gray-600 border border-slate-800/60 w-20">Raw Status</th>}
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                              {analysisParams.map(k => {
                                                                const val = influentParams[k];
                                                                if (val === undefined || val === null) return null;
                                                                const meta = ALL_PARAM_LABELS[k] || { label: k.toUpperCase(), unit: 'mg/L' };
                                                                const stdKey = PARAM_TO_STD[k];
                                                                const limit = stdLimits && stdKey ? (stdLimits as any)[stdKey] : null;
                                                                const rawStatus = limit !== null && limit !== undefined
                                                                  ? (k === 'ph'
                                                                    ? (val >= (stdLimits?.phMin || 6) && val <= (stdLimits?.phMax || 9) ? 'pass' : 'fail')
                                                                    : val <= limit ? 'pass' : 'fail')
                                                                  : null;
                                                                return (
                                                                  <tr key={k} className="hover:bg-rb-elevated">
                                                                    <td className="px-2 py-1 font-semibold text-slate-400 border border-slate-800/60">{meta.label}</td>
                                                                    <td className="px-2 py-1 text-right font-mono font-bold text-slate-300 border border-slate-800/60">{typeof val === 'number' ? val.toFixed(val < 1 ? 3 : 1) : val}</td>
                                                                    <td className="px-2 py-1 text-slate-400 border border-slate-800/60">{meta.unit}</td>
                                                                    {stdLimits && <td className="px-2 py-1 text-right text-slate-400 border border-slate-800/60">{limit !== null && limit !== undefined ? (k === 'ph' ? `${stdLimits?.phMin}–${stdLimits?.phMax}` : `≤${limit}`) : '—'}</td>}
                                                                    {stdLimits && (
                                                                      <td className="px-2 py-1 text-center border border-slate-800/60">
                                                                        {rawStatus === 'pass' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-500/15 text-green-400">PASS</span>}
                                                                        {rawStatus === 'fail' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-400">EXCEEDS</span>}
                                                                        {rawStatus === null && <span className="text-gray-300">—</span>}
                                                                      </td>
                                                                    )}
                                                                  </tr>
                                                                );
                                                              })}
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* ── Predicted Effluent Table ── */}
                                                    {hasInfluent && stageParams.length > 0 && (
                                                      <div>
                                                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#166534' }}>
                                                          Predicted Treated Effluent — {eqList.map((e: any) => e.label).join(' → ')}
                                                        </p>
                                                        <div className="overflow-x-auto">
                                                          <table className="w-full text-[11px] border-collapse">
                                                            <thead>
                                                              <tr style={{ background: 'rgba(16,185,129,0.08)' }}>
                                                                <th className="text-left px-2 py-1.5 font-bold border w-36" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Parameter</th>
                                                                <th className="text-right px-2 py-1.5 font-bold border w-24" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Influent</th>
                                                                <th className="text-right px-2 py-1.5 font-bold border w-24" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Predicted</th>
                                                                <th className="text-right px-2 py-1.5 font-bold border w-20" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Removal</th>
                                                                <th className="text-left px-2 py-1.5 font-bold border w-16" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Unit</th>
                                                                {stdLimits && <th className="text-right px-2 py-1.5 font-bold border w-24" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Limit</th>}
                                                                {stdLimits && <th className="text-center px-2 py-1.5 font-bold border w-24" style={{ borderColor: 'rgba(16,185,129,0.25)', color: '#10b981' }}>Compliance</th>}
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                              {stageParams.map(k => {
                                                                const inf = influentParams[k];
                                                                const pred = predictedParams[k];
                                                                if (inf === undefined || pred === undefined) return null;
                                                                const meta = ALL_PARAM_LABELS[k] || { label: k.toUpperCase(), unit: 'mg/L' };
                                                                const removal = overallRemoval(inf, pred);
                                                                const stdKey = PARAM_TO_STD[k];
                                                                const limit = stdLimits && stdKey ? (stdLimits as any)[stdKey] : null;
                                                                const compliant = limit !== null && limit !== undefined
                                                                  ? pred <= limit : null;
                                                                const remColour = removal >= 80 ? '#10b981' : removal >= 50 ? '#f59e0b' : '#ef4444';
                                                                return (
                                                                  <tr key={k} style={{ background: compliant === false ? 'rgba(239,68,68,0.08)' : compliant === true ? 'rgba(16,185,129,0.06)' : undefined }}>
                                                                    <td className="px-2 py-1.5 font-semibold border" style={{ borderColor: 'rgba(100,116,139,0.25)', color: '#e2e8f0' }}>{meta.label}</td>
                                                                    <td className="px-2 py-1.5 text-right font-mono border" style={{ borderColor: 'rgba(100,116,139,0.25)', color: '#94a3b8' }}>{inf.toFixed(inf < 1 ? 3 : 1)}</td>
                                                                    <td className="px-2 py-1.5 text-right font-mono font-bold border" style={{ borderColor: 'rgba(100,116,139,0.25)', color: '#10b981' }}>{pred.toFixed(pred < 1 ? 3 : 1)}</td>
                                                                    <td className="px-2 py-1.5 text-right font-bold border" style={{ borderColor: 'rgba(100,116,139,0.25)', color: remColour }}>{removal}%</td>
                                                                    <td className="px-2 py-1.5 border" style={{ borderColor: 'rgba(100,116,139,0.25)', color: '#94a3b8' }}>{meta.unit}</td>
                                                                    {stdLimits && <td className="px-2 py-1.5 text-right border" style={{ borderColor: 'rgba(100,116,139,0.25)', color: '#94a3b8' }}>{limit !== null && limit !== undefined ? `≤${limit}` : '—'}</td>}
                                                                    {stdLimits && (
                                                                      <td className="px-2 py-1.5 text-center border" style={{ borderColor: 'rgba(100,116,139,0.25)' }}>
                                                                        {compliant === true && <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-green-500/15 text-green-400">✓ COMPLIANT</span>}
                                                                        {compliant === false && <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-red-500/15 text-red-400">✗ EXCEEDS</span>}
                                                                        {compliant === null && <span style={{ color: '#d1d5db' }}>—</span>}
                                                                      </td>
                                                                    )}
                                                                  </tr>
                                                                );
                                                              })}
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 italic mt-1">* Predicted values are theoretical estimates based on typical equipment removal efficiencies. Actual performance depends on detailed design, loading rates, and site conditions.</p>
                                                      </div>
                                                    )}

                                                    {/* ── Measured Effluent (if available) ── */}
                                                    {measuredEffluent && Object.keys(measuredEffluent.params || {}).length > 0 && (
                                                      <div>
                                                        <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-2">
                                                          Measured Treated Effluent — {measuredEffluent.sampleName}
                                                          {measuredEffluent.reportNumber ? ` (${measuredEffluent.reportNumber})` : ''}
                                                        </p>
                                                        <div className="overflow-x-auto">
                                                          <table className="w-full text-[11px] border-collapse">
                                                            <thead>
                                                              <tr style={{ background: 'rgba(59,130,246,0.08)' }}>
                                                                <th className="text-left px-2 py-1.5 font-bold border border-blue-900/40 text-blue-400 w-36">Parameter</th>
                                                                <th className="text-right px-2 py-1.5 font-bold border border-blue-900/40 text-blue-400 w-24">Measured</th>
                                                                <th className="text-left px-2 py-1.5 font-bold border border-blue-900/40 text-blue-400 w-16">Unit</th>
                                                                {stdLimits && <th className="text-right px-2 py-1.5 font-bold border border-blue-900/40 text-blue-400 w-24">Limit</th>}
                                                                {stdLimits && <th className="text-center px-2 py-1.5 font-bold border border-blue-900/40 text-blue-400 w-24">Compliance</th>}
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                              {Object.entries(measuredEffluent.params).map(([k, val]) => {
                                                                if (val === undefined || val === null) return null;
                                                                const meta = ALL_PARAM_LABELS[k] || { label: k.toUpperCase(), unit: 'mg/L' };
                                                                const stdKey = PARAM_TO_STD[k];
                                                                const limit = stdLimits && stdKey ? (stdLimits as any)[stdKey] : null;
                                                                const compliant = limit !== null && limit !== undefined ? (val as number) <= limit : null;
                                                                return (
                                                                  <tr key={k} style={{ background: compliant === false ? 'rgba(239,68,68,0.08)' : compliant === true ? 'rgba(59,130,246,0.06)' : undefined }}>
                                                                    <td className="px-2 py-1 font-semibold border border-slate-700/40 text-slate-300">{meta.label}</td>
                                                                    <td className="px-2 py-1 text-right font-mono font-bold border border-slate-700/40 text-blue-400">{typeof val === 'number' ? val.toFixed(val < 1 ? 3 : 1) : val}</td>
                                                                    <td className="px-2 py-1 border border-slate-700/40 text-slate-500">{meta.unit}</td>
                                                                    {stdLimits && <td className="px-2 py-1 text-right border border-slate-700/40 text-slate-500">{limit !== null && limit !== undefined ? `≤${limit}` : '—'}</td>}
                                                                    {stdLimits && (
                                                                      <td className="px-2 py-1 text-center border border-slate-700/40">
                                                                        {compliant === true && <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-green-500/15 text-green-400">✓ COMPLIANT</span>}
                                                                        {compliant === false && <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-red-500/15 text-red-400">✗ EXCEEDS</span>}
                                                                        {compliant === null && <span className="text-gray-300">—</span>}
                                                                      </td>
                                                                    )}
                                                                  </tr>
                                                                );
                                                              })}
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* ── No data message ── */}
                                                    {!hasInfluent && (
                                                      <div className="rounded-lg border border-dashed border-slate-700/50 p-3 text-center">
                                                        <p className="text-[11px] text-slate-400">No influent sample linked to this train. Add an influent sample and link it to the process train for full treatment analysis.</p>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })()}

                                    {/* ── Notes ── */}
                                    {p.notes && (
                                      <div>
                                        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-800/60 pb-1 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>Notes &amp; Assumptions</h3>
                                        <p className="text-[12px] text-slate-400 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'system-ui, sans-serif' }}>{p.notes}</p>
                                      </div>
                                    )}

                                    {/* ── Revision reason ── */}
                                    {p.revisionReason && (
                                      <div className="bg-amber-500/10 border border-amber-800/50 rounded-lg p-3">
                                        <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'system-ui, sans-serif' }}>Reason for Revision</p>
                                        <p className="text-[11px] text-amber-400" style={{ fontFamily: 'system-ui, sans-serif' }}>{p.revisionReason}</p>
                                      </div>
                                    )}

                                    {/* ── Signature area ── */}
                                    <div className="border-t border-slate-700/50 pt-4 grid grid-cols-2 gap-6 mt-4" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                      <div>
                                        <div className="border-b border-gray-400 h-8 mb-1" />
                                        <p className="text-[10px] text-slate-400">Prepared by / Date</p>
                                      </div>
                                      <div>
                                        <div className="border-b border-gray-400 h-8 mb-1" />
                                        <p className="text-[10px] text-slate-400">Approved by / Date</p>
                                      </div>
                                    </div>

                                    {/* ── Footer ── */}
                                    <p className="text-[10px] text-slate-400 text-center border-t border-slate-800/60 pt-3" style={{ fontFamily: 'system-ui, sans-serif' }}>
                                      © {new Date().getFullYear()} Reynolds &amp; Bauhm · Proprietary &amp; Confidential · Preliminary assessment only — not for final engineering design.
                                    </p>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Edit Regulatory Modal ── */}
      <AnimatePresence>
        {editRegulatoryOpen && (
          <EditRegulatoryModal
            customer={customer}
            onClose={() => setEditRegulatoryOpen(false)}
            onSaved={(updated) => {
              onEditCustomer(updated);
              setEditRegulatoryOpen(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* ── Edit Customer Modal ── */}
      <AnimatePresence>
        {editCustomerOpen && (
          <EditCustomerModal
            customer={customer}
            onClose={() => setEditCustomerOpen(false)}
            onSaved={(updated) => {
              onEditCustomer(updated);
              setEditCustomerOpen(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
// ─── Active Session Panel ─────────────────────────────────────────────────────
// Persistent expandable panel that always shows the active session customer,
// their process trains, and quick-action shortcuts.
function ActiveSessionPanel({
  customer, trains, onClickCustomer, onActivateAndGo, onClear, navigate, expanded, onToggleExpand,
}: {
  customer: { id: string; company: string; name?: string; industryType?: string; country?: string; standard?: string };
  trains: UnifiedTrain[];
  onClickCustomer: () => void;
  onActivateAndGo: (path: string) => void;
  onClear: () => void;
  navigate: ReturnType<typeof useNavigate>;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const flag = (customer as any).country ? (COUNTRY_FLAGS[(customer as any).country] || '') : '';
  const sc = STANDARD_COLORS[(customer as any).standard || 'UK_EA'] || STANDARD_COLORS.UK_EA;
  const assessedCount = trains.filter(t => t.status === 'assessed').length;

  return (
    <div
      onClick={onToggleExpand}
      className={`rounded-xl border shrink-0 overflow-hidden cursor-pointer transition-all duration-200
        ${expanded
          ? 'border-cyan-400/50 bg-gradient-to-r from-cyan-500/12 to-cyan-500/4 shadow-lg shadow-cyan-900/20'
          : 'border-cyan-500/30 bg-gradient-to-r from-cyan-500/6 to-transparent hover:border-cyan-400/55 hover:from-cyan-500/12 hover:to-cyan-500/3 hover:shadow-md hover:shadow-cyan-900/15'
        }`}
    >
      {/* ── Always-visible header row ── */}
      <div className="flex items-center gap-2.5 px-4 py-2.5">
        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shrink-0" />
        <span className="text-[10px] font-bold text-cyan-500 uppercase tracking-widest shrink-0">Active Session</span>

        {/* Customer name — click navigates to card (stops expand propagation) */}
        <button
          onClick={e => { e.stopPropagation(); onClickCustomer(); }}
          className="flex items-center gap-1.5 min-w-0 group hover:opacity-80 transition-opacity"
        >
          <span className="text-sm font-bold text-white group-hover:text-cyan-200 truncate">{customer.company}</span>
          {flag && <span className="text-sm shrink-0">{flag}</span>}
          {(customer as any).industryType && (
            <span className="text-[10px] text-slate-500 shrink-0 hidden sm:block">{(customer as any).industryType}</span>
          )}
          {(customer as any).standard && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-black border ${sc.bg} ${sc.border} ${sc.color} shrink-0 hidden sm:inline-flex`}>
              {(customer as any).standard.replace('_', ' ')}
            </span>
          )}
        </button>

        {/* Train count badge */}
        {trains.length > 0 && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 shrink-0">
            <Workflow className="w-2.5 h-2.5" />
            {trains.length} train{trains.length !== 1 ? 's' : ''}
            {assessedCount > 0 && <span className="ml-1 text-emerald-400">· {assessedCount} assessed</span>}
          </div>
        )}

        {/* Compact quick-action buttons */}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <button onClick={e => { e.stopPropagation(); onActivateAndGo('/influent-entry'); }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 active:scale-95 transition-all">
            <Droplets className="w-2.5 h-2.5" /> Sample
          </button>
          <button onClick={e => { e.stopPropagation(); onActivateAndGo('/process-db'); }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 active:scale-95 transition-all">
            <Workflow className="w-2.5 h-2.5" /> Train
          </button>
          <button onClick={e => { e.stopPropagation(); onActivateAndGo('/proposals'); }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 active:scale-95 transition-all">
            <FileText className="w-2.5 h-2.5" /> Proposal
          </button>
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border pointer-events-none ${
              expanded
                ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/35'
                : 'bg-slate-700/70 text-slate-200 border-slate-600/70'
            }`}
          >
            {expanded
              ? <><ChevronUp className="w-3.5 h-3.5" /> Collapse</>
              : <><ChevronDown className="w-3.5 h-3.5" /> Details</>
            }
          </div>
          <button onClick={e => { e.stopPropagation(); onClear(); }} title="Clear active session"
            className="p-1.5 rounded-lg text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-all">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Expandable content: trains + full action grid ── */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-cyan-500/15">
              {/* ── Process trains for active customer ── */}
              <div className="mt-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                  <Workflow className="w-3 h-3 text-amber-400" />
                  Process Trains — {customer.company}
                </p>
                {trains.length > 0 ? (
                  <>
                    <div className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
                      {trains.slice(0, 6).map(t => {
                        const ts = TRAIN_STATUS[t.status] || TRAIN_STATUS.draft;
                        return (
                          <button
                            key={t.id}
                            onClick={() => navigate('/process-db')}
                            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left hover:opacity-90 active:scale-98 transition-all ${ts.bg} ${ts.border}`}
                          >
                            <div className="min-w-0">
                              <p className="text-[10px] font-bold text-slate-200 truncate">{t.name}</p>
                              <p className={`text-[10px] font-bold ${ts.color}`}>{ts.label}</p>
                            </div>
                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                              t.status === 'assessed' ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]' :
                              t.status === 'in_progress' ? 'bg-amber-400' : 'bg-slate-600'
                            }`} />
                          </button>
                        );
                      })}
                    </div>
                    {trains.length > 6 && (
                      <p className="text-[10px] text-slate-600 mt-1.5">
                        +{trains.length - 6} more ·{' '}
                        <button onClick={() => navigate('/process-db')} className="text-cyan-500 hover:text-cyan-400 underline underline-offset-2">view all in Process DB</button>
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-800/60 bg-rb-surface/60 text-slate-600 text-[10px]">
                      <Workflow className="w-3.5 h-3.5 shrink-0" />
                      No process trains yet for {customer.company}
                    </div>
                    <button onClick={() => onActivateAndGo('/process-db')}
                      className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all shrink-0">
                      <Plus className="w-3 h-3" /> Build First Train
                    </button>
                  </div>
                )}
              </div>

              {/* ── Full action grid ── */}
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: 'Add Influent', icon: Droplets, path: '/influent-entry', style: 'bg-sky-500/10 text-sky-400 border-sky-500/20 hover:bg-sky-500/20' },
                  { label: 'Add Effluent', icon: Beaker, path: '/effluent-entry', style: 'bg-rb-brand/10 text-rb-brand border-rb-brand/20 hover:bg-rb-brand/20' },
                  { label: 'Build Train', icon: Workflow, path: '/process-db', style: 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20' },
                  { label: 'New Proposal', icon: FileText, path: '/proposals', style: 'bg-violet-500/10 text-violet-400 border-violet-500/20 hover:bg-violet-500/20' },
                ].map(({ label, icon: Icon, path, style }) => (
                  <button key={label} onClick={() => onActivateAndGo(path)}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold border active:scale-95 transition-all ${style}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const { currentCustomer, setCurrentCustomer, setActiveStandard } = useSession();
  const { activeProposal, proposals: contextProposals, saveProposal } = useProposal();
  const { isAdmin, user: authUser } = useAuth(); // ensures session is initialised (keeps rb_api_token alive)

  const [customers, setCustomers] = useState<Customer[]>(loadCustomers);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [sessionPanelExpanded, setSessionPanelExpanded] = useState(false);
  // forceTab: when set, CustomerDetailPanel immediately switches to that tab
  const [forceTab, setForceTab] = useState<{ tab: DetailTab; nonce: number } | null>(null);

  // ─── Filter & sort state ──────────────────────────────────────────────────
  type SortMode = 'name' | 'recent' | 'data' | 'standard';
  const [sortMode, setSortMode]           = useState<SortMode>('name');
  const [filterStandard, setFilterStandard] = useState('');
  const [filterCountry, setFilterCountry]   = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [showFilters, setShowFilters]       = useState(false);

  // Auto-select active customer in list
  useEffect(() => {
    if (currentCustomer && !selectedId) {
      setSelectedId(currentCustomer.id);
    }
  }, [currentCustomer]);

  // ─── Cross-entity data ────────────────────────────────────────────────────
  const [allSamples, setAllSamples] = useState<UnifiedSample[]>([]);
  const [allTrains, setAllTrains] = useState<UnifiedTrain[]>([]);
  // allProposals: merge ProposalContext (localStorage-backed) with any server-fetched proposals
  const [serverProposals, setServerProposals] = useState<Proposal[]>([]);
  const allProposals = useMemo(() => {
    const ctxIds = new Set(contextProposals.map(p => p.id));
    return [...contextProposals, ...serverProposals.filter(sp => !ctxIds.has(sp.id))];
  }, [contextProposals, serverProposals]);

  const reload = () => {
    setCustomers(loadCustomers());
    // Filter local samples to only show current user's samples (prevent cross-user bleed)
    const currentUserId = authUser ? Number(authUser.id) : 0;
    const localSamples = getAllLocalSamples().filter(s =>
      !currentUserId || !s.userId || s.userId === currentUserId
    );
    setAllSamples(localSamples);
    const localTrains = getLocalProcessTrains();
    setAllTrains(currentUserId
      ? localTrains.filter(t => !t.userId || t.userId === currentUserId)
      : localTrains);
    // Proposals are live from ProposalContext — no manual reload needed
  };

  // Load local data immediately on mount
  useEffect(() => { reload(); }, []);

  // Helper: normalise any raw API sample row → UnifiedSample
  const normServerSample = (s: any): UnifiedSample => ({
    id:             Number(s.id),
    // Coerce to number — PHP PDO returns strings; all comparisons use Number()
    customerId:     (s.customer_id ?? s.customerId) != null ? Number(s.customer_id ?? s.customerId) : null,
    userId:         s.user_id ?? s.userId ?? 0,
    sampleName:     s.sample_name || s.sampleName || 'Untitled',
    reportNumber:   s.report_number || s.reportNumber || null,
    params:         typeof s.params === 'string' ? JSON.parse(s.params || '{}') : (s.params || {}),
    standard:       s.standard || null,
    notes:          s.notes || null,
    sampleType:     s.sample_type || s.sampleType || 'influent',
    siteId:         s.site_id != null ? String(s.site_id) : (s.siteId != null ? String(s.siteId) : null),
    linkedSampleId: s.linked_sample_id ?? s.linkedSampleId ?? null,
    createdAt:      s.created_at || s.createdAt || new Date().toISOString(),
    updatedAt:      s.updated_at || s.updatedAt || new Date().toISOString(),
    _local:         false,
  });

  const mergeSamplesToState = (rows: any[]) => {
    if (!rows?.length) return;
    const fresh = rows.map(normServerSample);
    setAllSamples(prev => {
      const existIds = new Set(prev.map(s => s.id));
      const extra = fresh.filter(s => !existIds.has(s.id));
      return extra.length ? [...prev, ...extra] : prev;
    });
  };

  // On mount: bulk-load ALL server data so every card shows counts + param snapshots
  // without requiring the user to click a customer first.
  useEffect(() => {
    const token = localStorage.getItem('rb_api_token');
    if (!token) return;

    // Current user's numeric DB ID — used to guard against server returning cross-user data
    const currentUserId = authUser ? Number(authUser.id) : 0;
    const byCurrentUser = (rows: any[]) => isAdmin ? rows : rows.filter((r: any) => {
      const uid = (r.userId ?? r.user_id) != null ? Number(r.userId ?? r.user_id) : null;
      return uid == null || uid === 0 || uid === currentUserId;
    });

    // ── Influent samples (influent_samples table — correct endpoint) ──
    apiListInfluentSamples(undefined, isAdmin ? { adminAll: true } : undefined).then((rows: any[]) => {
      if (!rows?.length) return;
      mergeSamplesToState(byCurrentUser(rows).map((s: any) => ({
        ...s,
        sample_type: s.sampleType || 'influent',
        customer_id: Number(s.customerId ?? s.customer_id ?? null),
      })));
    }).catch(() => {});

    // ── Effluent samples (influent_samples table with type=effluent) ──
    apiListInfluentSamples(undefined, isAdmin ? { type: 'effluent', adminAll: true } : { type: 'effluent' }).then((rows: any[]) => {
      if (!rows?.length) return;
      mergeSamplesToState(byCurrentUser(rows).map((s: any) => ({
        ...s,
        sample_type: s.sampleType || 'effluent',
        customer_id: Number(s.customerId ?? s.customer_id ?? null),
      })));
    }).catch(() => {});

    // ── Legacy customer_samples table (keep for backward compat) ──
    apiListCustomerSamples().then(mergeSamplesToState).catch(() => {});

    // All trains
    apiListTrains().then((rows: any[]) => {
      if (!rows?.length) return;
      const fresh: UnifiedTrain[] = rows.map((t: any) => ({
        id:               t.id,
        userId:           t.userId ?? t.user_id ?? 0,
        name:             t.name || 'Untitled Train',
        description:      t.description ?? null,
        sampleId:         t.sampleId ?? t.sample_id ?? null,
        customerId:       t.customerId ?? t.customer_id ?? null,
        proposalId:       t.proposalId ?? t.proposal_id ?? null,
        equipmentList:    Array.isArray(t.equipmentList) ? t.equipmentList
                          : (typeof t.equipmentList === 'string' ? JSON.parse(t.equipmentList || '[]') : []),
        status:           t.status || 'draft',
        assessmentResult: t.assessmentResult ?? null,
        createdAt:        t.createdAt || t.created_at || new Date().toISOString(),
        updatedAt:        t.updatedAt || t.updated_at || new Date().toISOString(),
        _local:           false,
      }));
      setAllTrains(prev => {
        const existIds = new Set(prev.map(tr => tr.id));
        const extra = fresh.filter(tr => !existIds.has(tr.id));
        return extra.length ? [...prev, ...extra] : prev;
      });
    }).catch(() => {});

    // All proposals
    apiListProposals().then((rows: any[]) => {
      if (!rows?.length) return;
      setServerProposals(prev => {
        const existIds = new Set(prev.map(p => p.id));
        const extra = rows.filter((p: any) => !existIds.has(p.id));
        if (!extra.length) return prev;
        return [...prev, ...extra.map((p: any) => ({
          ...p,
          customerId: String(p.customer_id ?? p.customerId ?? ''),
          status:     p.status || 'draft',
          createdAt:  p.created_at || p.createdAt || new Date().toISOString(),
          updatedAt:  p.updated_at || p.updatedAt || new Date().toISOString(),
        }))];
      });
    }).catch(() => {});
  }, []);

  // On mount: load customers from API to get proper DB integer IDs (serverId)
  // We ENRICH existing customers with their serverId — we do NOT change their local id
  // (changing id would break selectedId lookup and cause blank panel)
  useEffect(() => {
    const token = localStorage.getItem('rb_api_token');
    if (!token) return;

    apiListCustomers().then((rows: any[]) => {
      // If the backend responds with an empty array the DB is genuinely empty —
      // treat it as authoritative and wipe any stale localStorage customers so
      // offline demo-data fallback records don't ghost the customer list.
      if (!Array.isArray(rows)) return;   // parse/network failure — keep local state
      if (!rows.length) {
        setCustomers([]);
        saveCustomers([]);
        return;
      }
      // When authenticated and the server returns customers, the server IS the
      // source of truth. Replace localStorage entirely — this prevents ghost rows
      // caused by stale localStorage demo data (20 customers) mixing with
      // database-imported records (e.g. demo160 with different company names).
      const fromServer: Customer[] = rows.map((c: any) => ({
        id:           String(c.id),
        serverId:     Number(c.id),
        name:         c.name || '',
        company:      c.company || c.name || '',
        contactEmail: c.contactEmail || c.contact_email || c.email || '',
        phone:        c.phone || '',
        address:      c.address || '',
        notes:        c.notes || '',
        createdAt:    c.created_at || c.createdAt || new Date().toISOString(),
        country:      c.country || undefined,
        industryType: c.industryType || c.industry_type || undefined,
        standard:     c.standard || undefined,
        dischargeRoute: c.dischargeRoute || c.discharge_route || undefined,
        postcode:     c.postcode || undefined,
        city:         c.city || undefined,
        siteName:     c.siteName || c.site_name || undefined,
        siteReference: c.siteReference || c.site_reference || undefined,
        latitude:     c.latitude != null ? Number(c.latitude) : undefined,
        longitude:    c.longitude != null ? Number(c.longitude) : undefined,
        catchmentArea: c.catchmentArea || c.catchment_area || undefined,
        consentNumber: c.consentNumber || c.consent_number || undefined,
        consentIssuedDate: c.consentIssuedDate || c.consent_issued_date || undefined,
        consentExpiryDate: c.consentExpiryDate || c.consent_expiry_date || undefined,
        consentVolumeLimit: c.consentVolumeLimit != null ? Number(c.consentVolumeLimit) : undefined,
        regulatoryBody: c.regulatoryBody || c.regulatory_body || undefined,
        industrySubsector: c.industrySubsector || c.industry_subsector || undefined,
        sicCode:      c.sicCode || c.sic_code || undefined,
        employeeCount: c.employeeCount != null ? Number(c.employeeCount) : undefined,
        operatingHours: c.operatingHours || c.operating_hours || undefined,
        productionVolume: c.productionVolume || c.production_volume || undefined,
        avgFlowRate:  c.avgFlowRate != null ? Number(c.avgFlowRate) : undefined,
        peakFlowRate: c.peakFlowRate != null ? Number(c.peakFlowRate) : undefined,
        billingAddress: c.billingAddress || c.billing_address || undefined,
        accountsEmail: c.accountsEmail || c.accounts_email || undefined,
        environmentalContact: c.environmentalContact || c.environmental_contact || undefined,
        environmentalPhone: c.environmentalPhone || c.environmental_phone || undefined,
        wasteCarrierReg: c.wasteCarrierReg || c.waste_carrier_reg || undefined,
        isDemo:       !!(c.is_demo),
      }));
      // Deduplicate by id (guards against server returning the same customer twice)
      const seen = new Set<string>();
      const dedupedFromServer = fromServer.filter(c => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      setCustomers(dedupedFromServer);
      saveCustomers(dedupedFromServer);
    }).catch(() => {});
  }, []);

  // When a customer is selected (or its serverId is enriched): targeted API fetch
  // Re-runs whenever selectedId OR the customers list changes (serverId may be added async)
  useEffect(() => {
    if (!selectedId) return;
    const token = localStorage.getItem('rb_api_token');
    if (!token) return;

    // Resolve the DB integer ID to use for API filtering.
    // Priority: customer.serverId > small integer id > give up (local-only timestamp id)
    const customer = customers.find(c => c.id === selectedId);
    const rawNumId = parseInt(selectedId, 10);
    // serverId takes priority (enriched from API); otherwise use the id if it looks like a DB int
    const numId = customer?.serverId
      ?? (rawNumId > 0 && rawNumId < 2_000_000_000 && selectedId.length < 13 ? rawNumId : 0);
    if (!numId) return; // local-only customer — nothing to fetch from server

    // Fetch influent_samples + effluent + customer_samples for this customer
    apiListInfluentSamples(numId).then((rows: any[]) => {
      if (!rows?.length) return;
      mergeSamplesToState(rows.map((s: any) => ({
        ...s,
        sample_type: s.sampleType || 'influent',
        customer_id: s.customerId ?? null,
      })));
    }).catch(() => {});
    apiListInfluentSamples(numId, { type: 'effluent' }).then((rows: any[]) => {
      if (!rows?.length) return;
      mergeSamplesToState(rows.map((s: any) => ({
        ...s,
        sample_type: s.sampleType || 'effluent',
        customer_id: s.customerId ?? null,
      })));
    }).catch(() => {});
    apiListCustomerSamples(numId).then(mergeSamplesToState).catch(() => {});

    // Fetch process trains for this customer
    apiListTrains(numId).then((rows: any[]) => {
      if (!rows?.length) return;
      const fresh: UnifiedTrain[] = rows.map((t: any) => ({
        id:               t.id,
        userId:           t.userId ?? t.user_id ?? 0,
        name:             t.name || 'Untitled Train',
        description:      t.description ?? null,
        sampleId:         t.sampleId ?? t.sample_id ?? null,
        customerId:       t.customerId ?? t.customer_id ?? numId,
        proposalId:       t.proposalId ?? t.proposal_id ?? null,
        equipmentList:    Array.isArray(t.equipmentList) ? t.equipmentList
                          : (typeof t.equipmentList === 'string'
                              ? JSON.parse(t.equipmentList || '[]') : []),
        status:           t.status || 'draft',
        assessmentResult: t.assessmentResult ?? null,
        createdAt:        t.createdAt || t.created_at || new Date().toISOString(),
        updatedAt:        t.updatedAt || t.updated_at || new Date().toISOString(),
        _local:           false,
      }));
      setAllTrains(prev => {
        const existIds = new Set(prev.map(tr => tr.id));
        const extra = fresh.filter(tr => !existIds.has(tr.id));
        return extra.length ? [...prev, ...extra] : prev;
      });
    }).catch(() => {});

    // Fetch proposals for this customer
    apiListProposals({ customerId: numId }).then((rows: any[]) => {
      if (!rows?.length) return;
      setServerProposals(prev => {
        const existIds = new Set(prev.map(p => p.id));
        const extra = rows.filter((p: any) => !existIds.has(p.id));
        if (!extra.length) return prev;
        const fresh = extra.map((p: any) => ({
          ...p,
          id:         p.id,
          customerId: String(p.customer_id ?? p.customerId ?? numId),
          status:     p.status || 'draft',
          createdAt:  p.created_at || p.createdAt || new Date().toISOString(),
          updatedAt:  p.updated_at || p.updatedAt || new Date().toISOString(),
        }));
        return [...prev, ...fresh];
      });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, customers]);

  // ─── Demo fallback ────────────────────────────────────────────────────────
  const DEMO: Customer[] = [
    { id: '1', name: 'John Windsor', company: 'Windsor Dairy Ltd', contactEmail: 'john@windsor.uk', phone: '+44 1753 123456', address: 'Windsor, Berkshire', notes: 'CAT-2 discharge to sewer', createdAt: '2025-01-01T00:00:00Z', country: 'GB', industryType: 'Dairy Processing', standard: 'UK_EA' },
    { id: '2', name: 'Sarah Thames', company: 'Thames Brewing Co.', contactEmail: 'sarah@thamesbrew.co.uk', phone: '+44 20 7946 0958', address: 'London', notes: '', createdAt: '2025-01-02T00:00:00Z', country: 'GB', industryType: 'Brewery / Distillery', standard: 'UK_EA' },
    { id: '3', name: 'Mark Sheffield', company: 'Sheffield Metalworks', contactEmail: 'mark@sheffieldmetal.co.uk', phone: '+44 114 496 0123', address: 'Sheffield, South Yorkshire', notes: 'Heavy metals — List I substances', createdAt: '2025-01-03T00:00:00Z', country: 'GB', industryType: 'Metal Finishing', standard: 'UK_EA' },
    { id: '4', name: 'Emma Bristol', company: 'Bristol Textile Group', contactEmail: 'emma@bristoltex.com.au', phone: '+61 2 9374 4000', address: 'Bristol, NSW', notes: '', createdAt: '2025-01-04T00:00:00Z', country: 'AU', industryType: 'Textile / Laundry', standard: 'AU_EPA' },
    { id: '5', name: 'James Aberdeen', company: 'Aberdeen Seafood Processors', contactEmail: 'james@aberdeenseafood.co.uk', phone: '+44 1224 785000', address: 'Aberdeen, Scotland', notes: '', createdAt: '2025-01-05T00:00:00Z', country: 'GB', industryType: 'Seafood Processing', standard: 'UK_EA' },
  ];
  // Only show the DEMO fallback in offline/standalone mode (no PHP backend token).
  // When a real backend is connected, empty customers = genuinely empty — don't
  // inject fake records that make the clear look like it didn't work.
  const displayCustomers = customers.length > 0 ? customers : (hasAuthToken() ? [] : DEMO);

  // Unique filter options — must be after displayCustomers
  const uniqueStandards  = useMemo(() => [...new Set(displayCustomers.map(c => c.standard).filter(Boolean))].sort() as string[], [displayCustomers]);
  const uniqueCountries  = useMemo(() => [...new Set(displayCustomers.map(c => c.country).filter(Boolean))].sort() as string[], [displayCustomers]);
  const uniqueIndustries = useMemo(() => [...new Set(displayCustomers.map(c => c.industryType).filter(Boolean))].sort() as string[], [displayCustomers]);

  // ─── Search / filter / sort ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = displayCustomers.filter(c => {
      if (q && !(
        c.company.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) ||
        (c.contactEmail || '').toLowerCase().includes(q) ||
        (c.industryType || '').toLowerCase().includes(q) ||
        (c.country || '').toLowerCase().includes(q) ||
        (c.consentNumber || '').toLowerCase().includes(q)
      )) return false;
      if (filterStandard && c.standard !== filterStandard) return false;
      if (filterCountry  && c.country  !== filterCountry)  return false;
      if (filterIndustry && c.industryType !== filterIndustry) return false;
      return true;
    });
    // Sort
    list = [...list].sort((a, b) => {
      if (sortMode === 'name')   return a.company.localeCompare(b.company);
      if (sortMode === 'recent') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortMode === 'data') {
        const sa = getStats(a.id, a), sb = getStats(b.id, b);
        const ta = sa.influent.length + sa.effluent.length + sa.trains.length;
        const tb = sb.influent.length + sb.effluent.length + sb.trains.length;
        return tb - ta;
      }
      if (sortMode === 'standard') return (a.standard || 'ZZZ').localeCompare(b.standard || 'ZZZ');
      return 0;
    });
    return list;
  }, [displayCustomers, search, filterStandard, filterCountry, filterIndustry, sortMode, allSamples, allTrains]);

  const activeFilterCount = [filterStandard, filterCountry, filterIndustry].filter(Boolean).length;

  // ─── Per-customer stats ────────────────────────────────────────────────────
  const getStats = (cid: string, customer?: Customer): CustomerStats => {
    const numId   = parseInt(cid, 10);
    // Also match by serverId if available (handles timestamp local IDs)
    const srvId   = customer?.serverId ?? null;
    const matchSample = (s: UnifiedSample) =>
      (numId > 0 && (s.customerId === numId || String(s.customerId) === cid)) ||
      (srvId != null && (s.customerId === srvId || String(s.customerId) === String(srvId)));
    const matchTrain = (t: UnifiedTrain) =>
      (numId > 0 && (t.customerId === numId || String(t.customerId) === cid)) ||
      (srvId != null && (t.customerId === srvId || String(t.customerId) === String(srvId)));
    const matchProposal = (p: Proposal) =>
      p.customerId === cid ||
      (numId > 0 && (p.customerId === String(numId) || String(p.customerId) === cid)) ||
      (srvId != null && (p.customerId === String(srvId) || String(p.customerId) === String(srvId)));

    const samples = allSamples.filter(matchSample);
    return {
      influent:  samples.filter(s => !s.sampleType || s.sampleType === 'influent'),
      effluent:  samples.filter(s => s.sampleType === 'effluent'),
      trains:    allTrains.filter(matchTrain),
      proposals: allProposals.filter(matchProposal),
    };
  };

  // ─── Global stats ─────────────────────────────────────────────────────────
  const globalStats = useMemo(() => ({
    totalSamples: allSamples.length,
    totalTrains:  allTrains.length,
    totalProposals: allProposals.length,
    validatedProposals: allProposals.filter(p => p.status === 'validated').length,
  }), [allSamples, allTrains, allProposals]);

  const selectedCustomer = displayCustomers.find(c => c.id === selectedId) ?? null;
  const selectedStats = selectedCustomer ? getStats(selectedCustomer.id, selectedCustomer) : null;

  // ─── Active customer stats (for ActiveSessionPanel) ──────────────────────
  const activeCustomerFull = currentCustomer
    ? (customers.find(c => c.id === currentCustomer.id) || null)
    : null;
  const activeStats = activeCustomerFull
    ? getStats(activeCustomerFull.id, activeCustomerFull)
    : null;

  // Jump to active customer card and optionally switch the detail panel tab
  const jumpToActive = (tab: DetailTab = 'overview') => {
    if (!currentCustomer) return;
    setSelectedId(currentCustomer.id);
    setForceTab({ tab, nonce: Date.now() });
  };

  // ─── Activate customer (sets session + standard) ──────────────────────────
  const activateCustomer = (c: Customer) => {
    // Always resolve the standard — never leave it undefined in the session
    const std = (c.standard || COUNTRY_TO_STANDARD[c.country || 'GB'] || 'UK_EA') as StandardCode;
    // Resolve the DB integer ID — serverId takes priority; fall back to small integer id
    const resolvedServerId = c.serverId
      ?? (c.id.length < 13 && Number(c.id) > 0 && Number(c.id) < 2_000_000_000 ? Number(c.id) : undefined);
    setCurrentCustomer({
      id: c.id, serverId: resolvedServerId,
      name: c.name, company: c.company, contactEmail: c.contactEmail,
      phone: c.phone, address: c.address, notes: c.notes, createdAt: c.createdAt,
      country: c.country as any, industryType: c.industryType,
      standard: std,   // ← use the resolved std, never undefined
    });
    setActiveStandard(std);
    toast.success(`${c.company} set as active · standard: ${std.replace('_', ' ')}`);
  };

  // ─── Activate then navigate ───────────────────────────────────────────────
  const activateAndGo = (c: Customer, path: string) => {
    activateCustomer(c);
    navigate(path);
  };

  const handleDelete = (c: Customer) => {
    const policy = getRetentionPolicy(c.standard);
    if (isWithinRetention(c.createdAt, policy)) {
      if (!isAdmin) {
        toast.error(retentionToastMessage(policy, c.createdAt));
        return;
      }
      if (!window.confirm(adminOverridePrompt(policy, c.createdAt, `customer "${c.company}"`))) return;
      const updated = customers.filter(x => x.id !== c.id);
      setCustomers(updated);
      saveCustomers(updated);
      if (selectedId === c.id) setSelectedId(null);
      if (currentCustomer?.id === c.id) setCurrentCustomer(null);
      toast.warning(`Customer deleted (admin override — within ${policy.retentionYears}-year retention period)`);
      return;
    }
    if (!confirm(`Delete customer "${c.company}"?\n\nThis removes the customer record only — samples and trains are preserved.`)) return;
    const updated = customers.filter(x => x.id !== c.id);
    setCustomers(updated);
    saveCustomers(updated);
    if (selectedId === c.id) setSelectedId(null);
    if (currentCustomer?.id === c.id) setCurrentCustomer(null);
    toast.success('Customer deleted');
  };

  return (
    <div className="space-y-5 lg:h-full lg:flex lg:flex-col">

      <PageHeader
        icon={Building2}
        title="Customer Directory"
        subtitle="Samples · process trains · proposals · regulatory compliance — per customer"
        actions={
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setAddOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold text-black bg-rb-brand hover:bg-yellow-400 transition-all">
              <Plus className="w-3.5 h-3.5" /> Add Customer
            </button>
          </div>
        }
      />

      {/* ── Global Stats Strip — clickable, jumps to active customer + tab ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 shrink-0">
        {([
          { label: 'Customers',      value: displayCustomers.length,          icon: Building2,   color: 'text-rb-brand',   bg: 'bg-rb-brand/10 border-rb-brand/20',   tab: 'overview'  as DetailTab },
          { label: 'All Samples',    value: globalStats.totalSamples,         icon: FlaskConical,color: 'text-sky-400',     bg: 'bg-sky-500/10 border-sky-500/20',     tab: 'influent'  as DetailTab },
          { label: 'Process Trains', value: globalStats.totalTrains,          icon: Workflow,    color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20', tab: 'trains'    as DetailTab },
          { label: 'Proposals',      value: globalStats.totalProposals,       icon: FileText,    color: 'text-violet-400',  bg: 'bg-violet-500/10 border-violet-500/20',tab: 'proposals' as DetailTab },
          { label: 'Validated',      value: globalStats.validatedProposals,   icon: Shield,      color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20',tab:'proposals' as DetailTab },
        ] as const).map(s => (
          <button key={s.label} onClick={() => jumpToActive(s.tab)}
            title={currentCustomer ? `Show ${s.label} for ${currentCustomer.company}` : s.label}
            className={`rounded-xl border p-3 ${s.bg} flex items-center gap-3 text-left w-full transition-all hover:brightness-110 active:scale-[0.98] ${currentCustomer ? 'cursor-pointer' : 'cursor-default'}`}>
            <s.icon className={`w-5 h-5 shrink-0 ${s.color}`} />
            <div>
              <p className={`text-2xl font-black leading-none ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mt-0.5">{s.label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* ── Active Session Panel (replaces simple banner) ── */}
      <AnimatePresence>
        {currentCustomer && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="shrink-0"
          >
            <ActiveSessionPanel
              customer={currentCustomer as any}
              trains={activeStats?.trains ?? []}
              onClickCustomer={() => jumpToActive('overview')}
              onActivateAndGo={(path) => {
                if (activeCustomerFull) activateAndGo(activeCustomerFull, path);
              }}
              onClear={() => { setCurrentCustomer(null); setSessionPanelExpanded(false); toast.info('Active customer cleared'); }}
              navigate={navigate}
              expanded={sessionPanelExpanded}
              onToggleExpand={() => setSessionPanelExpanded(p => !p)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main grid ── */}
      <div className="lg:flex-1 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 lg:min-h-0">

        {/* LEFT: Customer List */}
        <div className="flex flex-col lg:min-h-0 gap-2">

          {/* ── Search + filter toolbar ── */}
          <div className="space-y-2">
            {/* Search row */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search company, industry, consent…"
                  className="w-full pl-8 pr-3 py-2 text-xs text-slate-200 placeholder-slate-600
                             bg-rb-surface border border-slate-700/60 rounded-xl focus:border-slate-500 focus:outline-none" />
                {search && (
                  <button onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              {/* Filter toggle */}
              <button onClick={() => setShowFilters(v => !v)}
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-rb-brand/10 text-rb-brand border-rb-brand/30'
                    : 'bg-rb-surface text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                }`}>
                <Layers className="w-3.5 h-3.5" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rb-brand text-black text-[9px] font-black flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button onClick={reload}
                className="p-2 rounded-xl bg-rb-surface border border-slate-700/60 text-slate-500 hover:text-slate-200 hover:border-slate-600 transition-all">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Filter + sort panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="p-3 rounded-xl bg-rb-surface border border-slate-700/50 space-y-2">

                    {/* Sort */}
                    <div>
                      <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Sort by</p>
                      <div className="grid grid-cols-4 gap-1">
                        {([
                          { id: 'name',     label: 'Name' },
                          { id: 'recent',   label: 'Recent' },
                          { id: 'data',     label: 'Most Data' },
                          { id: 'standard', label: 'Standard' },
                        ] as { id: SortMode; label: string }[]).map(opt => (
                          <button key={opt.id} onClick={() => setSortMode(opt.id)}
                            className={`py-1 rounded-lg text-[10px] font-bold border transition-all ${
                              sortMode === opt.id
                                ? 'bg-rb-brand text-black border-rb-brand/50'
                                : 'bg-rb-elevated text-slate-400 border-slate-700/40 hover:text-slate-200'
                            }`}>{opt.label}</button>
                        ))}
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 gap-2">
                      {/* Standard */}
                      {uniqueStandards.length > 1 && (
                        <div>
                          <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1">Standard</p>
                          <div className="flex flex-wrap gap-1">
                            {uniqueStandards.map(std => {
                              const sc = STANDARD_COLORS[std] || STANDARD_COLORS.UK_EA;
                              const active = filterStandard === std;
                              return (
                                <button key={std} onClick={() => setFilterStandard(active ? '' : std)}
                                  className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border transition-all ${
                                    active ? `${sc.bg} ${sc.color} ${sc.border}` : 'bg-rb-elevated text-slate-500 border-slate-700/40 hover:text-slate-300'
                                  }`}>{std.replace('_', ' ')}</button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Country */}
                      {uniqueCountries.length > 1 && (
                        <div>
                          <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1">Country</p>
                          <div className="flex flex-wrap gap-1">
                            {uniqueCountries.map(code => {
                              const active = filterCountry === code;
                              return (
                                <button key={code} onClick={() => setFilterCountry(active ? '' : code)}
                                  className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border transition-all ${
                                    active ? 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' : 'bg-rb-elevated text-slate-500 border-slate-700/40 hover:text-slate-300'
                                  }`}>
                                  {COUNTRY_FLAGS[code] ?? '🌐'} {code}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Industry */}
                      {uniqueIndustries.length > 1 && (
                        <div>
                          <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1">Industry</p>
                          <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}
                            className="w-full bg-rb-elevated border border-slate-700/40 rounded-lg px-2 py-1.5 text-xs text-slate-300 focus:outline-none">
                            <option value="">— All industries —</option>
                            {uniqueIndustries.map(i => <option key={i} value={i}>{i}</option>)}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Clear all */}
                    {activeFilterCount > 0 && (
                      <button onClick={() => { setFilterStandard(''); setFilterCountry(''); setFilterIndustry(''); }}
                        className="w-full py-1 text-[10px] font-bold text-slate-500 hover:text-slate-300 border border-slate-700/40 rounded-lg transition-colors">
                        Clear all filters
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active filter chips */}
            {!showFilters && activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-1">
                {filterStandard && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                                   bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    {filterStandard.replace('_', ' ')}
                    <button onClick={() => setFilterStandard('')}><X className="w-2.5 h-2.5" /></button>
                  </span>
                )}
                {filterCountry && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                                   bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    {COUNTRY_FLAGS[filterCountry] ?? '🌐'} {filterCountry}
                    <button onClick={() => setFilterCountry('')}><X className="w-2.5 h-2.5" /></button>
                  </span>
                )}
                {filterIndustry && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                                   bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    {filterIndustry}
                    <button onClick={() => setFilterIndustry('')}><X className="w-2.5 h-2.5" /></button>
                  </span>
                )}
              </div>
            )}

            {/* Result count */}
            <div className="flex items-center justify-between px-0.5">
              <p className="text-[10px] font-bold text-slate-600">
                {filtered.length} of {displayCustomers.length} customer{displayCustomers.length !== 1 ? 's' : ''}
                {sortMode !== 'name' && <span className="ml-1 text-slate-700">· sorted by {sortMode}</span>}
              </p>
              <button onClick={() => setAddOpen(true)}
                className="flex items-center gap-1 text-[10px] font-bold text-rb-brand/70 hover:text-rb-brand transition-colors">
                <Plus className="w-3 h-3" /> New
              </button>
            </div>
          </div>

          {/* ── Customer list ── */}
          <div className="lg:flex-1 lg:overflow-y-auto space-y-2 pr-1">
            {filtered.length === 0 ? (
              <div className="text-center py-12 bg-rb-surface rounded-xl border border-slate-800/60">
                <Building2 className="w-9 h-9 text-slate-700 mx-auto mb-3" />
                <p className="text-sm font-bold text-slate-400">No customers found</p>
                <p className="text-xs text-slate-600 mt-1">
                  {activeFilterCount > 0 ? 'Try clearing filters' : 'Add your first customer to get started'}
                </p>
                <div className="flex gap-2 justify-center mt-4">
                  {activeFilterCount > 0 && (
                    <button onClick={() => { setFilterStandard(''); setFilterCountry(''); setFilterIndustry(''); setSearch(''); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-rb-elevated text-slate-400 border border-slate-700/50 hover:text-slate-200">
                      <X className="w-3 h-3" /> Clear filters
                    </button>
                  )}
                  <button onClick={() => setAddOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20">
                    <Plus className="w-3 h-3" /> Add Customer
                  </button>
                </div>
              </div>
            ) : (
              filtered.map((c, i) => (
                <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}>
                  <CustomerListCard
                    customer={c}
                    stats={getStats(c.id, c)}
                    isActive={currentCustomer?.id === c.id}
                    isSelected={selectedId === c.id}
                    onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
                  />
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Detail Panel */}
        <div className="lg:min-h-0 lg:overflow-hidden">
          <AnimatePresence mode="wait">
            {selectedCustomer && selectedStats ? (
              <motion.div key={selectedCustomer.id} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }}
                className="lg:h-full">
                <CustomerDetailPanel
                  customer={selectedCustomer}
                  stats={selectedStats}
                  isActive={currentCustomer?.id === selectedCustomer.id}
                  onSetActive={() => activateCustomer(selectedCustomer)}
                  onDelete={() => handleDelete(selectedCustomer)}
                  navigate={navigate}
                  onActivateAndGo={(path) => activateAndGo(selectedCustomer, path)}
                  forceTab={forceTab}
                  onEditCustomer={(updated) => {
                    const newList = customers.map(c =>
                      c.id === selectedCustomer.id ? { ...c, ...updated } : c
                    );
                    setCustomers(newList);
                    saveCustomers(newList);
                    // If this is the active session customer, update the session standard too
                    if (currentCustomer?.id === selectedCustomer.id && updated.standard) {
                      setActiveStandard(updated.standard as StandardCode);
                    }
                  }}
                />
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="py-12 lg:h-full flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-[#0d1b30]/60">
                <div className="w-16 h-16 rounded-2xl bg-slate-800/60 border border-slate-800/50 flex items-center justify-center mb-4">
                  <Building2 className="w-7 h-7 text-slate-600" />
                </div>
                <p className="text-sm font-bold text-slate-400">Select a customer</p>
                <p className="text-[11px] text-slate-600 mt-1">Click any customer on the left to view their full profile</p>
                <div className="mt-6 flex flex-col gap-2 items-center">
                  {[
                    { icon: ShieldCheck, label: 'Regulatory profile & discharge limits', color: 'text-rb-brand' },
                    { icon: FlaskConical, label: 'Influent & effluent samples', color: 'text-sky-400' },
                    { icon: Workflow,     label: 'Process trains with equipment', color: 'text-amber-400' },
                    { icon: FileText,     label: 'Proposals and revisions', color: 'text-violet-400' },
                    { icon: Activity,     label: 'Full activity timeline', color: 'text-emerald-400' },
                  ].map(({ icon: Icon, label, color }) => (
                    <div key={label} className="flex items-center gap-2 text-[11px] text-slate-500">
                      <Icon className={`w-3.5 h-3.5 ${color}`} />{label}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Add Customer Modal ── */}
      <AnimatePresence>
        {addOpen && (
          <AddCustomerModal
            onClose={() => setAddOpen(false)}
            onSaved={(c) => { setCustomers(loadCustomers()); setSelectedId(c.id); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
